import React, { Component, useCallback, useEffect, useRef } from 'react';
import { Table } from './Table';
import { ListGroup, ListGroupItem, Alert, Col, Modal, ModalBody, Row, Button, Navbar, Container, NavbarBrand, NavbarToggler, Nav, NavItem, NavLink, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Form, Label, FormGroup, Collapse, ModalHeader, ModalFooter, InputGroup, InputGroupText, Input, FormFeedback, Fade, Card, Spinner, Toast, ToastBody} from 'reactstrap';
import DataGrid from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import PopUp from "./PopUp"; 
import { toLatLon } from 'utm';
import userIcon from '../assets/person.svg';
import settingsIcon from '../assets/settings.svg';
import exclamationIcon from '../assets/exclamation-fill.svg';
import { GPSMapContainer } from './GPSMapContainer';
import Plot from 'react-plotly.js';
import { TreeView } from './TreeView';
import classnames from 'classnames';
import { CSVLink } from "react-csv";
import Papa from 'papaparse';
import { sortTreeData, collectDispatchNodes, isShotNameValid, isAddShotSubmitBlocked, parseFlatInsertResponse } from './treeUtils';

function pad2(n) {  // always returns a string
    return (n < 10 ? '0' : '') + n;
}

const styles = {
    page: { backgroundColor: 'white' },
    headerTitle: {
        position: 'absolute',
        left: '7%',
        top: '12%',
        fontSize: 47,
        fontStyle: 'normal',
        fontWeight: 'bold',
        color: '#0E2836',
    },
    headerText: {
        position: 'absolute',
        top: '15%',
        right: '20%',
        fontSize: 25,
        fontStyle: 'normal',
        fontWeight: 'bold',
        color: '#0E2836',
    },
    headerGenerated: {
        position: 'absolute',
        bottom: '2%',
        fontSize: 10,
        fontStyle: 'normal',
        fontWeight: 'normal',
        textAlign: 'center',
        color: '#939a9e',
    },
    headerCustomField: {
        position: 'absolute',
        left: '7%',
        bottom: '15%',
        fontSize: 20,
        fontStyle: 'normal',
        fontWeight: 'normal',
        color: '#0E2836',
    },
    headerLogo: {
        position: 'absolute',
        height: 'auto',
        width: '30%',
        right: '10%',
        bottom: '10%',
    },
    headerImage: {
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: '#0E2836',
        position: 'absolute',
        height: '40%',
        left: '-1%',
        right: '-1%',
        bottom: '30%',
    },
    footerLogo: {
        position: 'absolute',
        height: '10%',
        width: '20%',
        left: '2%',
        bottom: '2%',
    },
    footerText: {
        position: 'absolute',
        left: '28%',
        bottom: '6.5%',
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 'bold',
        color: '#0E2836',
    },
    footerBreak: {
        position: 'absolute',
        height: '0.2%',
        width: '96%',
        left: '2%',
        bottom: '14%',
        color: '#0E2836',
    },
    pageTitle: {
        position: 'absolute',
        left: '2%',
        top: '2%',
        fontSize: 20,
        fontStyle: 'normal',
        fontWeight: 'bolder',
        color: '#0E2836',
        textDecoration: 'underline',
    },
    pageSummary: {
        position: 'absolute',
        left: '2%',
        top: '7%',
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 'normal',
        color: '#0E2836',
    },
    pageKey: {
        position: 'absolute',
        right: '10%',
        bottom: '0%',
        fontSize: 10,
        fontStyle: 'normal',
        fontWeight: 'bolder',
        color: '#0E2836',
    },
    imageTable: { backgroundColor: 'white', margin: 0 },
    imageLeaflet: {
        position: 'absolute',
        height: 'auto',
        width: '100%',
        left: '0%',
        top: '0%',
        backgroundColor: 'white',
        margin: 0,
    },
    imagePlotly: {
        position: 'absolute',
        height: 'auto',
        width: '60%',
        left: '20%',
        top: '0%',
        backgroundColor: 'white',
        margin: 0,
    },
};

const CSVDownload = (props) => {
    const btnRef = useRef(null)
    useEffect(() => btnRef.current?.click(), [btnRef])
    return (
        <CSVLink {...props}>
            <span ref={btnRef} />
        </CSVLink>
    )
}

const PLAN_FORMAT_HEADERS = {
    pulsesimple: {
        label: "Pulse (Simple)",
        required: ["Hole ID", "Collar X (m)", "Collar Y (m)", "Collar RL (m)", "Drill X (m)", "Drill Y (m)", "Drill RL (m)"],
        optional: ["Diameter (mm)", "XY Tolerance (m)", "Backfill Tolerance (m)", "Redrill Tolerance (m)", "Stem X (m)", "Stem Y (m)", "Stem RL (m)", "Overstem Tolerance (m)", "Understem Tolerance (m)"],
    },
    vulcan: {
        label: "Vulcan",
        required: ["Hole Name", "Length", "Collar X", "Collar Y", "Collar Z"],
        optional: [],
    },
    blastlogic: {
        label: "Blastlogic",
        required: ["Hole ID", "Design collar X (m)", "Design collar Y (m)", "Design collar Z (m)", "Design drill depth (m)"],
        optional: ["Hole status", "Diameter (mm)"],
    },
    pulse: {
        label: "Pulse",
        required: ["Hole ID", "Collar X (m)", "Collar Y (m)", "Collar RL (m)", "Drill X (m)", "Drill Y (m)", "Drill RL (m)"],
        optional: ["Diameter (mm)", "Collar XY Tolerance (m)", "Collar RL Tolerance+ (m)", "Collar RL Tolerance- (m)", "Drill XY Tolerance (m)", "Drill RL Tolerance+ (m)", "Drill RL Tolerance- (m)", "Stem X (m)", "Stem Y (m)", "Stem RL (m)", "Stem XY Tolerance (m)", "Stem RL Tolerance+ (m)", "Stem RL Tolerance- (m)"],
    },
};

const COLUMN_ALIASES = {
    "Hole ID":        ["hole name", "holeid", "hole_id", "name", "id", "hole id"],
    "Collar X (m)":   ["collar x", "easting", "east", "x", "collar_x", "col x"],
    "Collar Y (m)":   ["collar y", "northing", "north", "y", "collar_y", "col y"],
    "Collar RL (m)":  ["collar rl", "collar z", "elevation", "rl", "z", "height", "collar_z"],
    "Drill X (m)":    ["drill x", "toe x", "drill_x", "toe easting"],
    "Drill Y (m)":    ["drill y", "toe y", "drill_y", "toe northing"],
    "Drill RL (m)":   ["drill rl", "drill z", "toe z", "toe rl", "toe elevation"],
    "Diameter (mm)":  ["diameter", "diam", "bit size", "hole diameter", "dia"],
    "Hole Name":      ["hole name", "holename", "name", "hole id", "id"],
    "Length":         ["length", "depth", "hole depth", "design depth", "planned depth"],
    "Collar X":       ["collar x", "easting", "east", "x"],
    "Collar Y":       ["collar y", "northing", "north", "y"],
    "Collar Z":       ["collar z", "elevation", "rl", "z", "height"],
    "Design collar X (m)": ["design collar x", "collar x", "easting", "x"],
    "Design collar Y (m)": ["design collar y", "collar y", "northing", "y"],
    "Design collar Z (m)": ["design collar z", "collar z", "rl", "z", "elevation"],
    "Design drill depth (m)": ["design drill depth", "drill depth", "depth", "length"],
    "Hole status":    ["hole status", "status", "hole_status"],
};

export class Home extends Component {
    static displayName = Home.name;

    constructor(props) {
        super(props);

        //Init
        var dateOffset = (24 * 60 * 60 * 1000) * 30; //30days
        var startDate = new Date();
        startDate.setTime(startDate.getTime() - dateOffset);

        //File upload ref
        this.fileInput = React.createRef();

        //PDF download ref

        this.PDFleafletref = [React.createRef(), React.createRef(), React.createRef(), React.createRef()];
        this.nonPDFleafletref = React.createRef();
        this.PDFtableref = React.createRef();
        this.Extratableref_DiffvsPlan = React.createRef();
        this.Extratableref_Additional = React.createRef();
        this.Extratableref_Missing = React.createRef();
        this.styles = styles;

        this.state = {
            //Init - config
            pagePreload: false,
            pageDataSummary: true,

            //General
            captureData: [],
            loading: true,
            startDate: startDate,
            endDate: new Date(),

            //AnyPageDataLoading
            anyPageDataLoading: 0,
            anyPageDataLoadingDetails: "",

            //AnyPageDataLoading
            anyPageError: false,
            anyPageErrorDetails: "",

            //Popup
            seen: false,
            showDetailedWindow: false,
            popupDetailedData: {},

            //User Data
            name: "User",
            organisationName: "Company",
            userID: "0",

            //locations
            location: [],
            loadinglocation: true,
            selectedlocationID: "0",

            //sublocations
            sublocation: [],
            loadingsublocation: false,
            selectedsublocationID: "0",
            createsublocationName: "",

            //sequence
            sequence: [],
            loadingsequence: false,
            selectedsequenceID: "0",
            createsequenceName: "",

            //subsequence
            subsequence: [],
            loadingsubsequence: false,
            selectedsubsequenceID: "0",
            createsubsequenceName: "",

            //plan
            plan: [],
            loadingplan: false,
            selectedplanID: "0",
            createplanVersion: 0,
            createplanStartTime: new Date(),
            createplanEndTime: new Date(),
            createplanData: { 'Name': "" },
            loadingplanimages: false,

            //plandetails
            plandetails: [],
            loadingplandetails: true,

            //dispatch
            dispatch: [],
            loadingdispatch: false,
            selecteddispatchID: "0",
            createdispatchName: "",
            createdispatchTime: new Date(0), //deafult to epoch 1970, used to show actual dispatch datetime

            dispatchPlanDetailsSelected: false,
            dispatchPlanDetailsShow: false,
            selectedDispatchPlanDetailsID: "0",
            selectedDispatchPlanID: [],
            selectedDispatchPlanIDRequested: [],
            dispatchPlanDetailsAll: [],
            dispatchPlanDetails: [],
            dispatchPlanDetailsNot: [],

            dispatchCheckboxIncludeRefSelected: true,
            dispatchToDeviceLoading: false,

            //review
            review: [],
            loadedreview: false,
            reviewSelectionMode: false,
            reviewSelections: [],
            reviewUnassignMode: false,
            unassignSelections: [],
            selectedReviewDetails: [],
            selectedCaptureData: [],
            loadingreview: false,
            confirmResetAssignmentModalOpen: false,

            //report
            report: [],
            loadedreport: false,
            optionsIncludePageSelected: [true,true,true,true,true,true,true,true,true,true],
            reportHeaderCustomField: ["", "", "", "", "", "HoleDepth", "HoleDepth", "", "", ""],
            selectedReportDetails: [],
            loadingreport: false,
            loadedshotreport: false,

            //options
            optionsTreeShowDispatchWCaptures: true,
            optionsTreeShowArchived: false,
            optionsShowAdvanced: false,
            optionsWaterHeightZero: 0.1,
            optionsWaterHeightWet: 0.1,
            optionsReflectivityWet: 60,

            //hardware
            hardware: [],
            loadinghardware: true,
            selectedhardwareID: "0",

            //reflifecycletype
            reflifecycletype: [],
            loadingreflifecycletype: true,
            selectedreflifecycletypeID: "0",

            //planlifecycles
            planlifecycles: [],
            loadingplanlifecycles: true,
            selectedplanlifecyclesID: "0",
            createplanlifecyclesActive: 1,
            createplanlifecyclesCompleted: 0,
            createplanlifecyclesData: { 'Default': "" },
            createplanlifecyclesCaptureDistanceMin: 500, //mm
            createplanlifecyclesCaptureDistanceMax: 10000, //mm

            //plancampaigns
            plancampaigns: [],
            loadingplancampaigns: true,
            selectedplancampaignsID: "0",
            createplancampaignsActive: 1,
            createplancampaignsCompleted: 0,

            //refcampaigntype
            refcampaigntype: [],
            loadingrefcampaigntype: true,
            selectedrefcampaigntypeID: "0",

            //Dropdown Open
            dropdownOpen: false,

            //NavBarCollapse
            navOpen: false,

            //Filter Collapse Toggle
            filterOpen: false,
            planOpen: false,
            dispatchOpen: false,
            configOpen: false,
            configId: -1,
            configName: "",
            settingsOpen: false,
            reviewOpen: false,
            reportOpen: false,

            //Fileupload open
            isOpen: false,
            dataLoaded: false,
            isFormInvalid: false,
            checkValidation: true,
            validationErrors: [],
            rows: [],
            cols: [],
            extra: [],
            extra_line: [],
            filePlanData: [],
            uploadedFileName: "",
            updatePlanInfo: [],

            //Redrill
            redrillMatchResult: null,
            redrillIncludeUnmatched: {},
            redrillMatchLoading: false,

            //PDF
            leafletPDFcanvas0: null,
            leafletPDFcanvas1: null,
            leafletPDFcanvas2: null,
            leafletPDFcanvas3: null,
            plotlyPDFcanvas0: null,
            plotlyPDFcanvas1: null,
            plotlyPDFcanvas2: null,
            PDFgenerated: false,
            PDFloading: false,
            PDFplotDivId: ["PDFplotDivId0", "PDFplotDivId1", "PDFplotDivId2"], //Unique name/numbers
            ExtraDataLoading: false,

            //TreeView
            treeData: [
                { id: "1", name: "Please Select a Location" }
            ],
            activeTab: 1,
            sortString: "alpha-asc",

            //GPSMapContainer
            GPSmapStyle: {
                "HoleData_Missing": { "color": "red", "radius": -1, "weight": 3, "dashArray": "4", "fillColor": "#f03", "fillOpacity": 0.5 },
                "DeviceCaptureData": { "color": "magenta", "radius": 0.6, "weight": 3, "dashArray": "", "fillColor": "#ff00ff", "fillOpacity": 0.5 },
                "HoleData_Captured": { "color": "green", "radius": -1, "weight": 3, "dashArray": "", "fillColor": "#0f3", "fillOpacity": 0.5 },
                "HoleData_Plan": { "color": "white", "radius": -1, "weight": 3, "dashArray": "4", "fillColor": "#f03", "fillOpacity": 0.5 },
                "PreviousCaptureData": { "color": "DarkGrey", "radius": 0.6, "weight": 3, "dashArray": "", "fillColor": "#ff00ff", "fillOpacity": 0.5 },
                "Review_Diff": { "color": "orange", "radius": 0.6, "weight": 3, "dashArray": "", "fillColor": "orange", "fillOpacity": 1 },
                "Review_Polyline": { "color": "black", "weight": 3, "dashArray": "", "fillColor": "black", "fillOpacity": 1 },
                "Review_Clicked": { "color": "black", "radius": 0.6, "weight": 3, "dashArray": "", "fillColor": "black", "fillOpacity": 1 },
                "Review_ClickedPrevious": { "color": "brown", "radius": 0.6, "weight": 3, "dashArray": "", "fillColor": "brown", "fillOpacity": 1 }
            },

            //LifecycleTabs
            activeReviewLifecycle: 0,
            activeReviewLifecycleId: 0,
            activeReportLifecycle: 0,

            //dispatchReviewRequired menu
            showDispatchRRMenu: false,
            dispatchRRMenuX: 0,
            dispatchRRMenuY: 0,
            dispatchesReviewRequired: [],

            //dispatchLatestCaptures menu
            showDispatchLatestMenu: false,
            dispatchLatestMenuX: 0,
            dispatchLatestMenuY: 0,
            dispatchesLatestCaptures: [],

            //config modal
            configOption: "",

            //export modal
            showExportDataModal: false,
            exportDataType: "csv",
            exportDataFilename: "data.csv",
            exportDataCS: "wgs84",
            //export filters
            exportFilterIncludeDesign: false,
            exportFilterTimeWindow: "all",
            exportFilterCustomDate: "",
            exportFilterCompliance: [],
            exportFilterWetOnly: false,

            //upload options
            uploadPlanType: "pulsesimple",
            uploadPlanCS: "wgs84",
            uploadPlanXYTolerance: "1.0",
            uploadPlanRLTolerancePlus: "0.5",
            uploadPlanRLToleranceMinus: "0.5",
            uploadPlanToleranceOptions: Array(31).fill(0).map((x, i) => (i * 0.1).toFixed(1)),
            uploadShowAdvancedOptions: false,
            uploadShowExpectedColumns: false,
            headersCopied: false,

            //column mapping
            columnMappingRequired: false,
            columnMapping: {},
            uploadedRawHeaders: [],
            pendingPlanJSON: null,
            pendingPlanFileType: null,
            pendingHeaderRowIndex: 0,

            //refresh tolerances confirm + summary modals
            refreshTolerancesConfirmOpen: false,
            refreshTolerancesLocationID: null,
            refreshTolerancesModalOpen: false,
            refreshTolerancesSummary: null,
        };
    }

    componentDidMount() {
        const accessToken = this.props.auth.getAccessToken();

        //Get user info meta data
        this.props.auth.getUserInfo(accessToken, this.setUserData)

        //Init - get reference data
        this.getRefLifecycleTypeAll();
        this.getRefCampaignTypeAll();
    }

    setUserData = user => {
        var appdata = user["https://athena.com/app_metadata"];
        this.setState({
            name: appdata.name,
            organisationName: appdata.organisationName,
            userID: user.sub
        });

        this.postUserLog("login",null,null,-1);

        //First we run
        this.getLocationAll();
    }

    togglePop = () => {
        this.setState({
            seen: !this.state.seen
        });

        //reset three window when closed
        if (this.state.seen) {
            this.setState({
                showDetailedWindow: false
            });
        }
    };

    toggleDropdown = () => {
        this.setState({
            dropdownOpen: !this.state.dropdownOpen
        });

    };

    toggleNavCollapse = () => {
        this.setState({
            navOpen: !this.state.navOpen
        });
    };

    toggleFilter() {
        this.setState({
            filterOpen: !this.state.filterOpen
        });
    };

    toggleValidation = () => {
        this.setState({
            checkValidation: !this.state.checkValidation
        });
    };

    toggleFilterHome = () => {
        this.setState({
            filterOpen: !this.state.filterOpen
        });
    };

    togglePlan(option) {
        if (!this.state.pagePreload) {
            if (!this.state.planOpen) {
                if (this.state.loadingplan) {
                    var value = this.state.selectedsubsequenceID;
                    if (value !== "0") {
                        this.getPlanAll(value);
                    }
                }
            }
        }
        if (this.state.planOpen) {
            this.setState({
                //Only on close
                dataLoaded: false,
                uploadedFileName: "",
                validationErrors: 0,
                updatePlanInfo: 0,
                isFormInvalid: false,
                redrillMatchResult: null,
                redrillIncludeUnmatched: {},
                redrillMatchLoading: false,
                columnMappingRequired: false,
                columnMapping: {},
                uploadedRawHeaders: [],
                pendingPlanJSON: null,
                pendingPlanFileType: null,
                pendingHeaderRowIndex: 0,
                uploadShowExpectedColumns: false,
            });
        }
        this.setState({
            planOpen: !this.state.planOpen,
            planModalOption: option
        });
    };

    togglePlanHome = () => {
        if (this.state.planOpen) {
            this.setState({
                //Only on close
                dataLoaded: false,
                uploadedFileName: "",
                validationErrors: 0,
                updatePlanInfo: 0,
                isFormInvalid: false,
                uploadShowAdvancedOptions: false,
                redrillMatchResult: null,
                redrillIncludeUnmatched: {},
                redrillMatchLoading: false,
                columnMappingRequired: false,
                columnMapping: {},
                uploadedRawHeaders: [],
                pendingPlanJSON: null,
                pendingPlanFileType: null,
                pendingHeaderRowIndex: 0,
                uploadShowExpectedColumns: false,
            });
        }
        this.setState({
            planOpen: !this.state.planOpen
        });
    };

    toggleDispatch() {
        if (!this.state.pagePreload) {
            if (!this.state.dispatchOpen) {
                if (this.state.loadingdispatch) {
                    var value = this.state.selectedlocationID;
                    var value2 = this.state.selectedhardwareID;
                    if ((value !== "0") && (value2 !== "0")) {
                        this.getDispatchByHardware(value, value2);
                    }
                }
            }
        }
        if (this.state.dispatchOpen) {
            this.setState({
                //Only on close
                createdispatchName: "",
                dataLoaded: false,
            });
        }
        this.setState({
            dispatchOpen: !this.state.dispatchOpen
        });
    };

    toggleDispatchHome = () => {
        if (this.state.dispatchOpen) {
            this.setState({
                //Only on close
                createdispatchName: "",
                dataLoaded: false,
            });
        }
        this.setState({
            dispatchOpen: !this.state.dispatchOpen
        });
    };

    toggleDispatchCheckboxIncludeRef = () =>  {
        this.setState({
            dispatchCheckboxIncludeRefSelected: !this.state.dispatchCheckboxIncludeRefSelected
        });
    };

    toggleConfig(option,id,name) {
        if (!this.state.pagePreload) {
            if (!this.state.configOpen) {
                if (this.state.loadingsublocation) {
                    var value = this.state.selectedlocationID;
                    if (value !== "0") {
                        this.getSubLocationAll(value);
                    }
                }
                this.setState({
                    createsublocationName: "",
                    createsequenceName: "",
                    createsubsequenceName: ""
                });
            }
        }
        this.setState({
            configOpen: !this.state.configOpen,
            configOption: option,
            configId: id,
            configName: name
        });
    };

    toggleConfigHome = () => {
        this.setState({
            configOpen: !this.state.configOpen
        });
    };

    toggleResetAssignmentModal = () => {
        this.setState({
            resetAssignmentModalOpen: !this.state.resetAssignmentModalOpen
        });
    };

    toggleSettings() {
        this.setState({
            settingsOpen: !this.state.settingsOpen
        });
    };

    toggleSettingsHome = () => {
        if(!this.state.anyPageDataLoading){
            if (this.state.settingsOpen) { //refresh data on close
                var old = this.state.selectedDispatchTreeId;
                this.setState({ selectedDispatchTreeId: null, forceReload: true });
                this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: old }));
                this.getDispatchReviewRequired(this.state.selectedlocationID);
                this.getDispatchLatestCaptures(this.state.selectedlocationID);
            }

            this.setState({
                settingsOpen: !this.state.settingsOpen
            });
        }
    };

    toggleReview() {
        if (!this.state.pagePreload) {
            if (!this.state.reviewOpen) {
                if (this.state.loadingdispatch) {
                    var value = this.state.selectedlocationID;
                    var value2 = this.state.selectedhardwareID;
                    if ((value !== "0") && (value2 !== "0")) {
                        this.getDispatchByHardware(value, value2);
                    }
                }
            }
        }
        this.setState({
            reviewOpen: !this.state.reviewOpen,
        });
    };

    toggleReviewHome = () => {
        this.setState({
            reviewOpen: !this.state.reviewOpen,
        });
    };

    toggleReport() {
        if (!this.state.pagePreload) {
            if (!this.state.reportOpen) {
                if (this.state.loadingplan) {
                    var value = this.state.selectedsubsequenceID;
                    if (value !== "0") {
                        this.getPlanAll(value);
                    }
                }
            }
        }
        this.setState({
            reportOpen: !this.state.reportOpen,

            loadedreport: false,
            PDFgenerated: false,
            PDFloading: false
        });
    };

    toggleReportHome = () => {
        this.setState({
            reportOpen: !this.state.reportOpen,

            loadedreport: false,
            PDFgenerated: false,
            PDFloading: false
        });
    };

    closeErrorAlert = () => {
        this.setState({
            anyPageError: false,
            anyPageErrorDetails: ""
        });
    };

    toggleReportOptionsIncludePage(e, index) {
        //Turn off report loaded
        this.setState({
            loadedreport: false,
            PDFgenerated: false,
            PDFloading: false
        });

        var array = this.state.optionsIncludePageSelected
        array[index] = !array[index]
        this.setState({
            optionsIncludePageSelected: array
        });
    };

    toggleDispatchPlanDetails() {
        //Store results
        this.setState({
            dispatchPlanDetailsShow: !this.state.dispatchPlanDetailsShow,
            dispatchPlanDetailsSelected: false,
            selectedDispatchPlanDetailsID: "0",
            dispatchPlanDetailsAll: [], //Reset
            dispatchPlanDetails: [], //Reset
            dispatchPlanDetailsNot: [], //Reset
            selectedDispatchPlanID: [], //Reset
            selectedDispatchPlanIDRequested: [], //Reset
        });
    };

    deleteItem(ID) {
        var type = ID.split("-")[0];
        var id = ID.split("-")[1];

        if (type == 'subloc') {
            this.postsublocationUpdateDeleted(id, true);
        } else if (type == 'seq') {
            this.postsequenceUpdateDeleted(id, true);
        } else if (type == 'subseq') {
            this.postsubsequenceUpdateDeleted(id, true);
        }
    }

    archiveItem(ID) {
        var type = ID.split("-")[0];
        var id = ID.split("-")[1];

        if (type == 'subloc') {
            this.postsublocationUpdateArchived(id, true);
        } else if (type == 'seq') {
            this.postsequenceUpdateArchived(id, true);
        } else if (type == 'subseq') {
            this.postsubsequenceUpdateArchived(id, true);
        }
       
    }

    unarchiveItem(ID) {
        var type = ID.split("-")[0];
        var id = ID.split("-")[1];

        if (type == 'subloc') {
            this.postsublocationUpdateArchived(id, false);
        } else if (type == 'seq') {
            this.postsequenceUpdateArchived(id, false);
        } else if (type == 'subseq') {
            this.postsubsequenceUpdateArchived(id, false);
        }

    }

    refreshTolerances(ID) {
        const locationID = ID.split("-")[1];
        this.setState({ refreshTolerancesConfirmOpen: true, refreshTolerancesLocationID: locationID });
    }

    async confirmRefreshTolerances() {
        this.setState({ refreshTolerancesConfirmOpen: false });
        const locationID = this.state.refreshTolerancesLocationID;
        try {
            const accessToken = this.props.auth.getAccessToken();
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            };
            this.postUserLog("Refresh Dispatch Tolerances", "JSON", JSON.stringify({ locationID }), locationID);
            const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/refreshtolerances/' + locationID, requestOptions);
            if (response.ok) {
                const summary = await response.json();
                this.setState({ refreshTolerancesModalOpen: true, refreshTolerancesSummary: summary });
                this.getTreeData(this.state.selectedlocationID);
            } else {
                window.alert("Refresh Dispatch Tolerances failed: " + response.status);
            }
        } catch (e) {
            window.alert("Refresh Dispatch Tolerances error: " + e.message);
        }
    }

    setStartDate = date => {
        this.setState({
            startDate: date
        });
        //console.log(new Date(date + " UTC").toISOString());
    };

    setEndDate = date => {
        this.setState({
            endDate: date
        });
    };

    setSelectedHardware = (e) => {
        var value = e.target.value;
        this.setState({
            selectedhardwareID: value
        });
        if (this.state.pagePreload) {
            if (value !== "0") {

                //Request for the Dispatch for this location
                var value2 = this.state.selectedlocationID;
                if (value2 !== "0") {
                    this.getDispatchByHardware(value2, value);
                }
            };
        } else {
            this.setState({
                loadingdispatch: true,
                selecteddispatchID: "0",
            });
            var value2 = this.state.selectedlocationID;
            if ((value !== "0") && (value2 !== "0")) {
                this.getDispatchByHardware(value2, value);
            };
        };
    };

    setSelectedLocation = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.selectedlocationID != value) {
            this.setState({
                selectedlocationID: value
            });

            this.getLocationOptions(value).then(() => { //get options first
                this.getTreeData(value);
                this.getDispatchReviewRequired(value);
                this.getDispatchLatestCaptures(value);
            });
            

            if (this.state.pagePreload) {
                if (value !== "0") {
                    //Request for the hardware for this location
                    this.getHardwareAll(value);

                    //Request for the hardware for this location
                    this.getSubLocationAll(value);
                    if (this.state.sublocation) {
                        this.state.sublocation.forEach((subloc) => { this.getSequenceAll(subloc.id) });
                    }


                    //Request for the Dispatch for this location
                    var value2 = this.state.selectedhardwareID;
                    if (value2 !== "0") {
                        this.getDispatchByHardware(value, value2);
                    }
                };
            } else {
                this.setState({
                    loadinghardware: true,
                    selectedhardwareID: "0",
                    loadingsublocation: true,
                    selectedsublocationID: "0",
                    loadingsequence: true,
                    selectedsequenceID: "0",
                    loadingsubsequence: true,
                    selectedsubsequenceID: "0",
                    loadingplan: true,
                    selectedplanID: "0",
                    loadingdispatch: true,
                    selecteddispatchID: "0",
                    loadedreview: false,
                    loadedreport: false
                });
                if (value !== "0") {
                    this.getHardwareAll(value);
                    this.getSubLocationAll(value);
                };
            };
        }
    };

    setSelectedSubLocation = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.selectedsublocationID != value) {
            this.setState({
                selectedsublocationID: value
            });

            if (this.state.pagePreload) {
                //Request for the sequence for this sublocation
                if (value !== "0") {
                    this.getSequenceAll(value);
                };
            } else {
                this.setState({
                    loadingsequence: true,
                    selectedsequenceID: "0",
                    loadingsubsequence: true,
                    selectedsubsequenceID: "0",
                });
                if (value !== "0") {
                    this.getSequenceAll(value);
                    if (this.state.sequence) {
                        this.state.sequence.forEach((seq) => { this.getSubSequenceAll(seq.id) });
                    }
                };
            };
        }
    };

    setCreateSubLocationName = (e) => {
        var value = e.target.value;
        this.setState({
            createsublocationName: value
        });
    };

    setSelectedSequence = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.selectedsequenceID != value) {
            this.setState({
                selectedsequenceID: value
            });

            if (this.state.pagePreload) {
                //Request for the subsequence for this sequence
                if (value !== "0") {
                    this.getSubSequenceAll(value);
                };
            } else {
                this.setState({
                    loadingsubsequence: true,
                    selectedsubsequenceID: "0",
                });
                if (value !== "0") {
                    this.getSubSequenceAll(value);
                };
            };
        }
    };

    setCreateSequenceName = (e) => {
        var value = e.target.value;
        this.setState({
            createsequenceName: value
        });
    };

    setSelectedSubSequence = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.selectedsubsequenceID != value || this.state.loadedshotreport) {
            this.setState({
                selectedsubsequenceID: value
            });

            if (this.state.pagePreload) {
                //Request for the Plan for this sub-sequence
                if (value !== "0") {
                    this.getPlanAll(value);
                    //this.setState({
                    //    loadedreview: false,
                    //    loadedreport: false,
                    //    loadedshotreport: false,
                    //    loading: true,
                    //    activeTab: '4'
                    //});
                    //this.getShotReport(this.state.selectedlocationID, value);
                };
                
            } else {
                this.setState({
                    loadingplan: true,
                    selectedplanID: "0",
                });
                if (value !== "0") {
                    this.getPlanAll(value);
                    //this.setState({
                    //    loadedreview: false,
                    //    loadedreport: false,
                    //    loadedshotreport: false,
                    //    loading: true,
                    //    activeTab: '4'
                    //});
                    //this.getShotReport(this.state.selectedlocationID, value);
                };
                
            };
        }
    };

    setCreateSubSequenceName = (e) => {
        var value = e.target.value;
        this.setState({
            createsubsequenceName: value
        });
    };

    setSelectedTreeId = (id) => {
        if (this.state.selectedDispatchTreeId != id) {
            this.setState({ selectedDispatchTreeId: id });
        }
    }

    setSelectedPlan = (e,force = false) => {
        var value = (e.target != null) ? e.target.value : e;

        if (this.state.selectedplanID != value || this.state.loadedreview || force || this.state.forceReload) {
            this.setState({
                selectedplanID: value
            });

            if (this.state.pagePreload) {
                //Request for the PlanDetails for this Plan
                if (value !== "0") {
                    this.setState({
                        loadingdispatch: true,
                        loadedreview: false,
                        loadedreport: false,
                        loading: true,
                        activeTab: '4'
                    });

                    this.getPlanDetailsAll(value);
                    this.getDispatchByPlanID(this.state.selectedsubsequenceID, value);

                    this.getPlanReport(this.state.selectedlocationID, this.state.selectedsubsequenceID, value, this.state.exportDataCS);
                };
            } else {
                this.setState({
                    loadingplandetails: true,
                });
                if (value !== "0") {
                    this.setState({
                        loadingdispatch: true,
                        loadedreview: false,
                        loadedreport: false,
                        loading: true,
                        activeTab: '4'
                    });

                    this.getPlanDetailsAll(value);
                    this.getDispatchByPlanID(this.state.selectedsubsequenceID, value);

                    this.getPlanReport(this.state.selectedlocationID, this.state.selectedsubsequenceID, value, this.state.exportDataCS);
                };
            };
        }
    };

    setCreatePlanVersion = (e) => {
        var value = e.target.value;
        this.setState({
            createplanVersion: parseInt(value)
        });
    };

    setCreatePlanStartTime = (e) => {
        var value = e.target.value;
        this.setState({
            createplanStartTime: value
        });
    };

    setCreatePlanEndTime = (e) => {
        var value = e.target.value;
        this.setState({
            createplanEndTime: value
        });
    };

    setCreatePlanData = (e) => {
        var value = e.target.value;
        this.setState({
            createplanData: { 'Name': value },
        });
    };

    setSelectedDispatch = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.selecteddispatchID != value || this.state.loadedreport || this.state.forceReload) {
            this.setState({
                selecteddispatchID: value
            });

            this.setState({
                loadingplan: false,
                loadingdispatch: true,
                loadedreview: false,
                loadedreport: false,
                loading: true,
                activeTab: '3'
            });

            this.getDispatchReview(value, this.state.selectedlocationID);
        }
    };

    setCreateDispatchName = (e) => {
        var value = e.target.value;
        this.setState({
            createdispatchName: value
        });
    };

    setSelectedDispatchPlan = (e) => {
        var opts = [], opt;
        for (var i = 0; i < e.target.options.length; i++) {
            opt = e.target.options[i];
            if (opt.selected) {
                opts.push(opt.value);

                //Get detailed plan request
                if (this.state.selectedDispatchPlanIDRequested[i] !== opt.value) {
                    this.getPlanDetailsAllForDispatch(opt.value);
                }
            }
        }
        this.setState({
            selectedDispatchPlanID: opts,
            selectedDispatchPlanIDRequested: e.target.options,

            //clear Dispatchdetails select list
            dispatchPlanDetailsSelected: false, //turns off the map
            selectedDispatchPlanDetailsID: "0",
            dispatchPlanDetailsAll: [], //Reset
            dispatchPlanDetails: [], //Reset
            dispatchPlanDetailsNot: [], //Reset

        });
    }

    setSelectedDispatchPlanDetails = (e) => {
        var opts = [], opt;
        var data = [], datanot = [];
        for (var i = 0; i < e.target.options.length; i++) {
            opt = e.target.options[i];
            if (opt.selected) {
                opts.push(opt.value);
                data.push(this.state.dispatchPlanDetailsAll[i]);
            } else {
                //Not selected but kept incase needed for reference
                datanot.push(this.state.dispatchPlanDetailsAll[i]);
            }
        }
        this.setState({
            selectedDispatchPlanDetailsID: opts,
            dispatchPlanDetails: data,
            dispatchPlanDetailsNot: datanot,
        });

        //Store results
        var selected = false;
        if (data.length > 0) {
            selected = true;
        }
        this.setState({
            dispatchPlanDetailsSelected: selected,
        });
    }

    setReportHeaderCustomField(e, index) {
        //Turn off report loaded
        this.setState({
            loadedreport: false,
            PDFgenerated: false,
            PDFloading: false
        });

        var value = e.target.value;
        var array = this.state.reportHeaderCustomField;
        array[index] = value;
        this.setState({
            reportHeaderCustomField: array
        });
    };

    setSelectedRefLifecycleType = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        this.setState({
            selectedreflifecycletypeID: value
        });
    };

    createDispatchPlanDetailsForPost() {
        //Add to Dispatch List
        var data2 = this.state.dispatchPlanDetails;
        if (this.state.dispatchCheckboxIncludeRefSelected) {
            //Include Not details for reference
            var locationIndex = this.state.location.findIndex(x => x.id == this.state.selectedlocationID);
            var locationUTMZoneNum = parseInt(this.state.location[locationIndex].data["UTMZoneNum"]);
            var locationUTMZoneLet = this.state.location[locationIndex].data["UTMZoneLet"];

            for (var i = 0; i < this.state.dispatchPlanDetailsNot.length; i++) {
                var tempPlanDetailsNot = this.state.dispatchPlanDetailsNot[i];
                //manage for -ve numbers (react-leaflet cant handle)
                var tempLatLon = toLatLon(tempPlanDetailsNot.data["Easting"], tempPlanDetailsNot.data["Northing"], locationUTMZoneNum, locationUTMZoneLet); //Easting, Northing
                tempPlanDetailsNot["Latitude"] = tempLatLon.latitude;
                tempPlanDetailsNot["Longitude"] = tempLatLon.longitude;
                tempPlanDetailsNot["AssocCaptureId"] = 0;
                tempPlanDetailsNot["AssocCaptureDistDiff"] = 0;

                //Override
                tempPlanDetailsNot["dispatchOrder"] = -1; //Flag as only for reference

                data2.push(tempPlanDetailsNot);
            }
        }
        return data2;
    }

    selectionModeUpdate(object) {
        var tempreview = this.state.reviewSelections;
        tempreview.push(object);
        this.setState({
            reviewSelections: tempreview
        });
    };

    openFileBrowser = () => {
        this.fileInput.current.click();
    }

    setUploadPlanType = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.uploadPlanType != value) {
            this.setState({
                uploadPlanType: value,
                headersCopied: false,
            });
        }
    };

    buildAutoMapping = (formatDef, uploadedHeaders) => {
        const mapping = {};
        const allExpected = [...formatDef.required, ...formatDef.optional];
        const lowerUploaded = uploadedHeaders.map(h => h.toLowerCase().trim());

        allExpected.forEach(expected => {
            // Exact match first
            const exactIdx = uploadedHeaders.findIndex(h => h === expected);
            if (exactIdx >= 0) {
                mapping[expected] = uploadedHeaders[exactIdx];
                return;
            }
            // Case-insensitive exact match
            const ciIdx = lowerUploaded.findIndex(h => h === expected.toLowerCase());
            if (ciIdx >= 0) {
                mapping[expected] = uploadedHeaders[ciIdx];
                return;
            }
            // Alias match
            const aliases = COLUMN_ALIASES[expected];
            if (aliases) {
                for (const alias of aliases) {
                    const aliasIdx = lowerUploaded.findIndex(h => h === alias.toLowerCase());
                    if (aliasIdx >= 0) {
                        mapping[expected] = uploadedHeaders[aliasIdx];
                        return;
                    }
                }
            }
            // No match
            mapping[expected] = "";
        });
        return mapping;
    };

    findBestHeaderRow = (json, formatDef) => {
        let bestIdx = 0;
        let bestScore = 0;
        const maxScan = Math.min(json.length, 20);
        for (let i = 0; i < maxScan; i++) {
            if (!Array.isArray(json[i])) continue;
            const rowHeaders = json[i].map(h => String(h).trim().toLowerCase());
            let score = 0;
            formatDef.required.forEach(req => {
                if (rowHeaders.includes(req.toLowerCase())) {
                    score += 2;
                } else {
                    const aliases = COLUMN_ALIASES[req];
                    if (aliases && aliases.some(a => rowHeaders.includes(a.toLowerCase()))) {
                        score += 1;
                    }
                }
            });
            if (score > bestScore) {
                bestScore = score;
                bestIdx = i;
            }
        }
        return bestIdx;
    };

    checkHeadersAndMap = (json, fileType) => {
        const formatDef = PLAN_FORMAT_HEADERS[this.state.uploadPlanType];
        if (!formatDef) return false;

        // Determine header row
        let headerRowIndex = 0;
        if (this.state.uploadPlanType === "vulcan" || this.state.uploadPlanType === "blastlogic") {
            headerRowIndex = this.findBestHeaderRow(json, formatDef);
        }

        const uploadedHeaders = json[headerRowIndex].map(h => String(h).trim());
        // Persist trimmed headers so downstream processCSVPlan sees normalized names.
        json[headerRowIndex] = uploadedHeaders;
        const needsMapping = formatDef.required.some(h => !uploadedHeaders.includes(h));

        if (needsMapping) {
            const autoMapping = this.buildAutoMapping(formatDef, uploadedHeaders);
            this.setState({
                columnMappingRequired: true,
                columnMapping: autoMapping,
                uploadedRawHeaders: uploadedHeaders,
                pendingPlanJSON: json,
                pendingPlanFileType: fileType,
                pendingHeaderRowIndex: headerRowIndex,
            });
            return true; // mapping required, don't proceed
        }
        return false; // no mapping needed
    };

    applyColumnMapping = () => {
        const json = this.state.pendingPlanJSON;
        const mapping = this.state.columnMapping;
        const headerRowIndex = this.state.pendingHeaderRowIndex;
        const fileType = this.state.pendingPlanFileType;

        // Reset validation state for clean processing
        this.setState({ validationErrors: [], updatePlanInfo: [] });

        // Determine which column indices to keep (only columns the user actually mapped)
        const selectedOriginalNames = new Set(
            Object.values(mapping).filter(v => v !== "")
        );
        const headerRow = json[headerRowIndex].map(h => String(h).trim());
        const keptIndices = [];
        for (let i = 0; i < headerRow.length; i++) {
            if (selectedOriginalNames.has(headerRow[i])) {
                keptIndices.push(i);
            }
        }

        // Filter all rows to only keep selected columns, and rename headers
        for (let rowIdx = 0; rowIdx < json.length; rowIdx++) {
            json[rowIdx] = keptIndices.map(colIdx => {
                if (rowIdx === headerRowIndex) {
                    const trimmed = String(json[rowIdx][colIdx]).trim();
                    for (const [expected, selected] of Object.entries(mapping)) {
                        if (selected === trimmed) return expected;
                    }
                    return trimmed;
                }
                return json[rowIdx][colIdx];
            });
        }

        this.setState({
            columnMappingRequired: false,
            pendingPlanJSON: null,
            pendingPlanFileType: null,
            pendingHeaderRowIndex: 0,
        });

        // Re-run the normal processing pipeline
        this.processCSVPlan(json);
    };

    cancelColumnMapping = () => {
        this.setState({
            columnMappingRequired: false,
            columnMapping: {},
            uploadedRawHeaders: [],
            pendingPlanJSON: null,
            pendingPlanFileType: null,
            pendingHeaderRowIndex: 0,
        });
        this.uploadReset();
    };

    downloadTemplate = () => {
        const formatDef = PLAN_FORMAT_HEADERS[this.state.uploadPlanType];
        if (!formatDef) return;
        const allHeaders = [...formatDef.required, ...formatDef.optional];
        const sampleValues = {
            "Hole ID": "H001", "Hole Name": "H001",
            "Collar X (m)": "515000.0", "Collar Y (m)": "6835000.0", "Collar RL (m)": "350.0",
            "Drill X (m)": "515000.0", "Drill Y (m)": "6835000.0", "Drill RL (m)": "340.0",
            "Diameter (mm)": "127", "XY Tolerance (m)": "1.0", "Backfill Tolerance (m)": "0.5",
            "Redrill Tolerance (m)": "0.5", "Overstem Tolerance (m)": "0.5", "Understem Tolerance (m)": "0.5",
            "Stem X (m)": "515000.0", "Stem Y (m)": "6835000.0", "Stem RL (m)": "345.0",
            "Collar XY Tolerance (m)": "1.0", "Collar RL Tolerance+ (m)": "0.5", "Collar RL Tolerance- (m)": "0.5",
            "Drill XY Tolerance (m)": "1.0", "Drill RL Tolerance+ (m)": "0.5", "Drill RL Tolerance- (m)": "0.5",
            "Stem XY Tolerance (m)": "1.0", "Stem RL Tolerance+ (m)": "0.5", "Stem RL Tolerance- (m)": "0.5",
            "Length": "10.0", "Collar X": "515000.0", "Collar Y": "6835000.0", "Collar Z": "350.0",
            "Design collar X (m)": "515000.0", "Design collar Y (m)": "6835000.0", "Design collar Z (m)": "350.0",
            "Design drill depth (m)": "10.0", "Hole status": "Designed",
        };
        const headerRow = allHeaders.join(",");
        const sampleRow = allHeaders.map(h => sampleValues[h] || "").join(",");
        const csvContent = headerRow + "\n" + sampleRow + "\n";
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = this.state.uploadPlanType + "_template.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    copyHeaders = () => {
        const formatDef = PLAN_FORMAT_HEADERS[this.state.uploadPlanType];
        if (!formatDef) return;
        const allHeaders = [...formatDef.required, ...formatDef.optional];
        navigator.clipboard.writeText(allHeaders.join(", ")).then(() => {
            this.setState({ headersCopied: true });
            setTimeout(() => this.setState({ headersCopied: false }), 2000);
        });
    };

    setUploadPlanCS = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.uploadPlanCS != value) {
            this.setState({
                uploadPlanCS: value
            });
        }
    };

    setUploadPlanXYTolerance = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.uploadPlanXYTolerance != value) {
            this.setState({
                uploadPlanXYTolerance: value
            });
        }
    };

    setUploadPlanRLTolerancePlus = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.uploadPlanRLTolerancePlus != value) {
            this.setState({
                uploadPlanRLTolerancePlus: value
            });
        }
    };

    setUploadPlanRLToleranceMinus = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.uploadPlanRLToleranceMinus != value) {
            this.setState({
                uploadPlanRLToleranceMinus: value
            });
        }
    };

    processPlanJSON = async (json) => {
        var locationIndex = this.state.location.findIndex(x => x.id == this.state.selectedlocationID);
        var locationUTMZoneNum = parseInt(this.state.location[locationIndex].data["UTMZoneNum"]);
        var locationUTMZoneLet = this.state.location[locationIndex].data["UTMZoneLet"];

        var planHeaders = json.shift();
        // Validate Headers are as expected
        this.validateExcelPlanHeaders(planHeaders);

        if (this.state.uploadPlanCS != "wgs84") { //convert coords to wgs94
            var types = ["Collar", "Drill", "Stem"];

            for (var x = 0; x < types.length; x++) {
                var type = types[x];
                var tempCoordsEast = [];
                var tempCoordsNorth = [];
                var xCol = planHeaders.findIndex(data => data == type + " X (m)");
                var yCol = planHeaders.findIndex(data => data == type + " Y (m)");
                if (xCol > 0 && yCol > 0) {
                    for (var i = 0; i < json.length; i++) {
                        tempCoordsEast.push(json[i][xCol]);
                        tempCoordsNorth.push(json[i][yCol]);
                    }

                    var sendobject = [];
                    sendobject.push(JSON.stringify(tempCoordsEast));
                    sendobject.push(JSON.stringify(tempCoordsNorth));
                    var response = await this.postautoplanTransformCoordinates(sendobject);
                    var coordjson = await response.json();
                    if (coordjson == "8888") {
                        var currValidErrors = this.state.validationErrors;
                        currValidErrors.push("Coordinate transformation failed - ensure correct Coordinate System is selected");
                        this.setState({
                            validationErrors: currValidErrors
                        });
                        return;
                    }
                    
                    var data = JSON.parse(coordjson);

                    tempCoordsEast = data[0];
                    tempCoordsNorth = data[1];

                    for (var i = 0; i < json.length; i++) {
                        json[i][xCol] = tempCoordsEast[i];
                        json[i][yCol] = tempCoordsNorth[i];
                    }
                }
            }
        }

        var originalDataHeaders = []
        var originalDataRows = JSON.parse(JSON.stringify(json));

        if (planHeaders != null) {
            if (planHeaders.length > 0) {
                // Get Sheet Headers 'as is' to display in Table
                originalDataHeaders[0] = { name: "Row#", key: "0" }
                for (var i = 0; i < planHeaders.length; i++) {
                    originalDataHeaders[i + 1] = { name: planHeaders[i], key: String(i + 1) };
                }
                // Table Data 'as is'
                for (var i = 0; i < originalDataRows.length; i++) {
                    originalDataRows[i].splice(0, 0, i);
                }
            }
        }



        this.setState({
            cols: originalDataHeaders,
            rows: originalDataRows
        });

        var CoordCols = new Array();
        var RLCols = new Array();
        var DiamCols = new Array();
        var HoleIDCols = new Array();
        var ToleranceCols = new Array();

        planHeaders.forEach(function (name, index) {
            if (name.includes("X (m)") || name.includes("Y (m)")) {
                CoordCols.push(index);
            }
            else if (name.includes("RL (m)")) {
                RLCols.push(index);
            }
            else if (name == "Diameter (mm)") {
                DiamCols.push(index);
            }
            else if (name == "Hole ID") {
                HoleIDCols.push(index);
            }
            else if (name.includes("Tolerance")) {
                ToleranceCols.push(index);
            }
        });

        if (this.state.validationErrors.length == 0) {
            for (var i = 0; i < json.length; i++) {
                CoordCols.forEach(idx => {
                    this.validateCoord(json[i][idx], i);
                });
                RLCols.forEach(idx => {
                    this.validateRL(json[i][idx], i);
                });
                DiamCols.forEach(idx => {
                    this.validateDiam(json[i][idx], i);
                });
                HoleIDCols.forEach(idx => {
                    this.validateHoleID(json[i][idx], i);
                });
                ToleranceCols.forEach(idx => {
                    this.validateTolerance(json[i][idx], i);
                });
            }
        }


        var planData = {
            holeIDArr: new Array(),
            holeCoords: {
                eastingArr: new Array(),
                northingArr: new Array()
            },
            rlArr: new Array(),
            depthArr: new Array(),
            diamArr: new Array()
        }

        var extradata = [];
        var extradata_line = [];

        if (this.state.validationErrors.length == 0) {

            for (var i = 0; i < json.length; i++) {
                var tempHoleID = json[i][planHeaders.findIndex(name => name == "Hole ID")];
                var tempCoordsEast = json[i][planHeaders.findIndex(name => name == "Collar X (m)")];
                var tempCoordsNorth = json[i][planHeaders.findIndex(name => name == "Collar Y (m)")];
                var tempRL = json[i][planHeaders.findIndex(name => name == "Collar RL (m)")];
                var tempDepth = 0;
                var tempDiam = 0;

                planData.holeIDArr[i] = tempHoleID;
                planData.holeCoords.eastingArr[i] = tempCoordsEast;
                planData.holeCoords.northingArr[i] = tempCoordsNorth;
                planData.rlArr[i] = tempRL;
                planData.depthArr[i] = tempDepth;
                planData.diamArr[i] = tempDiam;
            }

            for (var i = 0; i < json.length; i++) {
                //Store LatLong
                var tempCoordsEast = planData.holeCoords.eastingArr[i];
                var tempCoordsNorth = planData.holeCoords.northingArr[i];

                if ((tempCoordsEast != null) && (tempCoordsNorth != null)) {
                    try{
                        var LatLong = toLatLon(tempCoordsEast, tempCoordsNorth, locationUTMZoneNum, locationUTMZoneLet); //Convert Easting, Northing, ZoneNumber (50), Zone Letter (J) to Lat Long
                    } catch (error) {
                        console.log("Error during LatLon conversion: " + error)
                        var currValidErrors = this.state.validationErrors;
                        currValidErrors.push("Error during coordinate processing - ensure all coordinates are valid");
                        this.setState({
                            validationErrors: currValidErrors
                        });
                        return;
                    }
                    extradata.push(LatLong); // Store Lat Long
                    if (extradata.length > 0) {
                        var tempArray = [extradata[i - 1], extradata[i]];
                        extradata_line.push(tempArray);
                    }
                }
            }

            // Validates for Duplicate HoleID's & Coordinates
            this.validatePlan(planData)

            // Validate against existing plan if Update - all existing hole data must match
            if (this.state.planModalOption == 'update') {
                this.validatePlanUpdate(originalDataHeaders, originalDataRows, this.state.selectedplanID).then((valid) => {
                    if (!valid) {
                        var currValidErrors = this.state.validationErrors;
                        currValidErrors.push("Plan Update Invalid");
                        this.setState({
                            validationErrors: currValidErrors
                        });
                    } else {
                        //this.setState({planUpdateInfo: })
                    }
                });
            }

            // If no errors set data as expected
            if (this.state.validationErrors.length == 0) {
                //Set state
                this.setState({
                    dataLoaded: true,
                    filePlanData: planData,
                    extra: extradata,
                    extra_line: extradata_line
                });
            }
        }
    }

    renderFileCSV = (fileObj) => {
        this.setState({ validationErrors: [], updatePlanInfo: [] });

        Papa.parse(fileObj, {
            skipEmptyLines: true,
            complete: results => {
                //console.log("Finished:", results.data);

                // Check if headers need mapping before format-specific processing
                if (this.checkHeadersAndMap(results.data, "csv")) return;

                this.processCSVPlan(results.data);
            }
        });
    }

    processCSVPlan = (csvData) => {
                if (this.state.uploadPlanType == "pulse") {
                    var json = csvData;

                    var pulseExcludeCols = ["Redrill Tolerance (m)", "Backfill Tolerance (m)", "XY Tolerance (m)", "Overstem Tolerance (m)", "Understem Tolerance (m)"];
                    let validationErrors = [...this.state.validationErrors];
                    let error = false;
                    json[0].forEach(name => {
                        if (pulseExcludeCols.includes(name)) {
                            validationErrors.push("Invalid Column Found: " + name + " - ensure correct format selected");
                            error = true;
                        }
                    });

                    var pointTypes = ["Collar", "Drill", "Stem"];
                    var tempArr = this.state.updatePlanInfo;
                    pointTypes.forEach((type) => {
                        if (json[0].includes(type + " X (m)") && json[0].includes(type + " Y (m)") && json[0].includes(type + " RL (m)")) {
                            if (!json[0].includes(type + " XY Tolerance (m)")) {
                                json[0].push(type + " XY Tolerance (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanXYTolerance);
                                }
                                tempArr.push("No " + type + " XY Tolerance column - default (" + this.state.uploadPlanXYTolerance + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, type + " XY Tolerance (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        type + `XY Tolerance (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }
                            }

                            if (!json[0].includes(type + " RL Tolerance+ (m)")) {
                                json[0].push(type + " RL Tolerance+ (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanRLTolerancePlus);
                                }
                                tempArr.push("No " + type + " RL Tolerance+ column - default (" + this.state.uploadPlanRLTolerancePlus + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, type + " RL Tolerance+ (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        type + `RL Tolerance+ (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }
                            }

                            if (!json[0].includes(type + " RL Tolerance- (m)")) {
                                json[0].push(type + " RL Tolerance- (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanRLToleranceMinus);
                                }
                                tempArr.push("No " + type + " RL Tolerance- column - default (" + this.state.uploadPlanRLToleranceMinus + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, type + " RL Tolerance- (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        type + ` RL Tolerance- (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }                          
                            }
                        }
                    });
                
                    if (error) return;

                    this.setState({ updatePlanInfo: tempArr });
                    this.processPlanJSON(json);

                } else if (this.state.uploadPlanType == "pulsesimple"){
                    var json = csvData;

                    var pulsesimpleExcludeCols = ["Collar XY Tolerance (m)", "Drill XY Tolerance (m)", "Stem XY Tolerance (m)", "Collar RL Tolerance- (m)", "Collar RL Tolerance+ (m)", "Drill RL Tolerance- (m)", "Drill RL Tolerance+ (m)", "Stem RL Tolerance- (m)", "Stem RL Tolerance+ (m)"];
                    let validationErrors = [...this.state.validationErrors];
                    var error = false;
                    json[0].forEach(name => {
                        if (pulsesimpleExcludeCols.includes(name)) {
                            validationErrors.push("Invalid Column Found: " + name + " - ensure correct format selected");
                            error = true;
                        }
                    });

                    if (error) {
                        // Was `validationErrors: tempArr` — but `tempArr` is declared via `var`
                        // a few lines below in the else branch, so hoisting made it undefined
                        // here, the next render hit `validationErrors != 0` (true for undefined),
                        // and `undefined.map(...)` crashed the whole app on bad-format uploads.
                        this.setState({
                            validationErrors: validationErrors
                        });
                        return;
                    } else {

                        var pointTypes = ["Collar", "Drill", "Stem"];
                        var tempArr = this.state.updatePlanInfo;
                        var type = "Drill";
                        if (json[0].includes(type + " X (m)") && json[0].includes(type + " Y (m)") && json[0].includes(type + " RL (m)")) {
                            if (!json[0].includes("XY Tolerance (m)")) {
                                json[0].push("XY Tolerance (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanXYTolerance);
                                }
                                tempArr.push("No XY Tolerance column - default (" + this.state.uploadPlanXYTolerance + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, "XY Tolerance (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        `XY Tolerance (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }
                            }

                            if (!json[0].includes("Backfill Tolerance (m)")) {
                                json[0].push("Backfill Tolerance (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanRLTolerancePlus);
                                }
                                tempArr.push("No Backfill Tolerance column - default (" + this.state.uploadPlanRLTolerancePlus + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, "Backfill Tolerance (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        `Backfill Tolerance (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }
                            }

                            if (!json[0].includes("Redrill Tolerance (m)")) {
                                json[0].push("Redrill Tolerance (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanRLToleranceMinus);
                                }
                                tempArr.push("No Redrill Tolerance column - default (" + this.state.uploadPlanRLToleranceMinus + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, "Redrill Tolerance (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        `Redrill Tolerance (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }
                            }

                            var idx = json[0].findIndex(x => x == "XY Tolerance (m)");

                            json[0].push("Collar XY Tolerance (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }

                            json[0].push("Drill XY Tolerance (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }

                            idx = json[0].findIndex(x => x == "Backfill Tolerance (m)");

                            json[0].push("Collar RL Tolerance+ (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }

                            json[0].push("Drill RL Tolerance+ (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }

                            idx = json[0].findIndex(x => x == "Redrill Tolerance (m)");

                            json[0].push("Collar RL Tolerance- (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }

                            json[0].push("Drill RL Tolerance- (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }
                        }

                        var type = "Stem";
                        if (json[0].includes(type + " X (m)") && json[0].includes(type + " Y (m)") && json[0].includes(type + " RL (m)")) {
                            if (!json[0].includes("XY Tolerance (m)")) {
                                json[0].push("XY Tolerance (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanXYTolerance);
                                }
                                tempArr.push("No XY Tolerance column - default (" + this.state.uploadPlanXYTolerance + "m) used");
                            } else {
                                // STEM XY? 
                            }

                            if (!json[0].includes("Overstem Tolerance (m)")) {
                                json[0].push("Overstem Tolerance (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanRLToleranceMinus);
                                }
                                tempArr.push("No Overstem Tolerance column - default (" + this.state.uploadPlanRLToleranceMinus + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, "Overstem Tolerance (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        `Overstem Tolerance (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }
                            }

                            if (!json[0].includes("Understem Tolerance (m)")) {
                                json[0].push("Understem Tolerance (m)");
                                for (var i = 1; i < json.length; i++) {
                                    json[i].push(this.state.uploadPlanRLTolerancePlus);
                                }
                                tempArr.push("No Understem Tolerance column - default (" + this.state.uploadPlanRLTolerancePlus + "m) used");
                            } else {
                                const blanks = this.findBlankHoleIds(json, "Understem Tolerance (m)", "Hole ID");

                                if (blanks.length > 0) {
                                    validationErrors.push(
                                        `Understem Tolerance (m) has blank values for ${blanks.length} hole(s). Please fill this column for all holes.`
                                    );
                                    error = true;
                                }
                            }

                            var idx = json[0].findIndex(x => x == "XY Tolerance (m)");

                            json[0].push("Stem XY Tolerance (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }

                            idx = json[0].findIndex(x => x == "Overstem Tolerance (m)");

                            json[0].push("Stem RL Tolerance+ (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }

                            idx = json[0].findIndex(x => x == "Understem Tolerance (m)");

                            json[0].push("Stem RL Tolerance- (m)");
                            for (var i = 1; i < json.length; i++) {
                                json[i].push(json[i][idx]);
                            }
                        }

                        if (error) {
                            this.setState({ validationErrors });
                            return;
                        }
                        this.setState({ updatePlanInfo: tempArr });
                        this.processPlanJSON(json);
                    }                    
                    
                } else if (this.state.uploadPlanType == "vulcan") {
                    var formattedJSON = [];
                    var inputJSON = csvData;

                    var headerRow = inputJSON.findIndex((row) => row.includes("Hole Name") && row.includes("Length") && row.includes("Collar X") && row.includes("Collar Y") && row.includes("Collar Z"));
                    if (headerRow >= 0) {
                        var inputHeader = inputJSON[headerRow];
                        var tempJSON = inputJSON.slice(headerRow + 1); //remove all rows above and including header

                        var formattedHeader = ["Hole ID", "Collar X (m)", "Collar Y (m)", "Collar RL (m)", "Drill X (m)", "Drill Y (m)", "Drill RL (m)", "Collar XY Tolerance (m)", "Collar RL Tolerance+ (m)", "Collar RL Tolerance- (m)", "Drill XY Tolerance (m)", "Drill RL Tolerance+ (m)", "Drill RL Tolerance- (m)"];
                        formattedJSON.push(formattedHeader);
                        tempJSON.forEach((dataRow) => {
                            var tempData = [];
                            tempData.push(dataRow[inputHeader.findIndex(x => x == "Hole Name")]);
                            tempData.push(dataRow[inputHeader.findIndex(x => x == "Collar X")]);
                            tempData.push(dataRow[inputHeader.findIndex(x => x == "Collar Y")]);
                            tempData.push(dataRow[inputHeader.findIndex(x => x == "Collar Z")]);
                            //assume straight holes
                            tempData.push(dataRow[inputHeader.findIndex(x => x == "Collar X")]); //Drill X
                            tempData.push(dataRow[inputHeader.findIndex(x => x == "Collar Y")]); //Drill Y
                            tempData.push(dataRow[inputHeader.findIndex(x => x == "Collar Z")] - dataRow[inputHeader.findIndex(x => x == "Length")]); //Drill RL
                            tempData.push(this.state.uploadPlanXYTolerance);
                            tempData.push(this.state.uploadPlanRLTolerancePlus);
                            tempData.push(this.state.uploadPlanRLToleranceMinus);
                            tempData.push(this.state.uploadPlanXYTolerance);
                            tempData.push(this.state.uploadPlanRLTolerancePlus);
                            tempData.push(this.state.uploadPlanRLToleranceMinus);

                            formattedJSON.push(tempData);
                        });

                        this.processPlanJSON(formattedJSON);
                    } else {
                        var tempArr = this.state.validationErrors;
                        tempArr.push("Error: File not in Vulcan format");
                        this.setState({ validationErrors: tempArr });
                    }

                } else if (this.state.uploadPlanType == "blastlogic") {
                    var formattedJSON = [];
                    var inputJSON = csvData;

                    var headerRow = inputJSON.findIndex((row) => row.includes("Hole ID") && row.includes("Design collar X (m)") && row.includes("Design collar Y (m)") && row.includes("Design collar Z (m)") && row.includes("Design drill depth (m)"));
                    if (headerRow >= 0) {
                        var inputHeader = inputJSON[headerRow];
                        var tempJSON = inputJSON.slice(headerRow + 1); //remove all rows above and including header

                        var formattedHeader = ["Hole ID", "Collar X (m)", "Collar Y (m)", "Collar RL (m)", "Drill X (m)", "Drill Y (m)", "Drill RL (m)", "Collar XY Tolerance (m)", "Collar RL Tolerance+ (m)", "Collar RL Tolerance- (m)", "Drill XY Tolerance (m)", "Drill RL Tolerance+ (m)", "Drill RL Tolerance- (m)", "Diameter (mm)"];
                        formattedJSON.push(formattedHeader);
                        var holeStatusIdx = inputHeader.findIndex(x => x == "Hole status");
                        var holeIdIdx = inputHeader.findIndex(x => x == "Hole ID");
                        var designXIdx = inputHeader.findIndex(x => x == "Design collar X (m)");
                        var designYIdx = inputHeader.findIndex(x => x == "Design collar Y (m)");
                        var designZIdx = inputHeader.findIndex(x => x == "Design collar Z (m)");
                        var designDepthIdx = inputHeader.findIndex(x => x == "Design drill depth (m)");
                        var blValidationErrors = [];

                        tempJSON.forEach((dataRow, rowIndex) => {
                            // Skip abandoned holes
                            if (holeStatusIdx >= 0 && dataRow[holeStatusIdx] && dataRow[holeStatusIdx].toString().trim().toLowerCase() === "abandoned") return;

                            var holeId = dataRow[holeIdIdx];
                            var rowLabel = holeId ? holeId : "Row " + (rowIndex + headerRow + 2);
                            var designX = dataRow[designXIdx];
                            var designY = dataRow[designYIdx];
                            var designZ = dataRow[designZIdx];
                            var designDepth = dataRow[designDepthIdx];

                            // Validate required design data
                            var missingFields = [];
                            if (holeId == null || holeId === "") missingFields.push("Hole ID");
                            if (designX == null || designX === "") missingFields.push("Design collar X");
                            if (designY == null || designY === "") missingFields.push("Design collar Y");
                            if (designZ == null || designZ === "") missingFields.push("Design collar Z");
                            if (designDepth == null || designDepth === "") missingFields.push("Design drill depth");

                            if (missingFields.length > 0) {
                                blValidationErrors.push("Error: " + rowLabel + " is missing: " + missingFields.join(", "));
                                return;
                            }

                            // Validate numeric values
                            if (isNaN(parseFloat(designX)) || isNaN(parseFloat(designY)) || isNaN(parseFloat(designZ)) || isNaN(parseFloat(designDepth))) {
                                blValidationErrors.push("Error: " + rowLabel + " has non-numeric design coordinate or depth values");
                                return;
                            }

                            var tempData = [];
                            tempData.push(holeId);
                            tempData.push(designX);
                            tempData.push(designY);
                            tempData.push(designZ);
                            //assume straight holes
                            tempData.push(designX); //Drill X
                            tempData.push(designY); //Drill Y
                            tempData.push((designZ - designDepth).toFixed(2)); //Drill RL
                            tempData.push(this.state.uploadPlanXYTolerance);
                            tempData.push(this.state.uploadPlanRLTolerancePlus);
                            tempData.push(this.state.uploadPlanRLToleranceMinus);
                            tempData.push(this.state.uploadPlanXYTolerance);
                            tempData.push(this.state.uploadPlanRLTolerancePlus);
                            tempData.push(this.state.uploadPlanRLToleranceMinus);
                            tempData.push(0); //Diameter - not in Blastlogic export, default to 0

                            formattedJSON.push(tempData);
                        });

                        if (blValidationErrors.length > 0) {
                            var tempArr = this.state.validationErrors;
                            blValidationErrors.forEach(e => tempArr.push(e));
                            this.setState({ validationErrors: tempArr });
                        } else {
                            this.processPlanJSON(formattedJSON);
                        }
                    } else {
                        var tempArr = this.state.validationErrors;
                        tempArr.push("Error: File not in Blastlogic format. Expected columns: Hole ID, Design collar X (m), Design collar Y (m), Design collar Z (m), Design drill depth (m)");
                        this.setState({ validationErrors: tempArr });
                    }

                }
    }

    renderFileXML = (fileObj) => {

        // Pass file object as param
        const fileReader = new FileReader();
        fileReader.readAsText(fileObj, "UTF-8");
        fileReader.onload = e => {
            // once file opened, parse
            const parser = new DOMParser();
            const xmlFile = parser.parseFromString(e.target.result, "application/xml");
            const holeData = this.validatePlanHoleData(xmlFile.getElementsByTagName("Hole"));

            var originalDataHeaders = [
                    {
                        "name": "Row#",
                        "key": "0"
                    },
                    {
                        "name": "Hole ID#",
                        "key": "1"
                    },
                    {
                        "name": "Easting",
                        "key": "2"
                    },
                    {
                        "name": "Northing",
                        "key": "3"
                    },
                    {
                        "name": "RL (m)",
                        "key": "4"
                    },
                    {
                        "name": "Design Depth (m)",
                        "key": "5"
                    },
                    {
                        "name": "Diameter (mm)",
                        "key": "6"
                    }
            ]

            // i starts at 1 to get first hole, 0'th element is NumberOfHoles. Finishes at -1 length as the last child is EquipmentData
            // HoleID: holeData[i].children[0].innerHTML 
            // Easting: holeData[i].children[2].children[1].innerHTML
            // Northing: holeData[i].children[2].children[0].innerHTML
            // RL (End Z): holeData[i].children[3].children[2].innerHTML
            // Depth (Start Z - End Z): holeData[i].children[2].children[2].innerHTML - holeData[i].children[3].children[2].innerHTML
            // Diameter: holeData[i].children[5].innerHTML

            var planData = {
                holeIDArr: new Array(),
                holeCoords: {
                    eastingArr: new Array(),
                    northingArr: new Array()
                },
                rlArr: new Array(),
                depthArr: new Array(),
                diamArr: new Array()
            }

            var extradata = [];
            var extradata_line = [];
            var originalDataRows = [];

            var locationIndex = this.state.location.findIndex(x => x.id == this.state.selectedlocationID);
            var locationUTMZoneNum = parseInt(this.state.location[locationIndex].data["UTMZoneNum"]);
            var locationUTMZoneLet = this.state.location[locationIndex].data["UTMZoneLet"];

            if (this.state.validationErrors.length == 0) {                
                for (var i = 0; i < holeData.length; i++) {
                    // * 1 to convert str to value
                    var tempHoleID = this.validateHoleID(holeData[i].getElementsByTagName("HoleId")[0].innerHTML, i)
                    var tempCoordsEast = this.validateCoord(holeData[i].getElementsByTagName("IR:PointY")[0].innerHTML * 1, i)
                    var tempCoordsNorth = this.validateCoord(holeData[i].getElementsByTagName("IR:PointX")[0].innerHTML * 1, i)
                    var tempRL = this.validateRL(holeData[i].getElementsByTagName("IR:PointZ")[1].innerHTML * 1, i)  // [1] for the end Z value
                    var tempDepth = this.validateDepth((holeData[i].getElementsByTagName("IR:PointZ")[0].innerHTML - holeData[i].getElementsByTagName("IR:PointZ")[1].innerHTML), i) // Start Z minus End Z
                    var tempDiam = this.validateDiam(holeData[i].getElementsByTagName("DrillBitDia")[0].innerHTML * 1, i)

                    planData.holeIDArr[i] = tempHoleID
                    planData.holeCoords.eastingArr[i] = tempCoordsEast
                    planData.holeCoords.northingArr[i] = tempCoordsNorth
                    planData.rlArr[i] = tempRL
                    planData.depthArr[i] = tempDepth
                    planData.diamArr[i] = tempDiam

                    //Store LatLong
                    if ((tempCoordsEast != null) && (tempCoordsNorth != null)) {
                        var LatLong = toLatLon(tempCoordsEast, tempCoordsNorth, locationUTMZoneNum, locationUTMZoneLet); //Convert Easting, Northing, ZoneNumber (50), Zone Letter (J) to Lat Long
                        extradata.push(LatLong); // Store Lat Long
                        if (extradata.length > 0) {
                            var tempArray = [extradata[i - 1], extradata[i]];
                            extradata_line.push(tempArray);
                        }

                        // Rows required for gps map 
                        originalDataRows[i] = [
                            i,
                            tempHoleID,
                            tempCoordsEast,
                            tempCoordsNorth,
                            tempRL,
                            tempDepth,
                            tempDiam
                        ]
                    }
                }
            
                //Validate Duplicates
                this.validatePlan(planData)

                if (this.state.validationErrors.length == 0) {
                    //Set state
                    this.setState({
                        dataLoaded: true,
                        cols: originalDataHeaders,
                        rows: originalDataRows,
                        filePlanData: planData,
                        extra: extradata,
                        extra_line: extradata_line
                    });
                }
                else {
                    this.setState({
                        cols: originalDataHeaders,
                        rows: originalDataRows
                    });
                }
            }
        }
    }

    findBlankHoleIds = (json, colName, holeIdCol = "Hole ID") => {
        const colIdx = json[0].indexOf(colName);
        const holeIdx = json[0].indexOf(holeIdCol);

        if (colIdx < 0) return []; // column doesn't exist

        const blanks = [];

        for (let i = 1; i < json.length; i++) {
            const row = json[i] || [];
            const v = (row.length > colIdx) ? row[colIdx] : undefined;

            const isBlank = (v === undefined || v === null || String(v).trim() === "");
            if (isBlank) {
                const holeId = (holeIdx >= 0 && row.length > holeIdx) ? String(row[holeIdx]).trim() : "";
                blanks.push({ rowIndex: i + 1, holeId });
            }
        }

        return blanks;
    };

    validateExcelPlanHeaders = (excelPlanHeaders) => {
        var requiredColumnHeaders = ["Hole ID", "Collar X (m)", "Collar Y (m)", "Collar RL (m)", "Drill X (m)", "Drill Y (m)", "Drill RL (m)"];
        var optionalColumnHeaders = ["Diameter (mm)", "Collar XY Tolerance (m)", "Collar RL Tolerance+ (m)", "Collar RL Tolerance- (m)", "Drill XY Tolerance (m)", "Drill RL Tolerance+ (m)", "Drill RL Tolerance- (m)",
            "Stem X (m)", "Stem Y (m)", "Stem RL (m)", "Stem XY Tolerance (m)", "Stem RL Tolerance+ (m)", "Stem RL Tolerance- (m)"];
        var depths = ["Collar", "Drill", "Stem"];

        var tempArr = this.state.validationErrors
        if (this.state.checkValidation) {
            if (excelPlanHeaders == null) {
                tempArr.push("Error: No plan data found");
            }
            else {
                requiredColumnHeaders.forEach((header) => {
                    var len = excelPlanHeaders.filter(x => x == header).length;
                    if (len !== 1) {
                        if (len < 1) {
                            tempArr.push("Missing column '"+header+"'");
                        } else {
                            tempArr.push("Duplicated column '" + header + "'");
                        }
                    }
                });

                optionalColumnHeaders.forEach((header) => {
                    var len = excelPlanHeaders.filter(x => x == header).length;
                    if (len !== 1) {
                        if (len > 1) {
                            tempArr.push("Duplicated column '" + header + "'");
                        }
                    }
                });

                depths.forEach((depth) => {
                    if (excelPlanHeaders.filter(x => x == x.includes(depth)).length > 0) {
                        if (excelPlanHeaders.filter(x => x == depth + " X (m)").length < 1) {
                            tempArr.push(depth + " Depth without '" + depth + " X (m)'");
                        }
                        if (excelPlanHeaders.filter(x => x == depth + " Y (m)").length < 1) {
                            tempArr.push(depth + " Depth without '" + depth + " Y (m)'");
                        }
                        if (excelPlanHeaders.filter(x => x == depth + " XY Tolerance (m)").length < 1) {
                            tempArr.push(depth + " Depth without '" + depth + " XY Tolerance (m)'");
                        }
                        if (excelPlanHeaders.filter(x => x == depth + " Tolerance+ (m)").length < 1) {
                            tempArr.push(depth + " Depth without '" + depth + " Tolerance+ (m)'");
                        }
                        if (excelPlanHeaders.filter(x => x == depth + " Tolerance- (m)").length < 1) {
                            tempArr.push(depth + " Depth without '" + depth + " Tolerance- (m)'");
                        }
                    }
                });

            }

            this.setState({
                validationErrors: tempArr
            });
        }        
    }

    validatePlanHoleData = (planHoleData) => {
        if (this.state.checkValidation) {
            var tempArr = this.state.validationErrors
            if (planHoleData == null) {
                tempArr.push("No hole data found in plan.")
                this.setState({
                    validationErrors: tempArr
                });
            }
        }
        return planHoleData;
    }

    validateDiam = (diam, row) => {
        if (this.state.checkValidation) {
            var tempArr = this.state.validationErrors;

            // handle blank strings too (common from CSV)
            if (diam === null || diam === undefined || String(diam).trim() === "") {
                tempArr.push(`Missing value in "Diameter (mm)" for row: ${row}`);
                this.setState({ validationErrors: tempArr });
                return diam;
            }

            const n = Number(diam);

            if (!Number.isFinite(n)) {
                tempArr.push(`Invalid value in "Diameter (mm)" for row: ${row} (value: "${diam}")`);
                this.setState({ validationErrors: tempArr });
                return diam;
            }

            if (n < 0) {
                tempArr.push(`Negative value in "Diameter (mm)" for row: ${row} (value: ${diam})`);
                this.setState({ validationErrors: tempArr });
                return diam;
            }

            // float check (must be int for DB)
            if (!Number.isInteger(n)) {
                tempArr.push(`"Diameter (mm)" must be a whole number (integer). Row: ${row} (value: ${diam})`);
                this.setState({ validationErrors: tempArr });
                return diam;
            }

            return n; // return parsed integer
        }

        return diam;
    };

    validateDepth = (depth, row) => {
        if (this.state.checkValidation) {
            var tempArr = this.state.validationErrors
            var NewDepth = depth;
            if ((depth == null) || (depth < 0)) {
                // Invalid depth
                tempArr.push("Negative or missing depth value for row: " + row)
                this.setState({
                    validationErrors: tempArr
                });
            }
            else {
                NewDepth = depth.toFixed(2)
            }
        }
        return NewDepth;
    }

    validateRL = (rl, row) => {
        if (this.state.checkValidation) {
            var tempArr = this.state.validationErrors
            if ((rl == null) || (rl < 0)) {
                // Invalid RL
                tempArr.push("Negative or missing RL value for row: " + row)
                this.setState({
                    validationErrors: tempArr
                });
            }
        }
        return rl;
    }

    validateCoord = (coord, row) => {
        if (this.state.checkValidation) {
            var tempArr = this.state.validationErrors
            if (coord == null) {
                // Invalid Coordinate
                tempArr.push("No coordinate found on row: " + row)
                this.setState({
                    validationErrors: tempArr
                });
            }
        }
        return coord;
    }

    validateTolerance = (tol, row) => {
        if (this.state.checkValidation) {
            var tempArr = this.state.validationErrors
            if (tol == null) {
                var errorMsg = "No tolerance found on row: " + row;
                if(tempArr.findIndex(msg => msg == errorMsg) == -1){ //not already added
                    // Invalid Coordinate
                    tempArr.push(errorMsg);
                    this.setState({
                        validationErrors: tempArr
                    });
                }
            }
        }
        return tol;
    }

    validateHoleID = (holeID, row) => {
        if (this.state.checkValidation) {
            var tempArr = this.state.validationErrors
            if ((holeID == null) || (holeID == "")) {
                // Invalid HoleID
                tempArr.push("No holeID found on row: " + row)
                this.setState({
                    validationErrors: tempArr
                });
            }
        }
        return holeID;
    }

    //Input Array 
    validatePlan = (planData) => {        
        if (this.state.checkValidation) {
            // Validate for duplicate Hole ID's
            var dupeHoleID = this.findDuplicates(planData.holeIDArr)
            if (dupeHoleID.length > 0) { // Duplicate found
                var dupeHoleArr = this.state.validationErrors
                for (var i = 0; i < dupeHoleID.length; i++) { // Find the index where that duplicate is
                    dupeHoleArr.push("Duplicate HoleID's found at rows: " + this.getAllIndexes(planData.holeIDArr, dupeHoleID[i]))
                }
                this.setState({
                    validationErrors: dupeHoleArr
                });
            }

            // Validate for duplicate Hole Coords
            // Check if a duplicate exists only in the Easting
            // If a duplicate exists, compare the northing with the other northing pairs (only east duplicate pairs)
            // If a northing duplicate exists then 
            var eastDupe = this.findDuplicates(planData.holeCoords.eastingArr)
            if (eastDupe.length > 0) // At least 1 dupe in Easting array found
            {
                var eastIndexArr = [];
                for (var i = 0; i < eastDupe.length; i++) { // Find the index where that duplicate is
                    eastIndexArr.push(this.getAllIndexes(planData.holeCoords.eastingArr, eastDupe[i]))
                }
                if (eastIndexArr.length > 0) // At least 1 dupe index found (Sanity Check)
                {
                    this.findNorthDuplicates(planData.holeCoords.northingArr, eastIndexArr)
                }
            }
        }
    }

    //Input Array 
    validatePlanUpdate = async (planCols, planRows, currentPlanID) => {
        var colsToMatch = ["Hole ID", "Diameter (mm)", "Collar X (m)", "Collar Y (m)", "Collar RL (m)", "Collar XY Tolerance (m)", "Collar RL Tolerance+ (m)", "Collar RL Tolerance- (m)", "Drill X (m)", "Drill Y (m)", "Drill RL (m)", "Drill XY Tolerance (m)", "Drill RL Tolerance+ (m)", "Drill RL Tolerance- (m)"];

        var currValidErrors = this.state.validationErrors;

        var validUpdate = true;
        var rawData = await this.getPlan(currentPlanID).then((plan) => {
            if (plan != null) {
                return JSON.parse(plan.rawData);
            } else {
                return null;
            }
        });

        var newCount = 0;
        var modCount = 0;
        var delCount = 0;
        var sameCount = 0;

        if (rawData != null) {
            planRows.forEach((row) => { //each new plan hole
                var current = rawData.find(x => x["Hole ID"] == row[planCols.findIndex(data => data.name == "Hole ID")]);
                if (current != null) { //holeID found in current plan
                    var modded = false;
                    colsToMatch.forEach((colName) => {
                        if (row[planCols.findIndex(data => data.name == colName)] != current[colName]) {
                            modded = true;
                        }
                    })
                    if (modded) {
                        modCount++;
                    } else {
                        sameCount++;
                    }
                } else { //holeID NOT found in current plan
                    newCount++;
                }

            });

            rawData.forEach((row) => { //each existing plan hole
                var current = planRows.find(x => x[planCols.findIndex(data => data.name == "Hole ID")] == row["Hole ID"]);
                if (current == null) { //holeID NOT found in new plan
                    delCount++;
                }
            });
        } else {
            validUpdate = false; //if current plan null

            currValidErrors.push("Could not get existing Plan");
        }

        if (sameCount <= 0 && modCount <= 0) {
            currValidErrors.push("NO MATCHING HOLES ENSURE THIS IS THE CORRECT PLAN BEFORE UPLOAD");
        }

        var tempArr = this.state.updatePlanInfo;
        tempArr.push("Matching Holes: " + (sameCount + modCount) + " (" + modCount + " Modified)", "Added Holes: " + newCount, "Deleted Holes: " + delCount);

        this.setState({ validationErrors: currValidErrors, updatePlanInfo: tempArr});

        return validUpdate;
    }

    // Find North Duplicates only concerned with the east duplicates
    findNorthDuplicates = (northArr, eastIndexArr) => {
        var duplicateCoords = this.state.validationErrors;
        var dupeCoords = false;
        for (var y = 0; y < eastIndexArr.length; y++) { // Loop through for each Duplicate Value
            for (var i = 0; i < eastIndexArr[y].length; i++) { // Loop through for each duplicate per value
                for (var n = i; n < eastIndexArr[y].length; n++) { //Check yourself and ahead in array (dont check again for past duplicates)
                    if (i != n) { // Dont comapre the same index as it will always return a duplicate (ie checking yourself with yourself)
                        if (northArr[eastIndexArr[y][i]] == northArr[eastIndexArr[y][n]]) {//Duplicate North & East Pair found
                            duplicateCoords.push("Duplicate Coordinates found at row: " + eastIndexArr[y][i] + " and row: " + eastIndexArr[y][n])
                            dupeCoords = true;
                        }
                    }
                }
            }
        }
        if (dupeCoords) {
            this.setState({
                validationErrors: duplicateCoords
            });
        }
        return duplicateCoords
    }

    // Find duplicate elements in an array
    findDuplicates = (validateArr) => {
        const distinct = new Set(validateArr); // Sets to improve performance
        const filtered = validateArr.filter(item => {
            if (distinct.has(item)) {   // Remove the element from the set on very first encounter
                distinct.delete(item);
            }
            else {
                return item; // return the element on subsequent encounters
            }
        });
        return [...new Set(filtered)] // Use set as there could be duplicate dupes...
    }

    // Find index of all occurances where val is in arr
    getAllIndexes = (arr, val) => { 
        var indexes = [];
        for (var i = 0; i < arr.length; i++) {
            if (arr[i] === val) {
                indexes.push(i);
            }
        }
        return indexes;
    }

    planFileHandler = (event) => {
        if (event.target.files.length) {
            this.setState({ dataLoaded: false });
            let fileObj = event.target.files[0];
            let fileName = fileObj.name;
            var extension = fileName.slice(fileName.lastIndexOf('.') + 1);

            this.setState({uploadPlanFile: fileObj});

            //check for file extension and pass only if it is .csv
            if (extension === "csv") {
                this.setState({
                    uploadedFileName: fileName,
                    isFormInvalid: false
                });
                this.renderFileCSV(fileObj);
            }
            //else if (fileName.slice(fileName.lastIndexOf('.') + 1) === "xml") {
            //    this.setState({
            //        uploadedFileName: fileName,
            //        isFormInvalid: false
            //    });
            //    this.renderFileXML(fileObj)
            //}
            else {
                this.setState({
                    isFormInvalid: true,
                    uploadedFileName: ""
                })
            }
        }
    }

    captureFileHandler = (event) => {
        if (event.target.files.length) {
            //For loop through multiple files
            for (var i = 0; i < event.target.files.length; i++) {
                let fileObj = event.target.files[i];
                let fileName = fileObj.name;

                //check for file extension and pass only if it is .json and display error message otherwise
                if (fileName.slice(fileName.lastIndexOf('.') + 1) === "json") {
                    this.setState({
                        uploadedFileName: fileName,
                        isFormInvalid: false
                    });

                    //Send the file
                    const fileReader = new FileReader();
                    fileReader.readAsText(fileObj, "UTF-8");
                    fileReader.onload = e => {
                        //Send to API
                        this.postDataCaptureInsert(e.target.result);
                    }
                }
                else {
                    this.setState({
                        isFormInvalid: true,
                        uploadedFileName: ""
                    })
                }
            }
        }
    }

    toggleExportDataModal = () => {
        //Turn off report loaded
        this.setState({ downloadCSV: false, showExportDataModal: !this.state.showExportDataModal});
    }

    setExportDataType = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.exportDataType != value ) {
            this.setState({
                exportDataType: value
            });
        }
    };

    setExportDataCS = (e) => {
        var value = (e.target != null) ? e.target.value : e;
        if (this.state.exportDataCS != value) {
            this.setState({
                exportDataCS: value
            }, () => {
                this.getPlanReport(this.state.selectedlocationID, this.state.selectedsubsequenceID, this.state.selectedplanID, this.state.exportDataCS);
            });
        }
    };

    getTimeCutoff = () => {
        const now = new Date();
        switch (this.state.exportFilterTimeWindow) {
            case "24h": return new Date(now.getTime() - 24 * 60 * 60 * 1000);
            case "7d": return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            case "custom":
                if (this.state.exportFilterCustomDate) {
                    return new Date(this.state.exportFilterCustomDate);
                }
                return new Date(0); // no cutoff if no date set
            default: return new Date(0);
        }
    }

    getFilteredReportData = () => {
        let diffvsPlan = [...this.state.selectedReportDetails.Summary.DiffvsPlan];
        let additional = [...this.state.selectedReportDetails.Summary.Additional];
        let missing = [...this.state.selectedReportDetails.Summary.Missing];
        let reviewPlans = [...this.state.selectedReportDetails.ReviewPlans];

        // Time window filter (applies to DiffvsPlan and Additional)
        if (this.state.exportFilterTimeWindow !== "all") {
            const cutoff = this.getTimeCutoff();
            diffvsPlan = diffvsPlan.filter(row => row.DateTime_Capture_Raw && new Date(row.DateTime_Capture_Raw) >= cutoff);
            additional = additional.filter(row => row.DateTime_Capture_Raw && new Date(row.DateTime_Capture_Raw) >= cutoff);
        }

        // Compliance filter (applies to DiffvsPlan only)
        if (this.state.exportFilterCompliance.length > 0) {
            const severityField = "Diff_" + this.state.reportHeaderCustomField[5] + "_Severity";
            const severityField2 = "Diff_" + this.state.reportHeaderCustomField[6] + "_Severity";
            diffvsPlan = diffvsPlan.filter(row => {
                if (this.state.exportFilterCompliance.includes("redrill") && row[severityField] < 3) return true;
                if (this.state.exportFilterCompliance.includes("atdesign") && row[severityField] >= 3 && row[severityField2] <= 3) return true;
                if (this.state.exportFilterCompliance.includes("backfill") && row[severityField2] > 3) return true;
                return false;
            });
        }

        // Wet only filter
        if (this.state.exportFilterWetOnly) {
            diffvsPlan = diffvsPlan.filter(row => row.wetness_Capture === "Wet" || row.wetness_Capture === "Wet*");
            additional = additional.filter(row => row.wetness_Capture === "Wet" || row.wetness_Capture === "Wet*");
        }

        // Default: measurements only — strip unmeasured plan holes and Missing
        // Opt-in: include design data (unmeasured plan rows + Missing sheet)
        if (!this.state.exportFilterIncludeDesign) {
            const matchedPlanIds = new Set(diffvsPlan.map(d => d.PlanDetailsId));
            reviewPlans = reviewPlans.filter(p => matchedPlanIds.has(p.AssocPlanDetailsId));
            missing = [];
        }

        return { diffvsPlan, additional, missing, reviewPlans };
    }

    getComplianceString = (diffData) => {
        const severityField = "Diff_" + this.state.reportHeaderCustomField[5] + "_Severity";
        const severityField2 = "Diff_" + this.state.reportHeaderCustomField[6] + "_Severity";
        if (diffData[severityField] < 3) {
            if (this.state.locationOptions?.RedrillMode === "Explicit") {
                return "TBC by Engineer";
            }
            const type = this.state.refcampaigntype.find(t => t.priorityOrder % 100 == 20 && t.lifecycleType == this.state.activeLifecycleTypeId);
            return type ? type.name : "Redrill";
        } else if (diffData[severityField2] > 3) {
            const type = this.state.refcampaigntype.find(t => t.priorityOrder % 100 == 30 && t.lifecycleType == this.state.activeLifecycleTypeId);
            return type ? type.name : "Backfill";
        }
        return "At Design";
    }

    safeFixed = (val, dp = 2) => (val != null && !isNaN(val)) ? Number(val).toFixed(dp) : "";
    safeParseField = (jsonStr, field) => { try { return jsonStr ? JSON.parse(jsonStr)[field] || "" : ""; } catch (e) { return ""; } };

    buildDiffvsPlanExportData = (filtered) => {
        const fullBlastName = this.state.report?.Name || "";
        const blastName = fullBlastName.replace(/-V\d+$/i, "");
        const headers = [
            "Hole ID", "Plan CollarX (Easting)", "Plan CollarY (Northing)", "Plan CollarZ (m)",
            "XY Tolerance", "Redrill Tolerance", "Backfill Tolerance", "Plan HoleLength (m)",
            "HoleLength (m)", "Difference (m)", "Compliance",
            "Potential Wetness", "Estimated Water Height (m)", "PrevAuto HoleLength (m)",
            "CollarX (Easting)", "CollarY (Northing)", "CollarZ (m)",
            "ToeX (Easting)", "ToeY (Northing)", "ToeZ (m)",
            "DateTime", "Handset ID", "Reflectivity (%)", "Capture Type", "Comment",
            "Blast Name", "PlanDetails ID", "Capture ID", "Calculation Type"
        ];
        const rows = [];
        for (const planrow of filtered.reviewPlans) {
            const diffData = filtered.diffvsPlan.find(row => row.PlanDetailsId === planrow.AssocPlanDetailsId);
            const row = [
                planrow.AssocPlanDetailsHoleId,
                planrow.Easting.toFixed(2), planrow.Northing.toFixed(2), planrow.RL.toFixed(2),
                planrow.AssocXYTolerance, planrow.AssocRLToleranceMinus, planrow.AssocRLTolerancePlus,
                planrow.Length.toFixed(2)
            ];
            if (diffData) {
                row.push(
                    this.safeFixed(diffData.HoleLength_Capture),
                    this.safeFixed(diffData.Diff_HoleDepth),
                    this.getComplianceString(diffData),
                    diffData.wetness_Capture || "",
                    diffData.waterHeight || "",
                    this.safeFixed(diffData.PreviousAuto_HoleDepth),
                    this.safeFixed(diffData.CollarEast_Capture), this.safeFixed(diffData.CollarNorth_Capture), this.safeFixed(diffData.CollarDepth_Capture),
                    this.safeFixed(diffData.ToeRLEast_Capture), this.safeFixed(diffData.ToeRLNorth_Capture), this.safeFixed(diffData.ToeRL_Capture),
                    diffData.DateTime_Capture || "",
                    this.safeParseField(diffData.Data_Capture, "handsetID"),
                    this.safeFixed(diffData.reflecthole_Capture),
                    diffData.capturetype_Capture || "",
                    diffData.commentString_Capture || "",
                    blastName,
                    diffData.PlanDetailsId || "",
                    diffData.CaptureId || "",
                    diffData.CalculationType || ""
                );
            } else {
                // Plan row with no capture data — fill empty cells
                for (var k = 0; k < 21; k++) row.push("");
            }
            rows.push(row);
        }
        return { headers, rows };
    }

    buildAdditionalExportData = (filtered) => {
        const headers = [
            "Hole ID", "CollarX (Easting)", "CollarY (Northing)", "CollarZ (m)",
            "ToeX (Easting)", "ToeY (Northing)", "ToeZ (m)",
            "HoleLength (m)", "PrevAuto HoleLength (m)", "DateTime",
            "Handset ID", "Reflectivity (%)", "Wetness", "Capture Type", "Comment", "Capture ID"
        ];
        const rows = filtered.additional.map(row => [
            row.Name || "",
            this.safeFixed(row.CollarEast_Capture), this.safeFixed(row.CollarNorth_Capture), this.safeFixed(row.CollarDepth_Capture),
            this.safeFixed(row.ToeRLEast_Capture), this.safeFixed(row.ToeRLNorth_Capture), this.safeFixed(row.ToeRL_Capture),
            this.safeFixed(row.HoleLength_Capture), this.safeFixed(row.PreviousAuto_HoleDepth),
            row.DateTime_Capture || "",
            this.safeParseField(row.Data_Capture, "handsetID"),
            row.reflecthole_Capture || "", row.wetness_Capture || "", row.capturetype_Capture || "",
            row.commentString_Capture || "", row.CaptureId || ""
        ]);
        return { headers, rows };
    }

    buildMissingExportData = (filtered) => {
        const headers = ["Hole ID", "PlanDetails ID"];
        const rows = filtered.missing.map(row => [row.Name, row.PlanDetailsId]);
        return { headers, rows };
    }

    exportData = () => {
        this.postUserLog("Plan Export Data", "JSON", JSON.stringify({
            planID: this.state.selectedplanID, type: this.state.exportDataType,
            coordinates: this.state.exportDataCS, dryTolerance: this.state.optionsWaterHeightZero,
            filters: {
                measuredOnly: this.state.exportFilterMeasuredOnly,
                timeWindow: this.state.exportFilterTimeWindow,
                compliance: this.state.exportFilterCompliance,
                wetOnly: this.state.exportFilterWetOnly
            }
        }), this.state.selectedlocationID);

        this.setState({ exportingData: true }, () => {
            this.ExportCSV();
        });
    }

    ExportCSV = () => {
        this.setState({ downloadCSV: false }, () => {
            const filtered = this.getFilteredReportData();
            const { headers, rows } = this.buildDiffvsPlanExportData(filtered);
            const csvData = [headers, ...rows];

            var currDate = new Date();
            const datestring = currDate.getFullYear() + "-" + (currDate.getMonth() + 1) + "-" + currDate.getDate() + "_" + String(currDate.getHours()).padStart(2, '0') + String(currDate.getMinutes()).padStart(2, '0');
            const filename = this.state.report.Name + "_PulseExport_" + datestring + ".csv";

            this.setState({
                exportCSVData: csvData, exportDataFilename: filename, downloadCSV: true, exportingData: false
            });
        });
    }


    toggleTab(tab, campaignPriorityOrderRemaining) {
        if (this.state.activeTab !== tab) {
            //Update map style
            this.setMapStyle(campaignPriorityOrderRemaining, this.state.activeLifecycleTypeId);

            //Set Tab
            this.setState({
                activeTab: tab
            });
        }
    }

    setMapStyle(campaignPriorityOrderRemaining, campaignLifecycleTypeId) {
        var campaignStyle;
        for (var i = 0; i < this.state.refcampaigntype.length; i++) {
            var calcPriorityOrderRemaining = (this.state.refcampaigntype[i].priorityOrder % 100); //i.e. 10 for drill depths/column, 20 for redrill/below design, 30 for backfill/above design
            if (calcPriorityOrderRemaining === campaignPriorityOrderRemaining && this.state.refcampaigntype[i].lifecycleType === campaignLifecycleTypeId) {
                campaignStyle = this.state.refcampaigntype[i].displayStyle;
                break;
            }
        }

        if (campaignStyle) {
            this.setState({
                GPSmapStyle: JSON.parse(campaignStyle)
            });
        }
    }

    imagesZippedGenerate = () => {
        //Set Loading
        this.setState({
            loadingplanimages: true
        });

        //Get zip from server
        this.getDatacaptureImagesZipped(this.state.selectedplanID);
    }

    setSelectedDispatchReviewRequired(id) {
        this.setState({ selectedDispatchTreeId: id, showDispatchRRMenu: false, showDispatchLatestMenu: false });
        this.setSelectedDispatch(id.split("-")[1]);
    }

    getReviewRequiredDispatch(treeNode) {
        return collectDispatchNodes(treeNode).filter((d) => d.id.includes("reviewRequired"));
    }

    getLatestCapturesDispatchNodes(treeNode, dispatches) {
        if (treeNode.length > 0 && treeNode[0].children != null) {
            var rrNodes = collectDispatchNodes(treeNode).map((dispatch) => {
                var dispatchNode = dispatches.find((disp) => disp.id == dispatch.id.split('-')[1]);
                if (dispatchNode) {
                    dispatchNode = { ...dispatchNode }; //take copy
                    dispatchNode.id = dispatch.id;
                    return dispatchNode;
                }
                return null;
            }).filter((n) => n != null);
            return rrNodes.sort((a, b) => new Date(a.dispatchTime) < new Date(b.dispatchTime) ? 1 : -1); //always sort as dipsatchTime desc
        } else {
            return null;
        }
    }

    getExportTableData() {
           var tableData = [];
           this.state.selectedReportDetails.ReviewPlans.map(planrow => {
            var rowData = [];

            var diffData = this.state.selectedReportDetails.Summary.DiffvsPlan.filter(row => row.PlanDetailsId == planrow.AssocPlanDetailsId)[0];

            rowData.push(<td>{planrow.AssocPlanDetailsHoleId}</td>); //Name
            rowData.push(<td>{planrow.Easting.toFixed(2)}</td>); //CollarEast_Plan
            rowData.push(<td>{planrow.Northing.toFixed(2)}</td>); //CollarNorth_Plan
            rowData.push(<td>{planrow.RL.toFixed(2)}</td>); //CollarRL_Plan
            rowData.push(<td>{planrow.AssocXYTolerance}</td>); //XY_Tolerance
            rowData.push(<td>{planrow.AssocRLToleranceMinus}</td>); //RL_ToleranceMinus - Redrill/Overstem
            rowData.push(<td>{planrow.AssocRLTolerancePlus}</td>); //RL_TolerancePlus - Backfill/Understem
            rowData.push(<td>{planrow.Length.toFixed(2)}</td>);//HoleLength_Plan

            if (diffData != null){
                rowData.push(<td>{diffData.HoleLength_Capture.toFixed(2)}</td>);
                rowData.push(<td>{diffData.Diff_HoleDepth.toFixed(2)}</td>);

                if (diffData["Diff_" + this.state.reportHeaderCustomField[5] + "_Severity"] < 3){
                    if (this.state.locationOptions?.RedrillMode === "Explicit") {
                        rowData.push(<td>TBC by Engineer</td>);
                    } else {
                        rowData.push(<td>{this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 20 && type.lifecycleType == this.state.activeLifecycleTypeId).name}</td>);
                    }
                } else if (diffData["Diff_" + this.state.reportHeaderCustomField[6] + "_Severity"] > 3) {
                    rowData.push(<td>{this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 30 && type.lifecycleType == this.state.activeLifecycleTypeId).name}</td>);
                } else {
                    rowData.push(<td>At Design</td>);
                }

                rowData.push(<td>{diffData.wetness_Capture}</td>); // Wet or Dry
                rowData.push(<td>{diffData.waterHeight}</td>); // Estimated Water Height
                rowData.push(<td>{diffData.PreviousAuto_HoleDepth.toFixed(2)}</td>); // Previous Auto Length 
                rowData.push(<td>{diffData.CollarEast_Capture.toFixed(2)}</td>); // Colar Captured X
                rowData.push(<td>{diffData.CollarNorth_Capture.toFixed(2)}</td>); // Collar Captured Y
                rowData.push(<td>{diffData.CollarDepth_Capture.toFixed(2)}</td>); // Collar Captured RL
                rowData.push(<td>{diffData.ToeRLEast_Capture.toFixed(2)}</td>); // Toe X
                rowData.push(<td>{diffData.ToeRLNorth_Capture.toFixed(2)}</td>); // Toe Y
                rowData.push(<td>{diffData.ToeRL_Capture.toFixed(2)}</td>); // Toe RL
                rowData.push(<td>{diffData.DateTime_Capture}</td>); // Date time of Capture
                rowData.push(<td>{JSON.parse(diffData.Data_Capture).handsetID}</td>); // HandsetID
                rowData.push(<td>{diffData.reflecthole_Capture.toFixed(2)}</td>); // Reflectivity
                rowData.push(<td>{diffData.capturetype_Capture}</td>); // Capture Type
                rowData.push(<td>{diffData.commentString_Capture}</td>); // Comment

                const fullBlastName = this.state.report?.Name || "";
                const blastName = fullBlastName.replace(/-V\d+$/i, "");
                rowData.push(<td>{blastName}</td>); // BlastName without plan version

                rowData.push(<td>{diffData.PlanDetailsId}</td>); // Plan Details ID
                rowData.push(<td>{diffData.CaptureId}</td>); // Capture ID
                rowData.push(<td>{diffData.CalculationType}</td>); // Calculation Type

                } else {
                    for (var i = 0; i < 17; i++){
                       rowData.push(<td></td>);
                    }
                }

                tableData.push(<tr>{rowData}</tr>);

            });

            return tableData;

    }

    dispatchReviewRequiredMenu() {
        if (this.state.treeData[0].children != null && !this.state.loadingsublocation) {
            var elements = this.getReviewRequiredDispatch(this.state.treeData).map((dispatch) => <ListGroupItem className="dispatchRRMenu" action style={{ color: "#EC6519", cursor: "pointer" }} onClick={() => this.setSelectedDispatchReviewRequired(dispatch.id)}>{dispatch.name}</ListGroupItem>)

            return (<div className="dispatchRRMenu" style={{ position: 'fixed', borderradius: '5px', top: this.state.dispatchRRMenuY + "px", left: this.state.dispatchRRMenuX + "px", zIndex: 100 }}><ListGroup className="dispatchRRMenu">{elements}</ListGroup></div>);
        } else {
            return <></>;
        }
    }

    dispatchReviewRequiredClick(e) {
        this.setState({ showDispatchRRMenu: true, dispatchRRMenuX: e.pageX, dispatchRRMenuY: e.pageY });

        const handleClick = (f) => {
            if (!f.target.className.includes("dispatchRRMenu")) {
                this.setState({ showDispatchRRMenu: false });
                document.removeEventListener("click", handleClick, { capture: true });
            }
        }
        document.addEventListener("click", handleClick, { capture: true });
    }

    dispatchLatestCapturesMenu() {
        if (this.state.treeData[0].children != null && !this.state.loadingsublocation){
            var clickable = (this.getLatestCapturesDispatchNodes(this.state.treeData, this.state.dispatchesLatestCaptures) || []).filter((d) => !d.archived);
            var archivedEntries = (this.state.dispatchesLatestCaptures || []).filter((d) => d.archived);
            var merged = clickable.concat(archivedEntries).sort((a, b) => new Date(a.dispatchTime) < new Date(b.dispatchTime) ? 1 : -1);
            var elements = merged.map((dispatch) => dispatch.archived
                ? <ListGroupItem className="dispatchLatestMenu" style={{ color: "#999", cursor: "default" }}>{dispatch.dispatchTimeString} - {dispatch.name} (archived)</ListGroupItem>
                : <ListGroupItem className="dispatchLatestMenu" action style={{ color: "#EC6519", cursor: "pointer" }} onClick={() => this.setSelectedDispatchReviewRequired(dispatch.id)}>{dispatch.dispatchTimeString} - {dispatch.name}</ListGroupItem>);

            return (<div className="dispatchLatestMenu" style={{ position: 'fixed', borderradius: '5px', top: this.state.dispatchLatestMenuY + "px", left: this.state.dispatchLatestMenuX + "px", zIndex: 100 }}><ListGroup className="dispatchLatestMenu">{elements}</ListGroup></div>);
        }else{
            return <></>;
        }
    }

    dispatchLatestCapturesClick(e) {
        this.setState({ showDispatchLatestMenu: true, dispatchLatestMenuX: e.pageX, dispatchLatestMenuY: e.pageY });

        const handleClick = (f) => {
            if (!f.target.className.includes("dispatchLatestMenu")) {
                this.setState({ showDispatchLatestMenu: false });
                document.removeEventListener("click", handleClick, { capture: true });
            }
        }
        document.addEventListener("click", handleClick, { capture: true });
    }

    configSublocationInsertOrUpdate() {
        if (this.state.configId == -1) {
            this.postsublocationInsert(this.state.selectedlocationID, this.state.createsublocationName);
        } else {
            this.postsublocationUpdateName(this.state.configId, this.state.createsublocationName);
        }
        this.setState({ configOpen: false, configId: -1, configName: "" });
    }

    configSequenceInsertOrUpdate() {
        if (this.state.configId == -1) {
            this.postsequenceInsert(this.state.selectedsublocationID, this.state.createsequenceName);
        } else {
            this.postsequenceUpdateName(this.state.configId, this.state.createsequenceName);
        }
        this.setState({ configOpen: false, configId: -1, configName: "" });
    }

    configSubsequenceInsertOrUpdate() {
        if (this.state.configId == -1) {
            if (this.state.configOption === "subseqflat") {
                this.postsubsequenceInsertFlat(this.state.selectedlocationID, this.state.createsubsequenceName);
            } else {
                this.postsubsequenceInsert(this.state.selectedsequenceID, this.state.createsubsequenceName);
            }
        } else {
            this.postsubsequenceUpdateName(this.state.configId, this.state.createsubsequenceName);
        }
        this.setState({ configOpen: false, configId: -1, configName: "" });
    }

    configForm(configOption) {
        var formGroups = [];
        var title = "";
        var insert = (this.state.configId == -1);
        if (configOption == "subloc") {
            if (insert){
                title = "Add Pit";
            }
            else {
                title = "Rename Pit";
            }
            formGroups.push(<FormGroup className="justify-content-md-left" row>
                <Col sm="12">
                    <br />
                    <InputGroup>
                        <Input onChange={this.setCreateSubLocationName} defaultValue={this.state.configName} onKeyPress={(event) => {
                            if (event.charCode == 13) {
                                event.preventDefault();
                                if (!(this.state.createsublocationName == "") && this.state.createsublocationName.match("^[0-9a-zA-Z-_ ]+$") && this.state.createsublocationName !== this.state.configName) {
                                    this.configSublocationInsertOrUpdate();
                                }
                            }
                        }} placeholder="e.g. NY Pit" />
                        <Button
                            onClick={() => { this.configSublocationInsertOrUpdate(); }}
                            disabled={
                                (this.state.anyPageDataLoading > 0) ||
                                this.state.loadinglocation ||
                                (this.state.createsublocationName === "") ||
                                !/^[0-9A-Za-z _-]+$/.test(this.state.createsublocationName) ||
                                (this.state.createsublocationName === this.state.configName)
                            }
                        >
                            {insert ? "Create" : "Update"}
                        </Button>
                    </InputGroup>
                    {!this.state.createsublocationName.match("^[0-9a-zA-Z-_ ]+$") && this.state.createsublocationName !== "" &&  <div style={{paddingTop: '5px', color: 'red'}}>Only alphanumeric, spaces, - and _ characters allowed</div>}
                </Col>
            </FormGroup>);
        }

        if (configOption == "seq") {
            if (insert) {
                title = "Add RL";
            }
            else {
                title = "Rename RL";
            }
            formGroups.push(<FormGroup className="justify-content-md-left" row>
                <Col sm="12">
                    <br />
                    <InputGroup>
                        <Input onChange={this.setCreateSequenceName} defaultValue={this.state.configName} onKeyPress={(event) => {
                            if (event.charCode == 13) {
                                event.preventDefault();
                                if (!(this.state.createsequenceName == "") && this.state.createsequenceName.match("^[0-9a-zA-Z-_ ]+$") && this.state.createsequenceName !== this.state.configName){
                                    this.configSequenceInsertOrUpdate();
                                }
                            }
                        }} placeholder="e.g. Bench1" />
                        <Button
                            onClick={() => { this.configSequenceInsertOrUpdate(); }}
                            disabled={
                                (this.state.anyPageDataLoading > 0) ||
                                this.state.loadingsublocation ||
                                (this.state.createsequenceName === "") ||
                                !/^[0-9A-Za-z _-]+$/.test(this.state.createsequenceName) ||
                                (this.state.createsequenceName === this.state.configName)
                            }
                        >
                            {insert ? "Create" : "Update"}
                        </Button>
                    </InputGroup>
                    {!this.state.createsequenceName.match("^[0-9a-zA-Z-_ ]+$") && this.state.createsequenceName !== "" && <div style={{ paddingTop: '5px', color: 'red' }}>Only alphanumeric, spaces, - and _ characters allowed</div>}
                </Col>
            </FormGroup>);
        }

        if (configOption == "subseq" || configOption == "subseqflat") {
            if (insert) {
                title = "Add Shot";
            }
            else {
                title = "Rename Shot";
            }
            formGroups.push(<FormGroup className="justify-content-md-left" row>
                <Col sm="12">
                    <br />
                    <InputGroup>
                        <Input onChange={this.setCreateSubSequenceName} defaultValue={this.state.configName} onKeyPress={(event) => {
                            if (event.charCode == 13) {
                                event.preventDefault();
                                if (configOption === "subseqflat") {
                                    // quick-260810-jvd finding 1: Enter must agree with the button in flat
                                    // mode — same predicate, no unreachable loadingsequence gate.
                                    if (!isAddShotSubmitBlocked({
                                        configOption,
                                        anyPageDataLoading: this.state.anyPageDataLoading,
                                        loadingsequence: this.state.loadingsequence,
                                        createsubsequenceName: this.state.createsubsequenceName,
                                        configName: this.state.configName,
                                    })) {
                                        this.configSubsequenceInsertOrUpdate();
                                    }
                                } else if (isShotNameValid(this.state.createsubsequenceName, this.state.configName)) {
                                    // Classic Enter is a term-for-term restatement of the original inline
                                    // expression — no anyPageDataLoading/loadingsequence gates added here.
                                    this.configSubsequenceInsertOrUpdate();
                                }
                            }
                        }} placeholder="e.g. Shot1" />
                        <Button
                            onClick={() => { this.configSubsequenceInsertOrUpdate(); }}
                            disabled={isAddShotSubmitBlocked({
                                configOption,
                                anyPageDataLoading: this.state.anyPageDataLoading,
                                loadingsequence: this.state.loadingsequence,
                                createsubsequenceName: this.state.createsubsequenceName,
                                configName: this.state.configName,
                            })}
                        >
                            {insert ? "Create" : "Update"}
                        </Button>
                    </InputGroup>
                    {!this.state.createsubsequenceName.match("^[0-9a-zA-Z-_ ]+$") && this.state.createsubsequenceName !== "" && <div style={{ paddingTop: '5px', color: 'red' }}>Only alphanumeric, spaces, - and _ characters allowed</div>}
                </Col>
            </FormGroup>);
        }


        return <><ModalHeader toggle={this.toggleConfigHome}>{title}</ModalHeader><ModalBody><Form>{formGroups}</Form></ModalBody></>;
    }

    render() {
        var dispatchReviewRequiredMenuElement = null;
        if (this.state.showDispatchRRMenu) {
            dispatchReviewRequiredMenuElement = this.dispatchReviewRequiredMenu();
        }

        var dispatchLatestCapturesMenuElement = null;
        if (this.state.showDispatchLatestMenu) {
            dispatchLatestCapturesMenuElement = this.dispatchLatestCapturesMenu();
        }

        return (
            <div>
                {this.state.showDispatchRRMenu && dispatchReviewRequiredMenuElement}
                {this.state.showDispatchLatestMenu && dispatchLatestCapturesMenuElement}
                <div id="header" style={{zIndex: 2}}>
                    <Navbar className="navBG" dark expand="md" >
                        <NavbarBrand href="/">{(window._env_.REACT_APP_CONTENT_LOGO != null) ? <img src={window._env_.REACT_APP_CONTENT_LOGO} height="30px" /> : window._env_.REACT_APP_CONTENT_TITLE}</NavbarBrand>
                        <NavbarToggler onClick={this.toggleNavCollapse} />
                        <Collapse isOpen={this.state.navOpen} navbar>
                            <Nav className="ml-auto" navbar>
                                <NavItem className="ml-auto" style={{ paddingRight: "10px", paddingTop: "7px" }} onClick={this.dispatchLatestCapturesClick.bind(this)}>
                                    {this.state.dispatchesLatestCaptures?.length > 0 &&
                                        <><div style={{
                                            color: "#EC6519",
                                            fontWeight: "bold",
                                            cursor: "pointer"
                                        }} >
                                        Latest Captures: {this.state.dispatchesLatestCaptures[0].dispatchTimeString} - {this.state.dispatchesLatestCaptures[0].name}{this.state.dispatchesLatestCaptures[0].archived ? " (archived)" : ""}
                                        </div>
                                        
                                        </>
                                    }
                                </NavItem>
                                {(this.state.dispatchesLatestCaptures?.length > 0 && this.state.dispatchesReviewRequired?.length > 0) && <NavItem className="ml-auto" style={{ paddingRight: "10px", paddingTop: "4px", paddingBottom: "4px" }}>
                                    <div style={{ backgroundColor: "rgb(236 101 31 / 60%)", width: "2px", height: "100%" }}></div>
                                </NavItem>}
                                <NavItem className="ml-auto" style={{ paddingRight: "10px", paddingTop: "7px" }} onClick={this.dispatchReviewRequiredClick.bind(this)}>
                                {this.state.dispatchesReviewRequired?.length > 0 &&
                                        <div style={{
                                            color: "#EC6519",
                                            fontWeight: "bold",
                                            cursor: "pointer"
                                        }} >
                                        {this.state.dispatchesReviewRequired.length} {this.state.locationOptions?.AutoReviewOnUpload == 'true' ? "Dispatches in progress" : "Dispatches requiring review!"}
                                    </div>
                                }
                                </NavItem>
                                <NavItem className="ml-auto">
                                    {this.state.loadinglocation
                                        ? <select id="location1" name="location1" disabled={true}>
                                            <option value="0">Loading...</option>
                                        </select>
                                        : <select value={this.state.selectedlocationID} id="location2" name="location2" onChange={this.setSelectedLocation} disabled={this.state.anyPageDataLoading > 0}>
                                            {this.state.location.map(row =>
                                                <option value={row.id} key={row.id}>{row.name}</option>
                                            )}
                                        </select>
                                    }
                                </NavItem>
                                {/* No `ml-auto` here on purpose — the prior NavItem in the
                                    cluster already pushes the right-side group to the right
                                    edge. Adding ml-auto on this item would set
                                    `margin-left: auto !important` and override our inline
                                    marginLeft, killing the gap between the location <select>
                                    and the pill. Keep this NavItem on plain inline spacing. */}
                                <NavItem style={{ display: "flex", alignItems: "center", marginLeft: "24px", marginRight: "16px", padding: 0 }}>
                                    {/* Phase 24 v2.1 Stats Portal — discoverability entry point.
                                        Full-page nav (not react-router <Link>) because /stats is a
                                        code-split bundle and we want the browser to fetch it fresh;
                                        also matches the rest of this navbar's plain-href idiom.
                                        Pulse-orange pill (matches the Download Data CTA on /stats)
                                        for visual continuity on click-through. Sized to match the
                                        ~38px height of the location <select> sibling so the navbar's
                                        right cluster (dropdown · pill · gear · avatar) reads as one
                                        aligned row instead of mismatched heights stacked together. */}
                                    <NavLink
                                        href="/stats"
                                        title="Drilling throughput statistics for your sites"
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "7px",
                                            color: "white",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            letterSpacing: "0.01em",
                                            background: "#EC6519",
                                            padding: "8px 18px",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            textDecoration: "none",
                                            lineHeight: 1.2,
                                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
                                            transition: "background 140ms cubic-bezier(0.2, 0.7, 0.2, 1)"
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = "#D65A0F"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = "#EC6519"; }}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <line x1="6" y1="20" x2="6" y2="13" />
                                            <line x1="12" y1="20" x2="12" y2="8" />
                                            <line x1="18" y1="20" x2="18" y2="3" />
                                        </svg>
                                        Site Statistics
                                    </NavLink>
                                </NavItem>
                                <NavItem className="ml-auto" >
                                    <NavLink onClick={this.toggleSettingsHome}>
                                        <img src={settingsIcon} style={{ filter: "invert(99%) sepia(3%) saturate(60%) hue-rotate(99deg) brightness(115%) contrast(100%)" }} /> {/*Filter calc https://codepen.io/sosuke/pen/Pjoqqp */}
                                    </NavLink>
                                </NavItem>
                                <NavItem className="ml-auto">
                                    <Dropdown isOpen={this.state.dropdownOpen} toggle={this.toggleDropdown}>
                                        <DropdownToggle caret nav>
                                            <img src={userIcon} style={{ filter: "invert(99%) sepia(3%) saturate(60%) hue-rotate(99deg) brightness(115%) contrast(100%)" }} /> {/*Filter calc https://codepen.io/sosuke/pen/Pjoqqp */}
                                    </DropdownToggle>
                                        <DropdownMenu end style={{ zIndex: 100 }}>
                                            <DropdownItem header style={{ fontSize: 15 }}> {this.state.name} @ {this.state.organisationName} </DropdownItem>
                                            <DropdownItem divider />
                                        <DropdownItem style={{ fontSize: 15 }} onClick={() => this.props.auth.logout()}>Logout</DropdownItem>
                                    </DropdownMenu>
                                    </Dropdown> 
                                </NavItem>
                            </Nav>
                        </Collapse>
                    </Navbar>
                </div>

                {this.state.seen ? <PopUp toggle={this.togglePop} showDetailedWindow={this.state.showDetailedWindow} popupDetailedData={this.state.popupDetailedData} /> : null}

                <div>
                    <Modal isOpen={this.state.filterOpen} toggle={this.toggleFilterHome}>
                        <ModalHeader toggle={this.toggleFilterHome}>Filter Data</ModalHeader>
                        <ModalBody>
                            <Form >
                                <FormGroup className="justify-content-md-left" row>
                                    <Col sm="auto">
                                        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: "bold" }}>Location</Label>
                                        {this.state.loadinglocation
                                            ? <select id="location1" name="location1" disabled={true}>
                                                <option value="0">Loading...</option>
                                            </select>
                                            : <select value={this.state.selectedlocationID} id="location2" name="location2" onChange={this.setSelectedLocation} disabled={this.state.anyPageDataLoading > 0}>
                                                {this.state.location.map(row =>
                                                    <option value={row.id} key={row.id}>{row.name}</option>
                                                )}
                                            </select>
                                        }
                                    </Col>
                                    <Col sm="auto">
                                        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: "bold" }}>Hardware</Label>
                                        {this.state.loadinghardware
                                            ? <select id="hardware1" name="hardware1" disabled={true}>
                                                <option value="0">Loading...</option>
                                            </select>
                                            : <select value={this.state.selectedhardwareID} id="hardware2" name="hardware2" onChange={this.setSelectedHardware} disabled={this.state.anyPageDataLoading > 0}>
                                                {this.state.hardware.map(row =>
                                                    <option value={row.id} key={row.id}>{row.name}</option>
                                                )}
                                            </select>
                                        }
                                    </Col>
                                </FormGroup>

                            </Form>  
                        </ModalBody>
                    </Modal>
                </div>

                <div>
                    <Modal isOpen={this.state.configOpen} toggle={this.toggleConfigHome}>
                            {this.configForm(this.state.configOption)}
                    </Modal>
                </div>

                <div>
                    <Modal isOpen={this.state.refreshTolerancesConfirmOpen} toggle={() => this.setState({ refreshTolerancesConfirmOpen: false })}>
                        <ModalHeader toggle={() => this.setState({ refreshTolerancesConfirmOpen: false })}>Refresh Dispatch Tolerances</ModalHeader>
                        <ModalBody>
                            <p>Overwrites redrill/backfill/XY tolerances on ALL holes of ALL active plans in this location with the current site defaults (including any custom per-hole tolerances from manual uploads). Devices receive updated dispatches on next sync.</p>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="danger" onClick={() => this.confirmRefreshTolerances()}>Confirm</Button>
                            <Button onClick={() => this.setState({ refreshTolerancesConfirmOpen: false })}>Cancel</Button>
                        </ModalFooter>
                    </Modal>
                </div>

                <div>
                    <Modal isOpen={this.state.refreshTolerancesModalOpen} toggle={() => this.setState({ refreshTolerancesModalOpen: false })}>
                        <ModalHeader toggle={() => this.setState({ refreshTolerancesModalOpen: false })}>Refresh Dispatch Tolerances — Complete</ModalHeader>
                        <ModalBody>
                            {this.state.refreshTolerancesSummary && (
                                <div>
                                    <p><strong>Plans refreshed:</strong> {this.state.refreshTolerancesSummary.plansRefreshed}</p>
                                    <p><strong>Points updated:</strong> {this.state.refreshTolerancesSummary.pointsUpdated}</p>
                                    <p><strong>Dispatches created:</strong> {this.state.refreshTolerancesSummary.dispatchesCreated}</p>
                                    {this.state.refreshTolerancesSummary.skippedPlans && this.state.refreshTolerancesSummary.skippedPlans.length > 0 && (
                                        <div>
                                            <strong>Skipped plans:</strong>
                                            <ul>{this.state.refreshTolerancesSummary.skippedPlans.map((s, i) => <li key={i}>Plan {s.planId}: {s.reason}</li>)}</ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={() => this.setState({ refreshTolerancesModalOpen: false })}>Close</Button>
                        </ModalFooter>
                    </Modal>
                </div>

                <div>
                    <Modal isOpen={this.state.resetAssignmentModalOpen} toggle={this.toggleResetAssignmentModal}>
                        <ModalHeader toggle={this.toggleResetAssignmentModal}>Reset Assignments</ModalHeader>
                        <ModalBody>
                            This will remove all Manual Assignments/Unassignments for this capture session.
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={() => { this.postDispatchReviewResetEdits(this.state.selecteddispatchID, this.state.selectedlocationID); this.toggleResetAssignmentModal(); }}>OK</Button>{' '}
                            <Button onClick={this.toggleResetAssignmentModal}>Cancel</Button>
                        </ModalFooter>
                    </Modal>
                </div>

                <div>
                    <Modal isOpen={this.state.planOpen} toggle={this.togglePlanHome} size="xl" style={{ maxWidth: '1640px', width: '100%' }}>
                        <ModalHeader toggle={this.togglePlanHome}>{this.state.planModalOption === 'redrill' ? 'Add Redrills' : 'Plan Data'}</ModalHeader>
                        <ModalBody>
                            <Form >
                                {/*<FormGroup className="justify-content-md-left" row>*/}
                                {/*    <Col sm="auto">*/}
                                {/*        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: "bold" }}>Create Plan</Label>*/}
                                {/*        <InputGroup>*/}
                                {/*            <Input disabled={true} onChange={this.setCreatePlanVersion} placeholder="VerNo" value={this.state.createplanVersion} />                                                */}
                                {/*            <Input onChange={this.setCreatePlanData} placeholder="Name" />*/}
                                {/*            <br />*/}
                                {/*        </InputGroup>*/}
                                {/*        <InputGroup>*/}
                                {/*            <Label style={{ marginTop: 5, fontSize: 15 }}>Start Date</Label>*/}
                                {/*            <DatePicker*/}
                                {/*                className="datepicker"*/}
                                {/*                selected={this.state.createplanStartTime}*/}
                                {/*                onChange={this.setCreatePlanStartTime}*/}
                                {/*                showTimeSelect*/}
                                {/*                timeFormat="HH:mm"*/}
                                {/*                timeIntervals={15}*/}
                                {/*                timeCaption="time"*/}
                                {/*                dateFormat="MMMM d, yyyy h:mm aa"*/}
                                {/*                popperModifiers={{*/}
                                {/*                    preventOverflow: {*/}
                                {/*                        enabled: true,*/}
                                {/*                        escapeWithReference: false,*/}
                                {/*                        boundariesElement: "viewport"*/}
                                {/*                    }*/}
                                {/*                }}*/}
                                {/*                />*/}
                                {/*            </InputGroup>*/}
                                {/*            <InputGroup>*/}
                                {/*            <Label style={{ marginTop: 5, fontSize: 15 }}>End Date</Label>*/}
                                {/*            <DatePicker*/}
                                {/*                className="datepicker"*/}
                                {/*                selected={this.state.createplanEndTime}*/}
                                {/*                onChange={this.createplanEndTime}*/}
                                {/*                showTimeSelect*/}
                                {/*                timeFormat="HH:mm"*/}
                                {/*                timeIntervals={15}*/}
                                {/*                timeCaption="time"*/}
                                {/*                dateFormat="MMMM d, yyyy h:mm aa"*/}
                                {/*                popperModifiers={{*/}
                                {/*                    preventOverflow: {*/}
                                {/*                        enabled: true,*/}
                                {/*                        escapeWithReference: false,*/}
                                {/*                        boundariesElement: "viewport"*/}
                                {/*                    }*/}
                                {/*                }}*/}
                                {/*            />*/}
                                {/*            </InputGroup>*/}
                                {/*        <Button onClick={() => this.postplanInsert(this.state.selectedsubsequenceID, this.state.createplanVersion, this.state.createplanStartTime, this.state.createplanEndTime, this.state.createplanData)} disabled={((this.state.anyPageDataLoading > 0) || (this.state.loadingplan) || (this.state.createplanData["Name"] == ""))}>Create</Button>*/}
                                {/*    </Col>*/}
                                {/*</FormGroup>*/}
                                {/* Main Upload Section */}
                                <div style={{
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    padding: '20px',
                                    marginBottom: '20px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    {/* Row 1: File Type and File Selection */}
                                    <FormGroup row style={{ marginBottom: '15px', alignItems: 'center' }}>
                                        <Label sm={2} style={{ fontWeight: '600', marginBottom: 0 }}>File Type</Label>
                                        <Col sm={3}>
                                            <select
                                                className="form-control"
                                                value={this.state.uploadPlanType}
                                                onChange={this.setUploadPlanType}
                                                disabled={this.state.dataLoaded}
                                                style={{ cursor: this.state.dataLoaded ? 'not-allowed' : 'pointer' }}
                                            >
                                                <option value="pulsesimple">Pulse (Simple)</option>
                                                <option value="vulcan">Vulcan</option>
                                                <option value="blastlogic">Blastlogic</option>
                                                <option value="pulse">Pulse</option>
                                            </select>
                                        </Col>
                                        <Col sm={7}>
                                            <InputGroup>
                                                <Button
                                                    color="primary"
                                                    onClick={() => this.fileInput.current?.click()}
                                                    disabled={this.state.anyPageDataLoading > 0}
                                                    style={{ minWidth: '100px' }}
                                                >
                                                    Browse...
                                                </Button>
                                                <Input
                                                    readOnly
                                                    value={this.state.uploadedFileName || ''}
                                                    placeholder="No file selected"
                                                    invalid={this.state.isFormInvalid}
                                                    style={{ background: '#fff' }}
                                                />
                                                {this.state.planModalOption === 'redrill' ? (
                                                    !this.state.redrillMatchResult ? (
                                                        <Button
                                                            color="info"
                                                            onClick={() => { this.matchRedrillHoles(); }}
                                                            disabled={
                                                                this.state.anyPageDataLoading > 0 ||
                                                                this.state.redrillMatchLoading ||
                                                                this.state.validationErrors != 0 ||
                                                                !this.state.dataLoaded
                                                            }
                                                            style={{ minWidth: '120px' }}
                                                        >
                                                            {this.state.redrillMatchLoading ? <Spinner size="sm" /> : 'Match Redrills'}
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            color="success"
                                                            onClick={() => { this.uploadRedrillPlan(); this.togglePlanHome(); }}
                                                            disabled={
                                                                this.state.anyPageDataLoading > 0 ||
                                                                (this.state.redrillMatchResult.matched.length === 0 &&
                                                                 this.state.redrillMatchResult.unmatched.filter(h => this.state.redrillIncludeUnmatched[h.newHoleId]).length === 0 &&
                                                                 (!this.state.redrillMatchResult.removed || this.state.redrillMatchResult.removed.length === 0))
                                                            }
                                                            style={{ minWidth: '120px' }}
                                                        >
                                                            Upload Redrills
                                                        </Button>
                                                    )
                                                ) : (
                                                    <Button
                                                        color="success"
                                                        onClick={() => { this.uploadNewPlan(); this.togglePlanHome(); }}
                                                        disabled={
                                                            this.state.anyPageDataLoading > 0 ||
                                                            this.state.validationErrors != 0 ||
                                                            !this.state.dataLoaded
                                                        }
                                                        style={{ minWidth: '100px' }}
                                                    >
                                                        Upload
                                                    </Button>
                                                )}
                                                <Button
                                                    color="secondary"
                                                    outline
                                                    onClick={() => { this.uploadReset(); }}
                                                    disabled={!this.state.dataLoaded && !this.state.columnMappingRequired}
                                                    style={{ minWidth: '80px' }}
                                                >
                                                    Reset
                                                </Button>
                                            </InputGroup>
                                            <input
                                                type="file"
                                                ref={this.fileInput}
                                                className="d-none"
                                                accept=".csv"
                                                onChange={(e) => {
                                                    this.planFileHandler(e);
                                                    this.setState({ uploadedFileName: e.target.files?.[0]?.name || '' });
                                                }}
                                            />
                                            {this.state.isFormInvalid && (
                                                <FormFeedback className="d-block" style={{ fontStyle: 'italic', marginTop: '5px' }}>
                                                    Please select a .csv file only!
                                                </FormFeedback>
                                            )}
                                        </Col>
                                    </FormGroup>

                                    {/* Expected Columns Panel - shown when no file loaded */}
                                    {!this.state.dataLoaded && !this.state.columnMappingRequired && (() => {
                                        const formatDef = PLAN_FORMAT_HEADERS[this.state.uploadPlanType];
                                        if (!formatDef) return null;
                                        return (
                                            <div style={{
                                                borderTop: '1px solid #dee2e6',
                                                paddingTop: '15px',
                                                marginTop: '5px'
                                            }}>
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => this.setState(prev => ({ uploadShowExpectedColumns: !prev.uploadShowExpectedColumns }))}
                                                >
                                                    <span style={{
                                                        marginRight: '8px',
                                                        fontSize: '12px',
                                                        transition: 'transform 0.2s',
                                                        transform: this.state.uploadShowExpectedColumns ? 'rotate(90deg)' : 'rotate(0deg)'
                                                    }}>
                                                        &#9654;
                                                    </span>
                                                    <Label style={{
                                                        marginBottom: 0,
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        color: '#495057'
                                                    }}>
                                                        Expected Columns
                                                    </Label>
                                                    <span style={{
                                                        marginLeft: '10px',
                                                        fontSize: '12px',
                                                        color: '#6c757d'
                                                    }}>
                                                        ({formatDef.required.length} required, {formatDef.optional.length} optional)
                                                    </span>
                                                </div>

                                                {this.state.uploadShowExpectedColumns && (
                                                    <div style={{
                                                        marginTop: '12px',
                                                        padding: '15px',
                                                        background: '#fff',
                                                        borderRadius: '6px',
                                                        border: '1px solid #e9ecef'
                                                    }}>
                                                        <div style={{ marginBottom: '10px' }}>
                                                            <Button
                                                                color="outline-primary"
                                                                size="sm"
                                                                onClick={this.downloadTemplate}
                                                                style={{ marginRight: '8px' }}
                                                            >
                                                                Download Template
                                                            </Button>
                                                            <Button
                                                                color={this.state.headersCopied ? "success" : "outline-secondary"}
                                                                size="sm"
                                                                onClick={this.copyHeaders}
                                                            >
                                                                {this.state.headersCopied ? "Copied!" : "Copy Headers"}
                                                            </Button>
                                                        </div>
                                                        <div style={{ marginBottom: '8px' }}>
                                                            <Label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Required:</Label>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {formatDef.required.map(col => (
                                                                    <span key={col} style={{
                                                                        display: 'inline-block',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        fontSize: '12px',
                                                                        fontWeight: '600',
                                                                        background: '#e3f2fd',
                                                                        color: '#1565c0',
                                                                        border: '1px solid #90caf9'
                                                                    }}>
                                                                        {col}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        {formatDef.optional.length > 0 && (
                                                            <div>
                                                                <Label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Optional:</Label>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                    {formatDef.optional.map(col => (
                                                                        <span key={col} style={{
                                                                            display: 'inline-block',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '12px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 'normal',
                                                                            background: '#f5f5f5',
                                                                            color: '#616161',
                                                                            border: '1px solid #e0e0e0'
                                                                        }}>
                                                                            {col}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Column Mapping UI - shown when headers don't match */}
                                    {this.state.columnMappingRequired && (() => {
                                        const formatDef = PLAN_FORMAT_HEADERS[this.state.uploadPlanType];
                                        if (!formatDef) return null;
                                        const allExpected = [...formatDef.required, ...formatDef.optional];
                                        return (
                                            <div style={{
                                                borderTop: '1px solid #dee2e6',
                                                paddingTop: '15px',
                                                marginTop: '10px'
                                            }}>
                                                <div style={{
                                                    padding: '15px',
                                                    background: '#fff8e1',
                                                    borderRadius: '6px',
                                                    border: '1px solid #ffecb3',
                                                    marginBottom: '10px'
                                                }}>
                                                    <Label style={{ fontWeight: '600', color: '#e65100', marginBottom: '10px', display: 'block' }}>
                                                        Column headers don't match the expected format. Please map your columns:
                                                    </Label>
                                                    <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff8e1', fontWeight: 'bold', fontSize: 13, padding: '6px 10px', borderBottom: '2px solid #ffcc80', textAlign: 'left' }}>Expected Column</th>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff8e1', fontWeight: 'bold', fontSize: 13, padding: '6px 10px', borderBottom: '2px solid #ffcc80', textAlign: 'left' }}>Your Column</th>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff8e1', fontWeight: 'bold', fontSize: 13, padding: '6px 10px', borderBottom: '2px solid #ffcc80', textAlign: 'center', width: '120px' }}>Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {allExpected.map((expected, idx) => {
                                                                    const isRequired = formatDef.required.includes(expected);
                                                                    const mapped = this.state.columnMapping[expected] || "";
                                                                    const isUnmatched = isRequired && !mapped;
                                                                    const isAutoMatched = mapped && mapped !== expected;
                                                                    const isExactMatch = mapped === expected;
                                                                    return (
                                                                        <tr key={expected} style={{
                                                                            backgroundColor: isUnmatched ? '#ffebee' : (idx % 2 === 0 ? '#fff' : '#fafafa')
                                                                        }}>
                                                                            <td style={{
                                                                                fontSize: 13,
                                                                                padding: '4px 10px',
                                                                                borderBottom: '1px solid #eee',
                                                                                fontWeight: isRequired ? '600' : 'normal'
                                                                            }}>
                                                                                {expected}
                                                                                {isRequired && <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>}
                                                                            </td>
                                                                            <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee' }}>
                                                                                <select
                                                                                    className="form-control form-control-sm"
                                                                                    value={mapped}
                                                                                    onChange={(e) => {
                                                                                        const val = e.target.value;
                                                                                        this.setState(prev => ({
                                                                                            columnMapping: { ...prev.columnMapping, [expected]: val }
                                                                                        }));
                                                                                    }}
                                                                                >
                                                                                    <option value="">-- not mapped --</option>
                                                                                    {this.state.uploadedRawHeaders.map(h => (
                                                                                        <option key={h} value={h}>{h}</option>
                                                                                    ))}
                                                                                </select>
                                                                            </td>
                                                                            <td style={{ fontSize: 12, padding: '4px 10px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                                                                                {isExactMatch && <span style={{ color: '#2e7d32' }}>exact match</span>}
                                                                                {isAutoMatched && <span style={{ color: '#f57f17' }}>auto-matched</span>}
                                                                                {isUnmatched && <span style={{ color: '#d32f2f', fontWeight: '600' }}>unmatched</span>}
                                                                                {!isRequired && !mapped && <span style={{ color: '#9e9e9e' }}>optional</span>}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={this.applyColumnMapping}
                                                            disabled={(() => {
                                                                const fmtDef = PLAN_FORMAT_HEADERS[this.state.uploadPlanType];
                                                                return fmtDef && fmtDef.required.some(r => !this.state.columnMapping[r]);
                                                            })()}
                                                        >
                                                            Apply Mapping
                                                        </Button>
                                                        <Button
                                                            color="secondary"
                                                            outline
                                                            size="sm"
                                                            onClick={this.cancelColumnMapping}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Advanced Options Toggle */}
                                    <div
                                        style={{
                                            borderTop: '1px solid #dee2e6',
                                            paddingTop: '15px',
                                            marginTop: '5px'
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: this.state.dataLoaded ? 'not-allowed' : 'pointer',
                                                opacity: this.state.dataLoaded ? 0.6 : 1
                                            }}
                                            onClick={() => !this.state.dataLoaded && this.setState(prev => ({ uploadShowAdvancedOptions: !prev.uploadShowAdvancedOptions }))}
                                        >
                                            <span style={{
                                                marginRight: '8px',
                                                fontSize: '12px',
                                                transition: 'transform 0.2s',
                                                transform: this.state.uploadShowAdvancedOptions ? 'rotate(90deg)' : 'rotate(0deg)'
                                            }}>
                                                &#9654;
                                            </span>
                                            <Label style={{
                                                marginBottom: 0,
                                                fontWeight: '600',
                                                cursor: 'inherit',
                                                color: '#495057'
                                            }}>
                                                Advanced Options
                                            </Label>
                                            <span style={{
                                                marginLeft: '10px',
                                                fontSize: '12px',
                                                color: '#6c757d'
                                            }}>
                                                (Coordinate system, tolerances)
                                            </span>
                                        </div>

                                        {/* Advanced Options Content */}
                                        {this.state.uploadShowAdvancedOptions && (
                                            <div style={{
                                                marginTop: '15px',
                                                padding: '15px',
                                                background: '#fff',
                                                borderRadius: '6px',
                                                border: '1px solid #e9ecef'
                                            }}>
                                                {/* Coordinates - hidden unless advanced options enabled */}
                                                {this.state.optionsShowAdvanced && (
                                                <FormGroup row style={{ marginBottom: '12px', alignItems: 'center' }}>
                                                    <Label sm={3} style={{ marginBottom: 0 }}>Coordinate System</Label>
                                                    <Col sm={4}>
                                                        <select
                                                            className="form-control"
                                                            value={this.state.uploadPlanCS}
                                                            onChange={this.setUploadPlanCS}
                                                            disabled={this.state.dataLoaded}
                                                        >
                                                            <option value="wgs84">WGS84 (EPSG:4326)</option>
                                                            <option value="agd66-55">AGD66 / AMG zone 55 (EPSG:20255-15786)</option>
                                                            <option value="agd84-55">AGD84 / AMG zone 55 (EPSG:20355-15785)</option>
                                                            <option value="bs1-84">BS1-84 / AMG zone 50 (Local)</option>
                                                            <option value="wamg">WAMG / AMG zone 50 (Local)</option>
                                                            <option value="prmg">PRMG / AMG zone 50 (Local)</option>
                                                            <option value="trmg">TRMG / AMG zone 51 (Local)</option>
                                                        </select>
                                                    </Col>
                                                </FormGroup>
                                                )}

                                                {/* Tolerances - available for all upload types */}
                                                {(
                                                    <>
                                                        <div style={{
                                                            borderTop: '1px solid #e9ecef',
                                                            margin: '15px 0',
                                                            paddingTop: '15px'
                                                        }}>
                                                            <Label style={{
                                                                fontSize: '13px',
                                                                color: '#6c757d',
                                                                marginBottom: '12px',
                                                                display: 'block'
                                                            }}>
                                                                Tolerance Settings (override site defaults)
                                                            </Label>
                                                            <FormGroup row style={{ marginBottom: '10px', alignItems: 'center' }}>
                                                                <Label sm={3} style={{ marginBottom: 0 }}>XY Tolerance (m)</Label>
                                                                <Col sm={2}>
                                                                    <select
                                                                        className="form-control"
                                                                        value={this.state.uploadPlanXYTolerance}
                                                                        onChange={this.setUploadPlanXYTolerance}
                                                                        disabled={this.state.dataLoaded}
                                                                    >
                                                                        {this.state.uploadPlanToleranceOptions.map((val) => <option key={val} value={val}>{val}</option>)}
                                                                    </select>
                                                                </Col>
                                                            </FormGroup>
                                                            <FormGroup row style={{ marginBottom: '10px', alignItems: 'center' }}>
                                                                <Label sm={3} style={{ marginBottom: 0 }}>Redrill Tolerance (m)</Label>
                                                                <Col sm={2}>
                                                                    <select
                                                                        className="form-control"
                                                                        value={this.state.uploadPlanRLToleranceMinus}
                                                                        onChange={this.setUploadPlanRLToleranceMinus}
                                                                        disabled={this.state.dataLoaded}
                                                                    >
                                                                        {this.state.uploadPlanToleranceOptions.map((val) => <option key={val} value={val}>{val}</option>)}
                                                                    </select>
                                                                </Col>
                                                            </FormGroup>
                                                            <FormGroup row style={{ marginBottom: 0, alignItems: 'center' }}>
                                                                <Label sm={3} style={{ marginBottom: 0 }}>Backfill Tolerance (m)</Label>
                                                                <Col sm={2}>
                                                                    <select
                                                                        className="form-control"
                                                                        value={this.state.uploadPlanRLTolerancePlus}
                                                                        onChange={this.setUploadPlanRLTolerancePlus}
                                                                        disabled={this.state.dataLoaded}
                                                                    >
                                                                        {this.state.uploadPlanToleranceOptions.map((val) => <option key={val} value={val}>{val}</option>)}
                                                                    </select>
                                                                </Col>
                                                            </FormGroup>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    {Array.isArray(this.state.validationErrors) && this.state.validationErrors.length > 0 &&
                                        <div>
                                            <ul tag="h6">
                                            {this.state.validationErrors.map(errorText => <li style={{ "fontWeight": "bold", "color": "red" }}>{errorText}</li>)}
                                        </ul>
                                        </div>
                                    }
                                    {Array.isArray(this.state.updatePlanInfo) && this.state.updatePlanInfo.length > 0 &&
                                        <div>
                                            <ul tag="h6">
                                            {this.state.updatePlanInfo.map(infoText => <li style={infoText.includes("WARNING") ? { "color": "red", "fontWeight": "bold" } : { "fontWeight": "bold"}}>{infoText}</li>)}
                                            </ul>
                                        </div>
                                    }
                                    {/* Redrill Match Results - above map view */}
                                    {this.state.planModalOption === 'redrill' && this.state.redrillMatchResult && (
                                        <div style={{ marginBottom: '15px' }}>
                                            {/* Matched Holes Table (new redrills) */}
                                            <div style={{ marginBottom: '10px' }}>
                                                <Label style={{ fontWeight: '600' }}>New Redrills: {this.state.redrillMatchResult.matched.length}</Label>
                                                {this.state.redrillMatchResult.matched.length > 0 && (
                                                    <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #ddd' }}>
                                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', borderRight: '1px solid #eee', textAlign: 'left' }}>New Hole ID</th>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', borderRight: '1px solid #eee', textAlign: 'left' }}>Replaces</th>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', textAlign: 'right' }}>Distance (m)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {this.state.redrillMatchResult.matched.map((match, idx) => (
                                                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee', borderRight: '1px solid #eee' }}>{match.newHoleId}</td>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee', borderRight: '1px solid #eee' }}>{match.existingHoleId}</td>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee', textAlign: 'right' }}>{match.distance}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Already Redrill Table (skipped) */}
                                            {this.state.redrillMatchResult.alreadyRedrill && this.state.redrillMatchResult.alreadyRedrill.length > 0 && (
                                                <div style={{ marginBottom: '10px' }}>
                                                    <Label style={{ fontWeight: '600', color: '#888' }}>Already Pending Redrills (no changes): {this.state.redrillMatchResult.alreadyRedrill.length}</Label>
                                                    <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ddd', opacity: 0.6 }}>
                                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', borderRight: '1px solid #eee', textAlign: 'left' }}>Hole ID</th>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f0f0f0', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', textAlign: 'left' }}>Existing Hole</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {this.state.redrillMatchResult.alreadyRedrill.map((hole, idx) => (
                                                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f9f9f9' : '#f4f4f4' }}>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee', borderRight: '1px solid #eee', color: '#888' }}>{hole.newHoleId}</td>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee', color: '#888' }}>{hole.existingHoleId}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Removed Redrills Table */}
                                            {this.state.redrillMatchResult.removed && this.state.redrillMatchResult.removed.length > 0 && (
                                                <div style={{ marginBottom: '10px' }}>
                                                    <Label style={{ fontWeight: '600', color: '#c9302c' }}>Removed Redrills: {this.state.redrillMatchResult.removed.length}</Label>
                                                    <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ddd' }}>
                                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', textAlign: 'left' }}>Hole ID</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {this.state.redrillMatchResult.removed.map((hole, idx) => (
                                                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff5f5' : '#fff0f0' }}>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee', color: '#c9302c' }}>{hole.holeId}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Unmatched Holes Table */}
                                            {this.state.redrillMatchResult.unmatched.length > 0 && (
                                                <div>
                                                    <Label style={{ fontWeight: '600' }}>Unmatched Holes: {this.state.redrillMatchResult.unmatched.length}</Label>
                                                    <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #ddd' }}>
                                                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', borderRight: '1px solid #eee', textAlign: 'center', width: '50px' }}>Include</th>
                                                                    <th style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#f5f5f5', fontWeight: 'bold', fontSize: 13, padding: '5px 10px', borderBottom: '2px solid #ccc', textAlign: 'left' }}>Hole ID</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {this.state.redrillMatchResult.unmatched.map((unmatch, idx) => (
                                                                    <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee', borderRight: '1px solid #eee', textAlign: 'center' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={!!this.state.redrillIncludeUnmatched[unmatch.newHoleId]}
                                                                                onChange={(e) => {
                                                                                    this.setState(prev => ({
                                                                                        redrillIncludeUnmatched: {
                                                                                            ...prev.redrillIncludeUnmatched,
                                                                                            [unmatch.newHoleId]: e.target.checked
                                                                                        }
                                                                                    }));
                                                                                }}
                                                                            />
                                                                        </td>
                                                                        <td style={{ fontSize: 13, padding: '4px 10px', borderBottom: '1px solid #eee' }}>{unmatch.newHoleId}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {this.state.dataLoaded && (
                                        <div className="plan-layout">
                                            <div className="map-pane">
                                                <Card body outline color="secondary" className="restrict-card h-100">
                                                    <div className="leaflet-wrap">
                                                        <GPSMapContainer
                                                            showAdvanced={this.state.optionsShowAdvanced}
                                                            styleMap={this.state.GPSmapStyle}
                                                            selectionModeEnabled={false}
                                                            PDFref={this.nonPDFleafletref}
                                                            tilelayerenabled={true}
                                                            capturedataLoaded={false}
                                                            plandataLoaded={true}
                                                            missingdataLoaded={false}
                                                            diffdataLoaded={false}
                                                            plandata={this.manipulateUploadPlanDataForMapView(
                                                                this.state.rows, this.state.cols, this.state.extra
                                                            )}
                                                        />
                                                    </div>
                                                </Card>
                                            </div>

                                            <div className="table-pane" style={{ overflowX: 'auto' }}>
                                                {(() => {
                                                    const tableData = this.manipulateUploadPlanDataForTableView(
                                                        this.state.cols,
                                                        this.state.rows
                                                    );
                                                    return (
                                                        <div style={{ maxHeight: 350, overflow: 'auto', border: '1px solid #ddd' }}>
                                                            <table style={{ borderCollapse: 'collapse', minWidth: 900, width: '100%' }}>
                                                                <thead>
                                                                    <tr>
                                                                        {tableData.cols.map((col, ci) => (
                                                                            <th
                                                                                key={col.key || ci}
                                                                                style={{
                                                                                    position: 'sticky',
                                                                                    top: 0,
                                                                                    zIndex: 2,
                                                                                    backgroundColor: '#f5f5f5',
                                                                                    fontWeight: 'bold',
                                                                                    fontSize: 14,
                                                                                    padding: '6px 10px',
                                                                                    whiteSpace: 'nowrap',
                                                                                    borderBottom: '2px solid #ccc',
                                                                                    borderRight: '1px solid #eee',
                                                                                    textAlign: 'center',
                                                                                    minWidth: col.minWidth || 120,
                                                                                }}
                                                                            >
                                                                                {col.name}
                                                                            </th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {tableData.rows.map((row, ri) => (
                                                                        <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                                            {tableData.cols.map((col, ci) => (
                                                                                <td
                                                                                    key={col.key || ci}
                                                                                    style={{
                                                                                        fontSize: 14,
                                                                                        padding: '4px 10px',
                                                                                        whiteSpace: 'nowrap',
                                                                                        borderBottom: '1px solid #eee',
                                                                                        borderRight: '1px solid #eee',
                                                                                        textAlign: 'center',
                                                                                    }}
                                                                                >
                                                                                    {row[col.key] ?? ''}
                                                                                </td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                        </div>
                                    )}
                                </div>
                            </Form>
                        </ModalBody>
                    </Modal>
                </div>

                <div>
                    <Modal isOpen={this.state.dispatchOpen} toggle={this.toggleDispatchHome} size="xl" style={{ maxWidth: '1640px', width: '100%' }}>
                        <ModalHeader toggle={this.toggleDispatchHome}>Dispatch Data</ModalHeader>
                        <ModalBody>
                            <Form >
                                <FormGroup row>
                                    <Col sm="auto">
                                        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: "bold" }}>Create Dispatch</Label>
                                        <Input onChange={this.setCreateDispatchName} placeholder="Name" />
                                    </Col>
                                    <Col sm="auto">
                                        <Button onClick={this.toggleDispatchPlanDetails.bind(this)} disabled={((this.state.createdispatchName == "") || (this.state.loadingplan))}>Select Plan Details</Button>                                      
                                    </Col>
                                </FormGroup>

                                {this.state.dispatchPlanDetailsShow &&
                                    <div>
                                    <Card body outline color="secondary" className="restrict-card">
                                        <Label check style={{ marginTop: 5, fontSize: 15, fontWeight: "bold" }}>
                                            <Input type="checkbox" checked={this.state.dispatchCheckboxIncludeRefSelected} onChange={this.toggleDispatchCheckboxIncludeRef.bind(this)} />{' '}
                                            Include all other holes in selected plan for reference
                                        </Label>

                                        <Button onClick={() => this.postDispatch(this.state.createdispatchName, this.state.selectedhardwareID, this.state.selectedlocationID, this.state.createdispatchTime, { 'plandetails': this.createDispatchPlanDetailsForPost() })} disabled={(this.state.anyPageDataLoading > 0) || (!this.state.dispatchPlanDetailsSelected)}>Create</Button>

                                        {this.state.loadingplan
                                            ? <select id="plan1" name="plan1" disabled={true}>
                                                <option value="0">Loading...</option>
                                            </select>
                                            : <select value={this.state.selectedDispatchPlanID} id="plan2" name="plan2" multiple onChange={this.setSelectedDispatchPlan} disabled={this.state.anyPageDataLoading > 0} style={{ height: 130 }}>
                                                {this.state.plan.map(row =>
                                                    <option value={row.id} key={row.id}>{row.version ? "V" + row.version : "---Select---"}</option>
                                                )}
                                            </select>
                                        }

                                        {(this.state.dispatchPlanDetailsAll.length == 0)
                                            ? <select id="plan1" name="plan1" disabled={true}>
                                                <option value="0">Loading...</option>
                                            </select>
                                            : <select value={this.state.selectedDispatchPlanDetailsID} id="plandetails2" name="plandetails2" multiple onChange={this.setSelectedDispatchPlanDetails} disabled={this.state.anyPageDataLoading > 0} style={{ height: 500 }}>
                                                {this.state.dispatchPlanDetailsAll.map(row =>
                                                    <option value={row.id} key={row.id}>{row.planId + "-" + row.data["Hole ID#"]}</option>
                                                )}
                                            </select>
                                        }

                                        {this.state.dispatchPlanDetailsSelected &&
                                            <GPSMapContainer showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeEnabled={false} PDFref={this.nonPDFleafletref} tilelayerenabled={true} capturedataLoaded={false} plandataLoaded={true} missingdataLoaded={false} diffdataLoaded={false} plandata={this.manipulateDispatchDetailsDataForMapView(this.state.dispatchPlanDetails)} />
                                        }
                                        </Card>
                                    </div>}

                                <FormGroup row>

                                </FormGroup>
                                <FormGroup className="justify-content-md-left" row>
                                    <Col sm="auto">
                                        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: "bold" }}>Dispatch To Device</Label>
                                        {this.state.loadingdispatch
                                            ? <select id="dispatch1" name="dispatch1" disabled={true}>
                                                <option value="0">Loading...</option>
                                            </select>
                                            : <select value={this.state.selecteddispatchID} id="dispatch2" name="dispatch2" onChange={this.setSelectedDispatch} disabled={this.state.anyPageDataLoading > 0}>
                                                {this.state.dispatch.map(row =>
                                                    <option value={row.id} key={row.id}>{row.name}</option>
                                                )}
                                            </select>
                                        }
                                        {this.state.loadinghardware
                                            ? <select id="hardware1" name="hardware1" disabled={true}>
                                                <option value="0">Loading...</option>
                                            </select>
                                            : <select value={this.state.selectedhardwareID} id="hardware2" name="hardware2" onChange={this.setSelectedHardware} disabled={true}>
                                                {this.state.hardware.map(row =>
                                                    <option value={row.id} key={row.id}>{row.name}</option>
                                                )}
                                            </select>
                                        }

                                        <Button onClick={() => this.postDispatchToDevice(this.state.selecteddispatchID, this.state.selectedlocationID)} disabled={((this.state.anyPageDataLoading > 0) || (this.state.loadingdispatch) || (this.state.selecteddispatchID == 0))}>Dispatch To Device</Button>
                                        {this.state.dispatchToDeviceLoading &&
                                            <Spinner tag="span" type="border" size="sm" aria-hidden="true" children={null} />}
                                    </Col>
                                </FormGroup>
   
                                {/*<FormGroup className="justify-content-md-left" row>*/}
                                {/*    <Col sm="auto">*/}
                                {/*        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: "bold" }}>Upload all from Device</Label>*/}
                                {/*        <FormGroup className="justify-content-md-left" row>*/}
                                {/*            <Col sm="auto">*/}
                                {/*                <Button onClick={() => this.postReuploadAll(this.state.selectedhardwareID)} disabled={this.state.anyPageDataLoading > 0}>Reupload All</Button>*/}
                                {/*            </Col>*/}
                                {/*        </FormGroup>*/}
                                {/*    </Col>*/}
                                {/*</FormGroup>*/}

                            </Form>
                        </ModalBody>
                    </Modal>
                </div>

                <div>
                    <Modal isOpen={this.state.settingsOpen} toggle={this.toggleSettingsHome} size="xl" style={{ maxWidth: '840px', width: '100%' }}>
                        <ModalHeader toggle={this.toggleSettingsHome}>User Settings</ModalHeader>
                        <ModalBody>
                            <Form >
                                <FormGroup row className="justify-content-md-left">
                                    <Col sm="auto">
                                        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: 'bold'  }}>Tree Options:</Label>
                                    </Col>
                                    <Col sm="auto">
                                        <FormGroup check inline className="mb-2 d-flex align-items-center gap-2">
                                            <Input
                                                id="opt-tree-captures"
                                                type="checkbox"
                                                checked={this.state.optionsTreeShowDispatchWCaptures}
                                                onChange={() =>
                                                    this.setState(prev => ({
                                                        optionsTreeShowDispatchWCaptures: !prev.optionsTreeShowDispatchWCaptures
                                                    }))
                                                }
                                            />
                                            <Label htmlFor="opt-tree-captures" check className="mb-0" style={{ fontSize: 15 }}>
                                                Only show Dispatches with Capture Data
                                            </Label>
                                        </FormGroup>

                                        {/* Show Archived */}
                                        <FormGroup check inline className="mb-2 d-flex align-items-center gap-2">
                                            <Input
                                                id="opt-tree-archived"
                                                type="checkbox"
                                                checked={this.state.optionsTreeShowArchived}
                                                onChange={() =>
                                                    this.setState(prev => ({
                                                        optionsTreeShowArchived: !prev.optionsTreeShowArchived
                                                    }))
                                                }
                                            />
                                            <Label htmlFor="opt-tree-archived" check className="mb-0" style={{ fontSize: 15 }}>
                                                Show Archived
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </FormGroup>
                                <FormGroup row className="justify-content-md-left">
                                    <Col sm="auto">
                                        <Label style={{ marginTop: 5, fontSize: 15, fontWeight: 'bold'  }}>Export Options:</Label>
                                    </Col>
                                    <Col sm="auto">
                                        {/* Dry tolerance */}
                                        <FormGroup className="d-flex align-items-center gap-2 mb-2" style={{ marginTop: '5px' }}>
                                            <Input
                                                id="opt-water-zero"
                                                type="number"
                                                step="0.1"
                                                style={{ width: '100px', marginRight: '5px' }}
                                                value={this.state.optionsWaterHeightZero}
                                                onChange={(e) => {
                                                    const newZero = Number(e.target.value);
                                                    this.setState(prev => ({
                                                        optionsWaterHeightZero: newZero,
                                                        // If advanced is on, keep Wet >= Zero; if not, tie Wet to Zero.
                                                        optionsWaterHeightWet: prev.optionsShowAdvanced
                                                            ? Math.max(newZero, prev.optionsWaterHeightWet)
                                                            : newZero
                                                    }));
                                                }}
                                            />
                                            <Label htmlFor="opt-water-zero" className="mb-0" style={{ fontSize: 15 }}>
                                                Dry Tolerance (water heights below this will be set to 0)
                                            </Label>
                                        </FormGroup>

                                        {this.state.optionsShowAdvanced && (
                                            <>
                                                {/* 'Wet' water height */}
                                                <FormGroup className="d-flex align-items-center gap-2 mb-2" style={{ marginTop: '5px' }}>
                                                    <Input
                                                        id="opt-water-wet"
                                                        type="number"
                                                        step="0.1"
                                                        style={{ width: '100px', marginRight: '5px' }}
                                                        value={this.state.optionsWaterHeightWet}
                                                        onChange={(e) => {
                                                            const newWet = Number(e.target.value);
                                                            this.setState(prev => ({
                                                                // Enforce Wet >= Zero
                                                                optionsWaterHeightWet: Math.max(newWet, prev.optionsWaterHeightZero)
                                                            }));
                                                        }}
                                                    />
                                                    <Label htmlFor="opt-water-wet" className="mb-0" style={{ fontSize: 15 }}>
                                                        'Wet' Water Height (holes with water height greater than this marked Wet)
                                                    </Label>
                                                </FormGroup>

                                                {/* Reflectivity Wet* */}
                                                <FormGroup className="d-flex align-items-center gap-2 mb-2" style={{ marginTop: '5px' }}>
                                                    <Input
                                                        id="opt-refl-wet"
                                                        type="number"
                                                        step="1"
                                                        style={{ width: '100px', marginRight: '5px' }}
                                                        value={this.state.optionsReflectivityWet}
                                                        onChange={(e) => {
                                                            const v = parseInt(e.target.value, 10);
                                                            this.setState({ optionsReflectivityWet: Number.isNaN(v) ? 0 : v });
                                                        }}
                                                    />
                                                    <Label htmlFor="opt-refl-wet" className="mb-0" style={{ fontSize: 15 }}>
                                                        'Wet*' Reflectivity (where no water height available, reflectivity greater than this marked as Wet*)
                                                    </Label>
                                                </FormGroup>
                                            </>
                                        )}
                                    </Col>
                                </FormGroup>
                                {this.state.organisationName == '%' &&
                                    <FormGroup row className="justify-content-md-left">
                                        <Col sm="auto">
                                            <Label style={{ marginTop: 5, fontSize: 15, fontWeight: 'bold' }}>Advanced Options:</Label>
                                        </Col>
                                        <Col sm="auto">
                                            <FormGroup check inline className="mb-2 d-flex align-items-center gap-2">
                                                <Input
                                                    id="opt-advanced"
                                                    type="checkbox"
                                                    checked={this.state.optionsShowAdvanced}
                                                    onChange={() =>
                                                        this.setState(prev => ({ optionsShowAdvanced: !prev.optionsShowAdvanced }), () => {
                                                            if (this.state.selectedlocationID && this.state.selectedlocationID !== "0") this.getTreeData(this.state.selectedlocationID);
                                                        })
                                                    }
                                                />
                                                <Label htmlFor="opt-advanced" check className="mb-0" style={{ fontSize: 15 }}>
                                                    Show Advanced Options
                                                </Label>
                                            </FormGroup>
                                        </Col>
                                    </FormGroup>
                                }
                            </Form>
                        </ModalBody>
                    </Modal>
                    <Modal isOpen={this.state.showExportDataModal} toggle={this.toggleExportDataModal} style={{ maxWidth: '560px', width: '100%' }}>
                        <ModalHeader close={
                            <button type="button" onClick={this.toggleExportDataModal}
                                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: '22px', lineHeight: 0, paddingBottom: '2px' }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
                                &#x2715;
                            </button>
                        } style={{ backgroundColor: '#123241', color: '#fff', borderBottom: 'none', padding: '16px 24px' }}>
                            <div>
                                <span style={{ fontSize: 14, fontWeight: 400, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Export</span>
                                {this.state.report?.Name && <div style={{ fontSize: 20, fontWeight: 600, marginTop: '2px' }}>{this.state.report.Name.replace(/-V\d+$/i, "")}</div>}
                            </div>
                        </ModalHeader>
                        <ModalBody style={{ padding: '24px' }}>
                            <Form>
                                {/* Coordinates (advanced only) */}
                                {this.state.optionsShowAdvanced && (
                                <div style={{ marginBottom: '20px' }}>
                                    <Label style={{ fontSize: 13, fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Coordinates</Label>
                                    <select value={this.state.exportDataCS} onChange={this.setExportDataCS} disabled={this.state.anyPageDataLoading > 0} style={{ fontSize: 15 }}>
                                        <option value="wgs84">WGS84 (EPSG:4326)</option>
                                        <option value="agd66-55">AGD66 / AMG zone 55</option>
                                        <option value="agd84-55">AGD84 / AMG zone 55</option>
                                        <option value="bs1-84">BS1-84 / AMG zone 50</option>
                                        <option value="wamg">WAMG / AMG zone 50</option>
                                        <option value="prmg">PRMG / AMG zone 50</option>
                                        <option value="trmg">TRMG / AMG zone 51</option>
                                    </select>
                                </div>
                                )}

                                {/* Options Section */}
                                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '18px 20px', marginBottom: '20px' }}>

                                    {/* Since */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <Label style={{ fontSize: 14, fontWeight: 500, marginBottom: '8px' }}>Since</Label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {[{ key: "all", label: "All Time" }, { key: "24h", label: "Last 24h" }, { key: "7d", label: "Last 7 Days" }, { key: "custom", label: "Custom" }].map(opt =>
                                                <Button key={opt.key} outline={this.state.exportFilterTimeWindow !== opt.key}
                                                    color={this.state.exportFilterTimeWindow === opt.key ? "primary" : "secondary"}
                                                    style={{ fontSize: 14, padding: '6px 16px', borderRadius: '16px' }}
                                                    onClick={() => this.setState({ exportFilterTimeWindow: opt.key })}>
                                                    {opt.label}
                                                </Button>
                                            )}
                                        </div>
                                        {this.state.exportFilterTimeWindow === "custom" &&
                                            <div style={{ marginTop: '10px' }}>
                                                <Input type="datetime-local"
                                                    style={{ fontSize: 14, maxWidth: '280px', padding: '8px 10px' }}
                                                    value={this.state.exportFilterCustomDate}
                                                    onChange={(e) => this.setState({ exportFilterCustomDate: e.target.value })} />
                                            </div>
                                        }
                                    </div>

                                    {/* Include Only */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <Label style={{ fontSize: 14, fontWeight: 500, marginBottom: '8px' }}>Include Only</Label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            {[{ key: "redrill", label: "Redrill" }, { key: "atdesign", label: "At Design" }, { key: "backfill", label: "Backfill" }, { key: "wet", label: "Wet" }].map(opt => {
                                                const active = opt.key === "wet"
                                                    ? this.state.exportFilterWetOnly
                                                    : this.state.exportFilterCompliance.includes(opt.key);
                                                return <Button key={opt.key} outline={!active}
                                                    color={active ? "primary" : "secondary"}
                                                    style={{ fontSize: 14, padding: '6px 16px', borderRadius: '16px' }}
                                                    onClick={() => {
                                                        if (opt.key === "wet") {
                                                            this.setState(prev => ({ exportFilterWetOnly: !prev.exportFilterWetOnly }));
                                                        } else {
                                                            this.setState(prev => {
                                                                const arr = prev.exportFilterCompliance.includes(opt.key)
                                                                    ? prev.exportFilterCompliance.filter(v => v !== opt.key)
                                                                    : [...prev.exportFilterCompliance, opt.key];
                                                                return { exportFilterCompliance: arr };
                                                            });
                                                        }
                                                    }}>
                                                    {opt.label}
                                                </Button>;
                                            })}
                                        </div>
                                        <small style={{ color: '#adb5bd', fontSize: 12, marginTop: '6px', display: 'block' }}>None selected = all measurements</small>
                                    </div>

                                    {/* Include unmeasured */}
                                    <FormGroup check className="mb-0" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                        <Input id="export-include-design" type="checkbox" style={{ width: '18px', height: '18px' }}
                                            checked={this.state.exportFilterIncludeDesign}
                                            onChange={() => this.setState(prev => ({ exportFilterIncludeDesign: !prev.exportFilterIncludeDesign }))} />
                                        <Label htmlFor="export-include-design" check className="mb-0" style={{ fontSize: 14, cursor: 'pointer' }}>Include unmeasured holes</Label>
                                    </FormGroup>
                                </div>

                                {/* Export Button */}
                                <Button color="primary" block onClick={this.exportData} disabled={this.state.anyPageDataLoading > 0}
                                    style={{ fontSize: 16, fontWeight: 600, padding: '12px', borderRadius: '8px' }}>
                                    {this.state.exportingData ? "Exporting..." : "Export CSV"}
                                </Button>
                            </Form>
                            {this.state.downloadCSV ? <CSVDownload data={this.state.exportCSVData} filename={this.state.exportDataFilename} target="_blank" /> : ""}
                        </ModalBody>
                    </Modal>
                </div>

                <div id="content">
                    <Container style={{ height: '100%' }}>
                        <Row style={{ height: '100%' }}>
                            <Col xs="3" style={{ backgroundColor: '#fafafa', borderRight: '2px #f5f5f5 solid', overflow: 'hidden', msFlex:'0 0 420px', flex: '0 0 420px', height: '100%' }}>
                                <TreeView treeData={this.state.treeData} setSelectedSubLocation={this.setSelectedSubLocation} setSelectedSequence={this.setSelectedSequence} setSelectedSubSequence={this.setSelectedSubSequence}
                                    setSelectedPlan={this.setSelectedPlan} setSelectedDispatch={this.setSelectedDispatch}
                                    toggleConfig={this.toggleConfig.bind(this)}
                                    togglePlan={this.togglePlan.bind(this)}
                                    sublocation={this.state.sublocation} selectedsublocationID={this.state.selectedsublocationID}
                                    sequence={this.state.sequence} selectedsequenceID={this.state.selectedsequenceID}
                                    subsequence={this.state.subsequence} selectedsubsequenceID={this.state.selectedsubsequenceID}
                                    refreshData={(() => {
                                        var old = this.state.selectedDispatchTreeId;
                                        this.setState({ selectedDispatchTreeId: null, forceReload: true });
                                        this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: old }));
                                        this.getDispatchReviewRequired(this.state.selectedlocationID);
                                        this.getDispatchLatestCaptures(this.state.selectedlocationID);
                                    }).bind(this)}
                                    selectedId={this.state.selectedDispatchTreeId}
                                    setSelectedId={this.setSelectedTreeId}
                                    deleteItem={this.deleteItem.bind(this)}
                                    archiveItem={this.archiveItem.bind(this)}
                                    unarchiveItem={this.unarchiveItem.bind(this)}
                                    refreshTolerances={this.refreshTolerances.bind(this)}
                                    showAdvanced={this.state.optionsShowAdvanced}
                                    updateSort={this.updateSortTreeData.bind(this)}
                                    locationOptions={this.state.locationOptions}
                                />
                            </Col>
                            <Col style={{ overflow: 'auto', height: '100%' }}>
                                {(this.state.selectedlocationID != 0 && this.state.loading) && (((this.state.selecteddispatchID != 0 && !this.state.loadedreview) || (this.state.selectedplanID != 0 && !this.state.loadedreport)) ?
                                    <div>
                                        <div style={{ height: "88vh", paddingTop: "12px", paddingBottom: "12px", textAlign: "center" }} >
                                            <Spinner tag="span" type="border" size="sm" aria-hidden="true" children={null} /> Loading...
                                        </div>
                                    </div>
                                    : (!this.state.loadedreview && !this.state.loadedreport) &&
                                    <div>
                                        <div style={{ height: "88vh", paddingTop: "12px", paddingBottom: "12px", textAlign: "center" }} >
                                            Please Select a Plan or Capture Session
                                        </div>
                                    </div>)
                                }

                                {(!this.state.loading || this.state.loadedreview || this.state.loadedreport) &&
                                    <>
                                    {this.state.loadedreview && this.state.review["data"]["ReviewDetails"].find((reviewDetails) => (reviewDetails["Captures_Raw"].length > 0)) !== undefined &&
                                        this.state.locationOptions?.AutoReviewOnUpload == 'true' && this.state.review["data"].Reviewed !== 1
                                    && <span style={{ width: "100%", color: "#ffffff", fontWeight: "bold", backgroundColor: "#EC6519", display: "block", textAlign: "center"}}>IN PROGRESS</span>}
                                    <Nav tabs style={{ paddingTop: "1px", color: "#777777" }}>
                                        {this.state.loadedreview &&
                                            <>
                                                {this.state.review["data"]["ReviewDetails"].map(function (item, i) {
                                                    return <NavItem>
                                                        <NavLink className={classnames({ active: this.state.activeReviewLifecycle === i })}
                                                            style={(this.state.locationOptions?.AutoReviewOnUpload != 'true' || this.state.optionsShowAdvanced) && this.state.review["data"]["ReviewDetails"][i].ReviewRequired ? { color: '#EC6519' } : {}}
                                                            onClick={() => {
                                                                var updatedReview = this.state.review;
                                                                updatedReview["data"]["ReviewDetails"][i].ReviewRequired = false; //set false once selected
                                                                var selReviewDetails = updatedReview["data"]["ReviewDetails"][i];
                                                        
                                                                this.setState({
                                                                    activeReviewLifecycle: i, activeReviewLifecycleId: item.Id,
                                                                    activeLifecycleTypeId: item.LifecycleTypeId, selectedReviewDetails: selReviewDetails,
                                                                    activeTab: '3', selectedCaptureData: selReviewDetails["Captures_Raw"], review: updatedReview,
                                                                    PDFgenerated: false, PDFloading: false, loadedreview: false
                                                                }, () => {this.setState({ loadedreview: true });} ); //reset loaded to re-init the map review due to issue with circle Refs not assigning correctly (hole assignment clicks went to wrong holes) when swapping between lifecycles
                                                                this.setMapStyle(10, 1); //na
                                                                
                                                            }}
                                                        >
                                                            {item.LifecycleName}
                                                            {(this.state.locationOptions?.AutoReviewOnUpload != 'true' || this.state.optionsShowAdvanced) && this.state.review["data"]["ReviewDetails"][i].ReviewRequired && <img src={exclamationIcon} style={{ height: "20px", width: "30px", marginBottom: "5px"}} />}
                                                        </NavLink>
                                                    </NavItem>
                                                    },this)
                                                }
                                            {(this.state.locationOptions?.AutoReviewOnUpload != 'true' || this.state.optionsShowAdvanced) &&
                                                <span style={{ marginLeft: 'auto', marginRight: 0 }}> {(this.state.review["data"].Reviewed !== 1 && (this.state.review["data"]["ReviewDetails"].find((reviewDetails) => reviewDetails.ReviewRequired) !== undefined)) &&
                                            <span style={{ color: "#EC6519", paddingRight: "5px", verticalAlign: "middle" }}>Review all Lifecycles before Approving</span>}
                                                {(this.state.locationOptions?.AutoReviewOnUpload != 'true' || this.state.optionsShowAdvanced) &&  <Button color="primary" style={{ marginLeft: 'auto', marginRight: 0, marginTop: '2px' }} onClick={() => this.postDispatchReviewApprove(this.state.dbReview, this.state.selecteddispatchID)}
                                                    disabled={((this.state.anyPageDataLoading > 0) || (this.state.reviewSelectionMode) || (this.state.review["data"].Reviewed == 1))}>
                                                    {(this.state.review["data"].Reviewed == 1) ? "Approved" : "Approve Review"}</Button>}
                                            </span>}
                                            </>
                                        }
                                        {this.state.loadedreport &&
                                            <>
                                                {this.state.report["ReportDetails"].map(function (item, i) {
                                                    return <NavItem>
                                                        <NavLink className={classnames({ active: this.state.activeReportLifecycle === i })}
                                                            onClick={() => {
                                                                var selReportDetails = this.state.report["ReportDetails"][i];

                                                                this.setState({ activeReportLifecycle: i, selectedReportDetails: selReportDetails, activeTab: '4', activeLifecycleTypeId: selReportDetails.LifecycleTypeId, PDFgenerated: false, PDFloading: false});
                                                                this.setMapStyle(10, 1); //na
                                                            }}
                                                        >
                                                            {this.state.reflifecycletype.find((lifecycle) => lifecycle.id == item.LifecycleTypeId)?.name}
                                                        </NavLink>
                                                    </NavItem>
                                                    }, this)
                                                }
                                            {/*<Button style={{ margin: 'auto 0px auto auto' }} disabled={this.state.anyPageDataLoading > 0} onClick={this.imagesZippedGenerate.bind(this)}>
                                                {this.state.loadingplanimages &&
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />}
                                                Download Images
                                            </Button>*/}
                                            </>
                                        }
                                    </Nav>
                                    
                                    <Nav tabs style={{ paddingTop: "1px", fontSize: "14px", backgroundColor: "#123241", color: "#fff" }} className={"nav-invert"}>
                                        {this.state.loadedreview &&
                                            <>
                                            <NavItem className={"nav-invert"}>
                                                <div className={classnames({ active: this.state.activeTab === '1' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '1' })}
                                                        onClick={() => { this.toggleTab('1', 0); }} //na
                                                    >
                                                        Table
                                                    </NavLink>
                                                </div>
                                            </NavItem>
                                            <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '2' })}>
                                                <NavLink
                                                    className={classnames({ active: this.state.activeTab === '2' })}
                                                    onClick={() => { this.toggleTab('2', 0); }} //na
                                                >
                                                    Review Summary
                                                    </NavLink>
                                                </div>
                                            </NavItem>
                                            <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '3' })}>
                                                <NavLink
                                                    className={classnames({ active: this.state.activeTab === '3' })}
                                                    onClick={() => { this.toggleTab('3', 10); }} //drill/column
                                                >
                                                    Review Map
                                                    </NavLink>
                                                </div>
                                            </NavItem>
                                            {(this.state.selectedReviewDetails.ReviewPlans.some(rp => rp.IsExplicitRedrill)
                                                || this.state.selectedReviewDetails.Summary.DiffvsPlan.filter((row) =>
                                                    row["Diff_" + this.state.reportHeaderCustomField[5] + "_Severity"] < 3).length > 0
                                            ) && <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '10' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '10' })}
                                                        onClick={() => { this.toggleTab('10', 20); }} //redrill
                                                    >
                                                        Proposed {this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 20 && type.lifecycleType == this.state.activeLifecycleTypeId).name}
                                                    </NavLink>
                                                </div>
                                            </NavItem>}
                                            {this.state.selectedReviewDetails.Summary.DiffvsPlan.filter((row) => row["Diff_" + this.state.reportHeaderCustomField[6] + "_Severity"] > 3).length > 0 && <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '11' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '11' })}
                                                        onClick={() => { this.toggleTab('11', 30); }} //backfill
                                                    >
                                                        Proposed {this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 30 && type.lifecycleType == this.state.activeLifecycleTypeId).name}
                                                    </NavLink>
                                                </div>
                                            </NavItem>}
                                            </>
                                        }
                                        {this.state.loadedreport && 
                                            <>
                                            <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '4' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '4' })}
                                                        onClick={() => { this.toggleTab('4', 10); }} //drill/column
                                                    >
                                                        Measured
                                                    </NavLink>
                                                    </div>
                                                </NavItem>
                                            <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '7' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '7' })}
                                                        onClick={() => { this.toggleTab('7', 10); }} //drill/column
                                                    >
                                                        Unmeasured
                                                    </NavLink>
                                                </div>
                                            </NavItem>
                                            <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '5' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '5' })}
                                                        onClick={() => { this.toggleTab('5', 20); }} //redrill/below
                                                    >
                                                        {this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 20 && type.lifecycleType == this.state.activeLifecycleTypeId).name}
                                                    </NavLink>
                                                    </div>
                                                </NavItem>
                                            <NavItem>
                                                <div className={classnames({ active: this.state.activeTab === '6' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '6' })}
                                                        onClick={() => { this.toggleTab('6', 30); }} //backfill/above
                                                    >
                                                        {this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 30 && type.lifecycleType == this.state.activeLifecycleTypeId).name}
                                                    </NavLink>
                                                </div>
                                                </NavItem>

                                                {this.state.optionsShowAdvanced && <NavItem>
                                                        <div className={classnames({ active: this.state.activeTab === '8' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '8' })}
                                                        onClick={() => { this.toggleTab('8', 10); }} //drill/column
                                                    >
                                                        Charts
                                                    </NavLink>
                                                </div>
                                                </NavItem>}
                                                {this.state.optionsShowAdvanced && <NavItem>
                                                            <div className={classnames({ active: this.state.activeTab === '9' })}>
                                                    <NavLink
                                                        className={classnames({ active: this.state.activeTab === '9' })}
                                                        onClick={() => { this.toggleTab('9', 0); }} //na
                                                    >
                                                        Data
                                                    </NavLink>
                                                </div>
                                                </NavItem>}
                                            </>

                                        }
                                        {this.state.loadedreport && <>
                                            {<span style={{ marginLeft: 'auto', marginRight: '4px', display: 'block', marginTop: 'auto', marginBottom: 'auto' }}>
                                                <Button style={{ marginLeft: '4px', marginRight: '4px' }} disabled={this.state.anyPageDataLoading > 0} onClick={this.toggleExportDataModal.bind(this)}>
                                                    {this.state.ExtraDataLoading &&
                                                        <Spinner tag="span" type="border" size="sm" aria-hidden="true" children={null} />}
                                                    Export Data
                                                </Button>
                                            </span>
                                            }</>
                                        }

                                    </Nav>
                                    {this.state.loadedreport && <div style={{ textAlign: 'center', paddingLeft: '8px', backgroundColor: '#123241' }}>
                                    {((this.state.selectedReportDetails.Summary.TotalCountPlan - this.state.selectedReportDetails.Summary.TotalCountDiffvsPlan) > 0) ? 
                                        <span style={{ fontWeight: 'bold', color:'#ec6519'}}>
                                        {this.state.selectedReportDetails.Summary.TotalCountPlan - this.state.selectedReportDetails.Summary.TotalCountDiffvsPlan} Holes Requiring Measurement
                                        ({this.state.selectedReportDetails.Summary.TotalCountDiffvsPlan} / {this.state.selectedReportDetails.Summary.TotalCountPlan}) 
                                        </span> 
                                        : 
                                        <span style={{ fontWeight: 'bold', color: 'white' }}>All Holes Measured ({this.state.selectedReportDetails.Summary.TotalCountDiffvsPlan} / {this.state.selectedReportDetails.Summary.TotalCountPlan})</span>
                                    }
                                    </div>}
                                    </>
                                }

                                

                                {this.state.loadedreview &&
                                    <div className="tableFix" style={{"overflowY": "auto" }}>
                                        {!this.state.loading && this.state.activeTab == '1' &&
                                        <Table showAdvanced={this.state.optionsShowAdvanced} showGPSMap={!this.state.seen} PDFref={this.PDFleafletref} data={this.state.selectedCaptureData} location={this.state.location} selectedlocationID={this.state.selectedlocationID} hardware={this.state.hardware} selectedhardwareID={this.state.selectedhardwareID} endDate={this.state.endDate} getDetailed={this.getDataCaptureOneDetailed.bind(this)} postUploadFull={this.postUploadFull.bind(this)} getDataCaptureOneDetailed={this.getDataCaptureOneDetailed.bind(this)} loading={this.state.loading} />
                                        }
                                        {this.state.activeTab == '2' &&
                                            <table aria-labelledby="tabelLabel">
                                                <thead>
                                                    <tr>
                                                        <th>Review Info</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                            {this.state.selectedReviewDetails.ReviewLog.map(row =>
                                                        <tr key={row}>
                                                            <td>{row}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        }
                                        {this.state.activeTab == '3' &&
                                            <>
                                            
                                        <GPSMapContainer allowRemeasurement={this.state?.locationOptions?.AllowRemeasurement == "true"} showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeUpdate={this.selectionModeUpdate.bind(this)} selectionModeEnabled={this.state.reviewSelectionMode} selectionModeSelections={this.state.reviewSelections} unassignModeEnabled={this.state.reviewUnassignMode} unassignSelections={this.state.unassignSelections} PDFref={this.nonPDFleafletref} tilelayerenabled={true} capturedataLoaded={true} plandataLoaded={true} missingdataLoaded={true} diffdataLoaded={true} capturedata={this.state.selectedReviewDetails.Captures_Raw} plandata={this.state.selectedReviewDetails.ReviewPlans} missingdata={this.state.selectedReviewDetails.Summary.Missing} diffdata={this.state.selectedReviewDetails.Summary.DiffvsPlan} />
                                        {((this.state.anyPageDataLoading <= 0) && (this.state.review["data"].Reviewed == 1) && (this.state.review["data"]["ReviewDetails"].find((reviewDetails) => (reviewDetails["Captures_Raw"].length > 0)) !== undefined)) && <div>
                                            <Button className={"statebutton"} style={{ margin: "0px 5px 0px 0px" }} color={(this.state.reviewSelectionMode && !this.state.reviewUnassignMode) ? 'primary' : 'secondary'} onClick={() => this.setState({ reviewSelectionMode: !this.state.reviewSelectionMode, reviewSelections: [] })} disabled={((this.state.anyPageDataLoading > 0) || (this.state.reviewSelectionMode))}><div> Assign Holes  </div></Button>
                                            <Button className={"statebutton"} style={{ margin: "5px" }} color={this.state.reviewUnassignMode ? 'primary' : 'secondary'} onClick={() => this.setState({ reviewUnassignMode: !this.state.reviewUnassignMode, reviewSelectionMode: !this.state.reviewUnassignMode, reviewSelections: [] })} disabled={((this.state.anyPageDataLoading > 0) || (this.state.reviewSelectionMode))}><div> Unassign Holes  </div></Button>
                                            <Button style={{ margin: "5px" }} onClick={() => this.setState({ reviewSelectionMode: false, reviewSelections: [], reviewUnassignMode: false })} disabled={((this.state.anyPageDataLoading > 0 || !this.state.reviewSelectionMode))}><div>Cancel</div></Button>
                                            <Button style={{ margin: "5px" }} color={this.state.reviewSelectionMode ? 'primary' : 'secondary'} onClick={() => this.postDispatchReviewEdits(this.state.selecteddispatchID, this.state.selectedlocationID, this.state.reviewSelections)} disabled={((this.state.anyPageDataLoading > 0) || (!this.state.reviewSelectionMode) || (this.state.reviewSelections == []))}>Submit</Button>
                                            <Button style={{ margin: "5px 0px 5px 0px", position: "absolute", right: "15px" }} onClick={this.toggleResetAssignmentModal} disabled={((this.state.anyPageDataLoading > 0) || this.state.reviewSelectionMode)}>Reset Assignments</Button>
                                        </div>}
                                        </>
                                    }
                                    {this.state.activeTab == '10' && //redrill
                                        <>

                                        <GPSMapContainer showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeEnabled={false} PDFref={this.nonPDFleafletref} tilelayerenabled={false} capturedataLoaded={false} plandataLoaded={true} missingdataLoaded={true} diffdataLoaded={false} plandata={this.state.selectedReviewDetails.ReviewPlans} missingdata={
                                            this.state.selectedReviewDetails.ReviewPlans.some(rp => rp.IsExplicitRedrill)
                                                ? this.state.selectedReviewDetails.ReviewPlans
                                                    .filter(rp => rp.IsExplicitRedrill)
                                                    .map(rp => ({
                                                        Latitude: rp.Latitude, Longitude: rp.Longitude,
                                                        Name: rp.AssocPlanDetailsHoleId, PlanDetailsId: rp.AssocPlanDetailsId
                                                    }))
                                                : this.state.selectedReviewDetails.Summary.DiffvsPlan.filter((row) =>
                                                    row["Diff_" + this.state.reportHeaderCustomField[5] + "_Severity"] < 3)
                                        } />
                                        </>
                                    }
                                    {this.state.activeTab == '11' && //backfill
                                        <>

                                        <GPSMapContainer showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeEnabled={false} PDFref={this.nonPDFleafletref} tilelayerenabled={false} capturedataLoaded={false} plandataLoaded={true} missingdataLoaded={true} diffdataLoaded={false} plandata={this.state.selectedReviewDetails.ReviewPlans} missingdata={this.state.selectedReviewDetails.Summary.DiffvsPlan.filter((row) => row["Diff_" + this.state.reportHeaderCustomField[6] + "_Severity"] > 3)} />
                                        </>
                                    }
                                    </div>
                                }
                                {this.state.loadedreport &&
                                    <div>
                                        {(this.state.activeTab == '4' || (this.state.PDFloading && !this.state.PDFgenerated)) &&
                                        <GPSMapContainer showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeEnabled={false} PDFref={this.PDFleafletref[0]} tilelayerenabled={false} capturedataLoaded={false} plandataLoaded={true} missingdataLoaded={false} diffdataLoaded={true} plandata={this.state.selectedReportDetails.ReviewPlans} diffdata={this.state.selectedReportDetails.Summary.DiffvsPlan} />
                                        }
                                        {(this.state.activeTab == '5' || (this.state.PDFloading && !this.state.PDFgenerated)) &&
                                        <GPSMapContainer showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeEnabled={false} PDFref={this.PDFleafletref[1]} tilelayerenabled={false} capturedataLoaded={false} plandataLoaded={true} missingdataLoaded={true} diffdataLoaded={false} plandata={this.state.selectedReportDetails.ReviewPlans} missingdata={this.state.selectedReportDetails.Chart.RedrillMapData} />
                                        }
                                        {(this.state.activeTab == '6' || (this.state.PDFloading && !this.state.PDFgenerated)) &&
                                        <GPSMapContainer showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeEnabled={false} PDFref={this.PDFleafletref[2]} tilelayerenabled={false} capturedataLoaded={false} plandataLoaded={true} missingdataLoaded={true} diffdataLoaded={false} plandata={this.state.selectedReportDetails.ReviewPlans} missingdata={this.state.selectedReportDetails.Chart.BackfillMapData} />
                                        }
                                        {(this.state.activeTab == '7' || (this.state.PDFloading && !this.state.PDFgenerated)) &&
                                        <GPSMapContainer showAdvanced={this.state.optionsShowAdvanced} styleMap={this.state.GPSmapStyle} selectionModeEnabled={false} PDFref={this.PDFleafletref[3]} tilelayerenabled={false} capturedataLoaded={false} plandataLoaded={true} missingdataLoaded={true} diffdataLoaded={false} plandata={this.state.selectedReportDetails.ReviewPlans} missingdata={this.state.selectedReportDetails.Summary.Missing} />
                                        }
                                        {(this.state.activeTab == '8' || (this.state.PDFloading && !this.state.PDFgenerated)) &&
                                            <>
                                                <Plot
                                                    divId={this.state.PDFplotDivId[0]}
                                                    data={[
                                                        {
                                                            x: this.state.selectedReportDetails["Chart"]["RadarData"]["x"],
                                                            y: this.state.selectedReportDetails["Chart"]["RadarData"]["y"],
                                                            mode: 'markers',
                                                            name: 'points',
                                                            marker: {
                                                                color: 'rgb(0,68,27)',
                                                                size: 4,
                                                                opacity: 0.6
                                                            },
                                                            type: 'scatter'
                                                        },
                                                        {
                                                            x: this.state.selectedReportDetails["Chart"]["RadarData"]["x"],
                                                            y: this.state.selectedReportDetails["Chart"]["RadarData"]["y"],
                                                            name: 'density',
                                                            ncontours: 20,
                                                            colorscale: 'Greens',
                                                            reversescale: true,
                                                            showscale: false,
                                                            type: 'histogram2dcontour'
                                                        },
                                                        {
                                                            x: this.state.selectedReportDetails["Chart"]["RadarData"]["x"],
                                                            name: 'x density',
                                                            marker: { color: 'rgb(11,118,52)' },
                                                            yaxis: 'y2',
                                                            type: 'histogram',
                                                        },
                                                        {
                                                            y: this.state.selectedReportDetails["Chart"]["RadarData"]["y"],
                                                            name: 'y density',
                                                            marker: { color: 'rgb(11,118,52)' },
                                                            xaxis: 'x2',
                                                            type: 'histogram',
                                                        }
                                                    ]}
                                                    layout={{
                                                        showlegend: false,
                                                        autosize: false,
                                                        width: 720,
                                                        height: 720,
                                                        margin: { t: 50 },
                                                        hovermode: 'closest',
                                                        bargap: 0,
                                                        xaxis: {
                                                            domain: [0, 0.85],
                                                            showgrid: false,
                                                            zeroline: false
                                                        },
                                                        yaxis: {
                                                            domain: [0, 0.85],
                                                            showgrid: false,
                                                            zeroline: false
                                                        },
                                                        xaxis2: {
                                                            domain: [0.85, 1],
                                                            showgrid: false,
                                                            zeroline: false
                                                        },
                                                        yaxis2: {
                                                            domain: [0.85, 1],
                                                            showgrid: false,
                                                            zeroline: false
                                                        }
                                                    }}
                                                />

                                                <Plot
                                                    divId={this.state.PDFplotDivId[1]}
                                                    data={[
                                                        {
                                                            x: ["Above Tolerance", "Within Tolerance", "Below Tolerance"],
                                                            y: this.state.selectedReportDetails["Chart"]["BarDepthData"]["x"],
                                                            marker: { color: ['#03d7fc', 'green', '#ad03fc'] },
                                                            type: 'bar',
                                                        },
                                                    ]}
                                                    layout={{
                                                        width: 1080,
                                                        height: 720,
                                                    }}
                                                />

                                                <Plot
                                                    divId={this.state.PDFplotDivId[2]}
                                                    data={[
                                                        {
                                                            x: ["Above Tolerance", "Within Tolerance", "Below Tolerance"],
                                                            y: this.state.selectedReportDetails["Chart"]["BarToelRLData"]["x"],
                                                            marker: { color: ['#03d7fc', 'green', '#ad03fc'] },
                                                            type: 'bar',
                                                        },
                                                    ]}
                                                    layout={{
                                                        width: 1080,
                                                        height: 720,
                                                    }}
                                                />
                                            </>
                                        }
                                    {(this.state.activeTab == '9' || (this.state.PDFloading && !this.state.PDFgenerated) || (this.state.exportingData && !this.state.exportedData)) &&
                                        <>
                                            <div ref={this.PDFtableref} className="tableFix">
                                                Missing Hole Data
                                                <table aria-labelledby="tabelLabel">
                                                    <thead>
                                                        <tr>
                                                            <th>Hole #</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.state.selectedReportDetails.Summary.Missing.map(row =>
                                                            <tr>
                                                                <td style={{ backgroundColor: "#ff0000" }}>{row.Name}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                                Hole Diff Data
                                                <table aria-labelledby="tabelLabel">
                                                    <thead>
                                                        <tr>
                                                            <th>Hole #</th>
                                                            <th>Length Acc. (m)</th>
                                                            <th>Toe Acc. (m)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.state.selectedReportDetails.Summary.DiffvsPlan.map(row =>
                                                            <tr>
                                                                <td>{row.Name}</td>
                                                                {row["Diff_" + this.state.reportHeaderCustomField[5] + "_Severity"] == 1 ?
                                                                    <td style={{ backgroundColor: "#ff0000" }}>{row["Diff_" + this.state.reportHeaderCustomField[5]]}</td>
                                                                    : <td style={{ backgroundColor: "#ffffff" }}>{row["Diff_" + this.state.reportHeaderCustomField[5]]}</td>
                                                                }
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div ref={this.Extratableref_DiffvsPlan}>
                                                Extra Data - DiffvsPlan
                                                <table aria-labelledby="tabelLabel">
                                                    <thead>
                                                        <tr>
                                                            <th>Hole ID</th>
                                                            <th>Plan CollarX (Easting)</th>
                                                            <th>Plan CollarY (Northing)</th>
                                                            <th>Plan CollarZ (m)</th>
                                                            <th>XY Tolerance (m)</th>
                                                            <th>{this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 20 && type.lifecycleType == this.state.activeLifecycleTypeId).name} Tolerance (m)</th>
                                                            <th>{this.state.refcampaigntype.find(type => type.priorityOrder % 100 == 30 && type.lifecycleType == this.state.activeLifecycleTypeId).name} Tolerance (m)</th>
                                                            <th>Plan HoleLength (m)</th>
                                                            <th>HoleLength (m)</th>
                                                            <th>Difference (m)</th>
                                                            <th>Compliance</th>
                                                            <th>Potential Wetness</th>
                                                            <th>Estimated Water Height</th>
                                                            <th>PrevAuto HoleLength (m)</th>
                                                            <th>CollarX (Easting)</th>
                                                            <th>CollarY (Northing)</th>
                                                            <th>CollarZ (m)</th>
                                                            <th>ToeX (Easting)</th>
                                                            <th>ToeY (Northing)</th>
                                                            <th>ToeZ (m)</th>
                                                            <th>DateTime</th>
                                                            <th>Handset ID</th>
                                                            <th>Reflectivity (%)</th>
                                                            <th>Capture Type</th>
                                                            <th>Comment</th>
                                                            <th>Blast Name</th>
                                                            <th>PlanDetails ID</th>
                                                            <th>Capture ID</th>
                                                            <th>Calculation Type</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                    {this.getExportTableData()}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div ref={this.Extratableref_Additional}>
                                                Extra Data - Additional
                                                <table aria-labelledby="tabelLabel">
                                                    <thead>
                                                        <tr>
                                                            <th>Hole ID</th>
                                                            <th>CollarX (Easting)</th>
                                                            <th>CollarY (Northing)</th>
                                                            <th>CollarZ (m)</th>
                                                            <th>ToeX (Easting)</th>
                                                            <th>ToeY (Northing)</th>
                                                            <th>ToeZ (m)</th>
                                                            <th>HoleLength (m)</th>
                                                            <th>PrevAuto HoleLength (m)</th>
                                                            <th>DateTime</th>
                                                            <th>Handset ID</th>
                                                            <th>Reflectivity (%)</th>
                                                            <th>Wetness</th>
                                                            <th>Capture Type</th>
                                                            <th>Comment</th>
                                                            <th>Capture ID</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.state.selectedReportDetails.Summary.Additional.map(row =>
                                                            <tr>
                                                                <td>{row.Name}</td>
                                                                <td>{row.CollarEast_Capture.toFixed(2)}</td>
                                                                <td>{row.CollarNorth_Capture.toFixed(2)}</td>
                                                                <td>{row.CollarDepth_Capture.toFixed(2)}</td>
                                                                <td>{row.ToeRLEast_Capture.toFixed(2)}</td>
                                                                <td>{row.ToeRLNorth_Capture.toFixed(2)}</td>
                                                                <td>{row.ToeRL_Capture.toFixed(2)}</td>
                                                                <td>{row.HoleLength_Capture.toFixed(2)}</td>
                                                                <td>{row.PreviousAuto_HoleDepth.toFixed(2)}</td>
                                                                <td>{row.DateTime_Capture}</td>
                                                                <td>{JSON.parse(row.Data_Capture).handsetID}</td>
                                                                <td>{row.reflecthole_Capture}</td>
                                                                <td>{row.wetness_Capture}</td>
                                                                <td>{row.capturetype_Capture}</td>
                                                                <td>{row.commentString_Capture}</td>
                                                                <td>{row.CaptureId}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div ref={this.Extratableref_Missing}>
                                                Extra Data - Missing
                                                <table aria-labelledby="tabelLabel">
                                                    <thead>
                                                        <tr>
                                                            <th>Hole ID</th>
                                                            <th>PlanDetails ID</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.state.selectedReportDetails.Summary.Missing.map(row =>
                                                            <tr>
                                                                <td>{row.Name}</td>
                                                                <td>{row.PlanDetailsId}</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    }
                                    </div>
                                }
                            </Col>
                        </Row>
                    </Container>
                </div>

                <Alert
                    color="warning"
                    isOpen={!!this.state.anyPageError}    
                    toggle={this.closeErrorAlert}
                    transition={{ unmountOnExit: true }}  
                    style={{ position: 'absolute', top: 120, right: 0, zIndex: 1 }} 
                >
                    Page Error - {this.state.anyPageErrorDetails}
                </Alert>


                {(this.state.anyPageDataLoading > 0) &&
                    <div>
                        <Toast className="p-1 navBG my-3 position-absolute" style={{ fontSize: 15, bottom: 50, right: 0 }}>
                            <ToastBody className="d-flex align-items-center gap-2">
                                <Spinner tag="span" type="border" size="sm" aria-hidden="true" children={null} />
                                <span className="text-white">
                                    Data Loading ({this.state.anyPageDataLoading}) - ({this.state.anyPageDataLoadingDetails})
                                </span>
                            </ToastBody>
                        </Toast>
                    </div>
                }
                
                <div id="footer" >
                    <Navbar className="navBG" dark style={{ paddingTop: "12px", paddingBottom: "12px"}}>
                        <div className="mx-auto"   >
                            <font color="#FFFFF">Copyright &copy; 2026 {window._env_.REACT_APP_CONTENT_COMPANY} - {window._env_.REACT_APP_VERSION}</font>
                        </div>
                    </Navbar>
                </div>

            </div>

        );
    }

    manipulateCaptureDataForFrontEnd(rawcaptures,reviewcaptures) {
        for (let i in rawcaptures) {
            if (reviewcaptures.findIndex((row) => row.CaptureId == rawcaptures[i].Id && row.AssocPlan == true) > -1){
                rawcaptures[i].Assigned = true;
            } else {
                rawcaptures[i].Assigned = false;
            }
        }
        var tempdata = this.manipulateCaptureDataForFrontEndInternal(rawcaptures);

        tempdata = tempdata.filter(capture => {
            try {
                var data = JSON.parse(capture.Data);
                return data.depthhole !== -1 && !(data.deleted > 0);
            } catch (e) { return false; }
        });
        
        return tempdata;
    }

    manipulateCaptureDataForFrontEndInternal(data) {
        for (let i in data) {
            //******************fix for uppercase on first letter sometimes*********************** TEMP
            if (data[i]["dateTime"] == null) {
                data[i]["dateTime"] = data[i]["DateTime"];
            }
            if (data[i]["data"] == null) {
                data[i]["data"] = data[i]["Data"];
            }
            if (data[i]["detailedData"] == null) {
                data[i]["detailedData"] = data[i]["DetailedData"];
            }
            if (data[i]["detailedDataAvailable"] == null) {
                data[i]["detailedDataAvailable"] = data[i]["DetailedDataAvailable"];
            }
            if (data[i]["dispatchId"] == null) {
                data[i]["dispatchId"] = data[i]["DispatchId"];
            }
            if (data[i]["hardwareId"] == null) {
                data[i]["hardwareId"] = data[i]["HardwareId"];
            }
            if (data[i]["id"] == null) {
                data[i]["id"] = data[i]["Id"];
            }

            //manage for -ve numbers (react-leaflet cant handle)
            data[i]["Latitude"] = JSON.parse(data[i]["data"]).rovergpslat;
            if (data[i]["Latitude"] == -1) {
                data[i]["Latitude"] = 0;
            }
            data[i]["Longitude"] = JSON.parse(data[i]["data"]).rovergpslong;
            if (data[i]["Longitude"] == -1) {
                data[i]["Longitude"] = 0;
            }

            //generate dates to local
            var offset = 0;
            if (this.state?.locationOptions?.UTCOffset != null) {
                offset = this.state.locationOptions.UTCOffset;
            }
            var date = new Date(new Date(data[i]["DateTime"]).getTime() + (offset * 60 * 60 * 1000));
            var datestring = ('0' + date.getDate()).slice(-2) + "/" + ('0' + (date.getMonth()+1)).slice(-2) + "/" + date.getFullYear(); //Month is 0-11
            var timestring = ('0' + date.getHours()).slice(-2) + ":" + ('0' + date.getMinutes()).slice(-2) + ":" + ('0' + date.getSeconds()).slice(-2);

            data[i]["dateTimeLocalDateString"] = datestring;
            data[i]["dateTimeLocalTimeString"] = timestring;
            data[i]["dateTimeLocalString"] = datestring + " " + timestring;

            //Round depthhole
            data[i]["depthhole_rounded"] = Math.round(JSON.parse(data[i]["data"]).depthhole); 

            //Calculate Reflectivity
            var reflectivity = JSON.parse(data[i]["data"]).reflecthole;
            var reflectivitystring = "n/a";
            if (reflectivity != null) {
                if (reflectivity > 8) {
                    reflectivitystring = "Wet"
                } else {
                    reflectivitystring = "Dry"
                }
            }
            data[i]["reflecthole_data"] = reflectivity;
            data[i]["wetness_data"] = reflectivitystring;

            //Calculate CaptureType
            var capturetype = JSON.parse(data[i]["data"]).capturetype;
            var capturtypestring = "n/a";
            if (capturetype != null) {
                capturtypestring = capturetype
            }
            data[i]["capturetype_data"] = capturtypestring;

            //Calculate Deleted
            var deletedflag = JSON.parse(data[i]["data"]).deleted;
            var deletedstring = "False";
            if (deletedflag != null) {
                if (deletedflag > 0) {
                    deletedstring = "True";
                }
            }
            data[i]["deletedString"] = deletedstring;

            //Calculate Comment
            var comment = JSON.parse(data[i]["data"]).comment;
            var commentstring = "-";
            if (comment != null) {
                if (comment != "") {
                    commentstring = comment;
                }
            }
            data[i]["commentString"] = commentstring;
        };
        return data;
    }

    manipulatePlanDataForFrontEnd(data) {
        for (let i in data) {
            //manage for -ve numbers (react-leaflet cant handle)
            if (data[i]["Latitude"] == -1) {
                data[i]["Latitude"] = 0;
            }
            if (data[i]["Longitude"] == -1) {
                data[i]["Longitude"] = 0;
            }
        };
        data.sort((a, b) => a.AssocPlanCampaignDetailsId - b.AssocPlanCampaignDetailsId);
        return data;
    }

    manipulateUploadPlanDataForMapView(rows, cols, extra) {
        var data = [];
        var tempdataobject = {};
        var arrayobject = {};
        var pulsesimpleExcludeCols = ["Collar XY Tolerance (m)", "Drill XY Tolerance (m)", "Stem XY Tolerance (m)", "Collar RL Tolerance- (m)", "Collar RL Tolerance+ (m)", "Drill RL Tolerance- (m)", "Drill RL Tolerance+ (m)", "Stem RL Tolerance- (m)", "Stem RL Tolerance+ (m)"];
        if (rows != null) {
            for (var i = 0; i < rows.length; i++) {
                arrayobject = {};
                arrayobject["Id"] = i; //Not used
                arrayobject["PlanId"] = -1; //Not used
                arrayobject["PlanOrder"] = i;
                arrayobject["DispatchId"] = -1; //Not used
                arrayobject["DispatchOrder"] = -1; //Not used

                //Temp data object from upload information
                tempdataobject = {}
                for (var j = 1; j < cols.length; j++) { //As Row# in first spot and dont want
                    if (this.state.uploadPlanType == "pulsesimple"){
                        if (!pulsesimpleExcludeCols.includes(cols[j].name)){ //NOT in exclude list
                            tempdataobject[cols[j].name] = rows[i][j];
                        }
                    } else {
                        tempdataobject[cols[j].name] = rows[i][j];
                    }
                }
                arrayobject["Data"] = JSON.stringify(tempdataobject);
                data.push(arrayobject);
            }

            for (let i in data) {
                //Add additional data
                data[i]["Latitude"] = extra[i].latitude;
                data[i]["Longitude"] = extra[i].longitude;
                data[i]["AssocCaptureId"] = -1;
                data[i]["AssocCaptureDistDiff"] = 0;
                data[i]["AssocCaptureDepthDiff"] = 0;
            };
        }
        return data;
    }

    manipulateUploadPlanDataForTableView(cols, rows) {
        const exclude = [
            "Collar XY Tolerance (m)", "Drill XY Tolerance (m)", "Stem XY Tolerance (m)",
            "Collar RL Tolerance- (m)", "Collar RL Tolerance+ (m)",
            "Drill RL Tolerance- (m)", "Drill RL Tolerance+ (m)",
            "Stem RL Tolerance- (m)", "Stem RL Tolerance+ (m)"
        ];

        // 1) keep columns (skip Row#) and ensure string keys + explicit widths
        const filteredCols = [];
        for (let j = 1; j < (cols?.length || 0); j++) {
            const c = cols[j];
            if (this.state.uploadPlanType === "pulsesimple" && exclude.includes(c.name)) continue;

            const col = { ...c };
            col.key = String(col.key || col.field || col.name);
            col.width = col.width || Math.max(140, ((col.name || '').length * 10) + 30);
            col.minWidth = col.minWidth || 140;
            filteredCols.push(col);
        }

        // 2) decide which columns to round (by header text)
        const xyzColKeys = new Set(
            filteredCols
                .filter(c => {
                    const name = (c.name || "").trim();
                    if (/tolerance/i.test(name)) return false;
                    return /^(collar|drill)\s+(x|y|rl)\s*\(m\)$/i.test(name);
                })
                .map(c => c.key)
        );

        // 3) convert array rows to objects with string keys and apply rounding
        const displayRows = (rows || []).map(r => {
            const obj = {};
            for (let i = 0; i < r.length; i++) {
                obj[String(i)] = r[i];
            }
            xyzColKeys.forEach(k => {
                if (k in obj) {
                    const n = Number(obj[k]);
                    if (Number.isFinite(n)) obj[k] = n.toFixed(3);
                }
            });
            return obj;
        });

        return { cols: filteredCols, rows: displayRows };
    }

    manipulateDispatchDetailsDataForMapView(data) {
        var locationIndex = this.state.location.findIndex(x => x.id == this.state.selectedlocationID);
        var locationUTMZoneNum = parseInt(this.state.location[locationIndex].data["UTMZoneNum"]);
        var locationUTMZoneLet = this.state.location[locationIndex].data["UTMZoneLet"];
        for (let i in data) {
            //manage for -ve numbers (react-leaflet cant handle)
            var tempLatLon = toLatLon(data[i].data["Easting"], data[i].data["Northing"], locationUTMZoneNum, locationUTMZoneLet); //Easting, Northing
            data[i]["Latitude"] = tempLatLon.latitude;
            data[i]["Longitude"] = tempLatLon.longitude;
            data[i]["AssocCaptureId"] = 0;
            data[i]["AssocCaptureDistDiff"] = 0;
        };
        return data;
    }

    manipulateSummaryForFrontEnd(data) {
        for (var i = data["Missing"].length - 1; i >= 0; --i) {
            //Remvoe Nulls from summary data
            if ((data["Missing"][i]["PlanDetailsId"] == null) || (data["Missing"][i]["PlanDetailsId"] == -1)) {
                //Remove 0 element of length 1
                data["Missing"].splice(i, 1);
            }
        };
        for (var i = data["DiffvsPlan"].length - 1; i >= 0; --i) {
            //Remvoe Nulls from summary data
            if ((data["DiffvsPlan"][i]["PlanDetailsId"] == null) || (data["DiffvsPlan"][i]["PlanDetailsId"] == -1)) {
                //Remove 0 element of length 1
                data["DiffvsPlan"].splice(i, 1);
            }
        };
        for (var i = data["DiffvsPlan"].length - 1; i >= 0; --i) {
            //manage for -ve numbers (react-leaflet cant handle)
            if (data["DiffvsPlan"][i]["Latitude_Plan"] == -1) {
                data["DiffvsPlan"][i]["Latitude_Plan"] = 0;
            }
            if (data["DiffvsPlan"][i]["Longitude_Plan"] == -1) {
                data["DiffvsPlan"][i]["Longitude_Plan"] = 0;
            }
            if (data["DiffvsPlan"][i]["Latitude_Capture"] == -1) {
                data["DiffvsPlan"][i]["Latitude_Capture"] = 0;
            }
            if (data["DiffvsPlan"][i]["Longitude_Capture"] == -1) {
                data["DiffvsPlan"][i]["Longitude_Capture"] = 0;
            }

            //add defaults for Review Redrill/Backfill
            data["DiffvsPlan"][i]["Latitude"] = data["DiffvsPlan"][i]["Latitude_Plan"];
            data["DiffvsPlan"][i]["Longitude"] = data["DiffvsPlan"][i]["Longitude_Plan"];
        };
        //Unpack data section (if available)
        for (var i = data["DiffvsPlan"].length - 1; i >= 0; --i) {
            if (data["DiffvsPlan"][i]["Data_Capture"] != null) {

                var holeLength = data["DiffvsPlan"][i].HoleLength_Capture;
                var lastAuto = data["DiffvsPlan"][i].PreviousAuto_HoleDepth;

                if (holeLength <= 0.1) { //replace implausible hole lengths for 3rd party compatibility
                    data["DiffvsPlan"][i].HoleLength_Capture = 0.1;
                }

                if (lastAuto < 0 && lastAuto !== -1) { //replace negative hole lengths with 0, -1 indicates no lastAuto
                    data["DiffvsPlan"][i].PreviousAuto_HoleDepth = 0;
                }

                //Calculate Reflectivity
                var reflectivity = JSON.parse(data["DiffvsPlan"][i]["Data_Capture"]).reflecthole;
                if (reflectivity == null || reflectivity == -1) {
                    reflectivity = data["DiffvsPlan"][i].PreviousAuto_Reflectivity;
                }

                data["DiffvsPlan"][i]["reflecthole_Capture"] = reflectivity;

                //Calculate CaptureType
                var capturetype = JSON.parse(data["DiffvsPlan"][i]["Data_Capture"]).capturetype;
                var capturtypestring = "n/a";
                if (capturetype != null) {
                    capturtypestring = capturetype
                }
                data["DiffvsPlan"][i]["capturetype_Capture"] = capturtypestring;

                //Calculate Deleted
                var deletedflag = JSON.parse(data["DiffvsPlan"][i]["Data_Capture"]).deleted;
                var deletedstring = "False";
                if (deletedflag != null) {
                    if (deletedflag > 0) {
                        deletedstring = "True";
                    }
                }
                data["DiffvsPlan"][i]["deletedString_Capture"] = deletedstring;

                //Calculate Comment
                var comment = JSON.parse(data["DiffvsPlan"][i]["Data_Capture"]).comment;
                var commentstring = "-";
                if (comment != null) {
                    if (comment != "") {
                        commentstring = comment;
                    }
                }
                data["DiffvsPlan"][i]["commentString_Capture"] = commentstring;

                //Calculate WaterHeight
                var waterHeight;
                if (holeLength !== -1 && lastAuto !== -1) {
                    waterHeight = (holeLength - lastAuto).toFixed(2);
                } else {
                    waterHeight = 0;
                }

                if (waterHeight < this.state.optionsWaterHeightZero){
                    data["DiffvsPlan"][i]["waterHeight"] = 0;
                } else {
                    data["DiffvsPlan"][i]["waterHeight"] = waterHeight;
                }

                var reflectivitystring = "-";
                if (lastAuto == -1) {
                    if (reflectivity >= this.state.optionsReflectivityWet){
                        reflectivitystring = "Wet*"
                    } else {
                        reflectivitystring = "Dry*"
                    }
                }else{
                    if (waterHeight > this.state.optionsWaterHeightWet) {
                        reflectivitystring = "Wet"
                    } else {
                        reflectivitystring = "Dry"
                    }
                }
                data["DiffvsPlan"][i]["wetness_Capture"] = reflectivitystring;

                //generate dates to local
                var offset = 0;
                if (this.state?.locationOptions?.UTCOffset != null) {
                    offset = this.state.locationOptions.UTCOffset;
                }
                var date = new Date(new Date(data["DiffvsPlan"][i]["DateTime_Capture"]).getTime() + (offset * 60 * 60 * 1000));
                data["DiffvsPlan"][i]["DateTime_Capture_Raw"] = date.toISOString(); // Preserve raw ISO string for export filtering
                var datestring = ('0' + date.getDate()).slice(-2) + "/" + ('0' + (date.getMonth() + 1)).slice(-2) + "/" + date.getFullYear(); //Month is 0-11
                var timestring = ('0' + date.getHours()).slice(-2) + ":" + ('0' + date.getMinutes()).slice(-2) + ":" + ('0' + date.getSeconds()).slice(-2);

                data["DiffvsPlan"][i]["DateTime_Capture"] = datestring + " " + timestring;
            }
        }

        // Filter out deleted captures from DiffvsPlan — treat as if they never happened
        data["DiffvsPlan"] = data["DiffvsPlan"].filter(row => row.deletedString_Capture !== "True");
        data["TotalCountDiffvsPlan"] = data["DiffvsPlan"].length;

        //Additional
        if (data["Additional"] != null) {
            for (var i = data["Additional"].length - 1; i >= 0; --i) {
                //Remvoe Nulls from summary data
                if ((data["Additional"][i]["CaptureId"] == null) || (data["Additional"][i]["CaptureId"] == -1)) {
                    //Remove 0 element of length 1
                    data["Additional"].splice(i, 1);
                }
            };
            for (var i = data["Additional"].length - 1; i >= 0; --i) {
                //manage for -ve numbers (react-leaflet cant handle)
                if (data["Additional"][i]["Latitude_Capture"] == -1) {
                    data["Additional"][i]["Latitude_Capture"] = 0;
                }
                if (data["Additional"][i]["Longitude_Capture"] == -1) {
                    data["Additional"][i]["Longitude_Capture"] = 0;
                }
            };
            for (var i = data["Additional"].length - 1; i >= 0; --i) {
                if (data["Additional"][i]["Data_Capture"] != null) {

                    var holeLength = data["Additional"][i].HoleLength_Capture;
                    var lastAuto = data["Additional"][i].PreviousAuto_HoleDepth;

                    if (holeLength <= 0.1) { //replace implausible hole lengths for 3rd party compatibility
                        data["Additional"][i].HoleLength_Capture = 0.1;
                    }

                    if (lastAuto < 0 && lastAuto !== -1) { //replace negative hole lengths with 0, -1 indicates no lastAuto
                        data["Additional"][i].PreviousAuto_HoleDepth = 0;
                    }

                    //Calculate Reflectivity
                    var reflectivity = JSON.parse(data["Additional"][i]["Data_Capture"]).reflecthole;
                    if (reflectivity == null || reflectivity == -1) {
                        reflectivity = data["Additional"][i].PreviousAuto_Reflectivity;
                    }

                    data["Additional"][i]["reflecthole_Capture"] = reflectivity;

                    //Calculate CaptureType
                    var capturetype = JSON.parse(data["Additional"][i]["Data_Capture"]).capturetype;
                    var capturtypestring = "n/a";
                    if (capturetype != null) {
                        capturtypestring = capturetype
                    }
                    data["Additional"][i]["capturetype_Capture"] = capturtypestring;

                    //Calculate Deleted
                    var deletedflag = JSON.parse(data["Additional"][i]["Data_Capture"]).deleted;
                    var deletedstring = "False";
                    if (deletedflag != null) {
                        if (deletedflag > 0) {
                            deletedstring = "True";
                        }
                    }
                    data["Additional"][i]["deletedString_Capture"] = deletedstring;

                    //Calculate Comment
                    var comment = JSON.parse(data["Additional"][i]["Data_Capture"]).comment;
                    var commentstring = "-";
                    if (comment != null) {
                        if (comment != "") {
                            commentstring = comment;
                        }
                    }
                    data["Additional"][i]["commentString_Capture"] = commentstring;

                    //Calculate WaterHeight
                    var waterHeight;
                    if (holeLength !== -1 && lastAuto !== -1) {
                        waterHeight = (holeLength - lastAuto).toFixed(2);
                    } else {
                        waterHeight = 0;
                    }

                    if (waterHeight < this.state.optionsWaterHeightZero) {
                        data["Additional"][i]["waterHeight"] = 0;
                    } else {
                        data["Additional"][i]["waterHeight"] = waterHeight;
                    }

                    var reflectivitystring = "-";
                    if (lastAuto == -1) {
                        if (reflectivity >= this.state.optionsReflectivityWet) {
                            reflectivitystring = "Wet*"
                        } else {
                            reflectivitystring = "Dry*"
                        }
                    } else {
                        if (waterHeight > this.state.optionsWaterHeightWet) {
                            reflectivitystring = "Wet"
                        } else {
                            reflectivitystring = "Dry"
                        }
                    }
                    data["Additional"][i]["wetness_Capture"] = reflectivitystring;

                    //generate dates to local
                    var offset = 0;
                    if (this.state?.locationOptions?.UTCOffset != null) {
                        offset = this.state.locationOptions.UTCOffset;
                    }
                    var date = new Date(new Date(data["Additional"][i]["DateTime_Capture"]).getTime() + (offset * 60 * 60 * 1000));
                    data["Additional"][i]["DateTime_Capture_Raw"] = date.toISOString(); // Preserve raw ISO string for export filtering
                    var datestring = ('0' + date.getDate()).slice(-2) + "/" + ('0' + (date.getMonth() + 1)).slice(-2) + "/" + date.getFullYear(); //Month is 0-11
                    var timestring = ('0' + date.getHours()).slice(-2) + ":" + ('0' + date.getMinutes()).slice(-2) + ":" + ('0' + date.getSeconds()).slice(-2);

                    data["Additional"][i]["DateTime_Capture"] = datestring + " " + timestring;
                }
            }

            // Filter out deleted captures from Additional — treat as if they never happened
            data["Additional"] = data["Additional"].filter(row => row.deletedString_Capture !== "True");
            data["TotalCountAdditional"] = data["Additional"].length;
        }

        return data;
    }

    generateTreeData() {
        var newTreeData = [];
        var locationIndex = -1;
        locationIndex = this.state.location.findIndex(x => x.id == this.state.selectedlocationID);

        if (locationIndex !== -1) {
            newTreeData = [{ id: "loc", name: this.state.location[locationIndex].name }];


            newTreeData[0].children = this.state.sublocation.filter(row => row.id != 0).map(row => { return { id: "subloc-" + row.id.toString(), name: row.name, isApiManaged: row.name === "API" } }); //sublocation
            if (this.state.sequence != null) { //sequence
                for (var i in newTreeData[0].children) {
                    var isApiSubloc = newTreeData[0].children[i].isApiManaged;
                    //if ("subloc-" + this.state.selectedsublocationID == newTreeData[0].children[i].id) {
                    newTreeData[0].children[i].children = this.state.sequence.filter(row => row.id != 0 && row.subLocationId == newTreeData[0].children[i].id.split('-')[1]).map(row => { return { id: "seq-" + row.id.toString(), name: row.name, isApiManaged: isApiSubloc } })

                        if (this.state.subsequence != null) { //subsequence
                            for (var j in newTreeData[0].children[i].children) {
                                //if ("seq-" + this.state.selectedsequenceID == newTreeData[0].children[i].children[j].id) {
                                var isApiSeq = newTreeData[0].children[i].children[j].isApiManaged;
                                newTreeData[0].children[i].children[j].children = this.state.subsequence.filter(row => row.id != 0 && row.sequenceId == newTreeData[0].children[i].children[j].id.split('-')[1]).map(row => { return { id: "subseq-" + row.id.toString(), name: row.name, isApiManaged: isApiSeq, externalShotId: row.externalShotId } });

                                    if (this.state.plan != null) { //plan
                                        for (var k in newTreeData[0].children[i].children[j].children) {
                                            //if ("subseq-" + this.state.selectedsubsequenceID == newTreeData[0].children[i].children[j].children[k].id) {
                                            newTreeData[0].children[i].children[j].children[k].children = this.state.plan.filter(row => row.id != 0 && row.subSequenceId == newTreeData[0].children[i].children[j].children[k].id.split('-')[1]).map(row => { return { id: "plan-" + row.id.toString() + "-" + newTreeData[0].children[i].children[j].children[k].id, name: "V" + row.version } });

                                                if (this.state.dispatch != null) { //dispatch
                                                    for (var l in newTreeData[0].children[i].children[j].children[k].children) {
                                                        //if ("plan-" + this.state.selectedplanID == newTreeData[0].children[i].children[j].children[k].children[l].id) {
                                                        newTreeData[0].children[i].children[j].children[k].children[l].children = this.state.dispatch.filter(row => row.id != 0 && row.subSequenceId == newTreeData[0].children[i].children[j].children[k].children[l].id).map(row => {
                                                                var date = new Date(row.dispatchTime);
                                                                var dtString = date.getFullYear().toString().slice(2) + pad2(date.getMonth() + 1) + pad2(date.getDate()) + pad2(date.getHours()) + pad2(date.getMinutes()) + pad2(date.getSeconds());
                                                            return { id: "dispatch-" + row.id.toString() + "-" + newTreeData[0].children[i].children[j].children[k].children[l].id.split('-')[1], name: dtString + "-HWID#" + row.hardwareId };
                                                            });
                                                        //}
                                                    }
                                                }

                                            //}
                                        }
                                    }
                                //}
                            }
                        //}


                    }
                }
            }
        }

        

        var treeNode = this.state.selectedsublocationID + this.state.selectedsequenceID + this.state.selectedsubsequenceID;
        this.setState({ treeData: newTreeData, selectedTreeNode: treeNode });
    }

    async getLocationAll() {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Location" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/locationall', {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            //Unpack data object
            if ((data !== null) && (data.length !== 0)) {
                for (var i = 0; i < data.length; i++) {
                    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                }
            }

            // Guard: empty location list (org has no sites, or the API returned []).
            // Without this the auto-select branch below dereferences data[0].id and the
            // home page hangs in 'Loading...' forever on an unhandled promise rejection.
            if (data.length === 0) {
                this.setState({ location: [], loadinglocation: false });
                this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
                return;
            }

            if (data.length > 1){
                //Insert blank
                data.unshift({
                    id: '0',
                    name: '---Select---',
                });
            } else if (data.length == 1) { //auto select if only 1
                this.setSelectedLocation(data[0].id);
            }

            //Request for the hardware for this location if we have it
            var checkMatch = false;
            for (var i = 0; i < data.length; i++) {
                if (this.state.selectedlocationID == data[i].id) {
                    checkMatch = true;
                    break;
                }
            }
            if (checkMatch == false) {
                var value = data[0].id;
                if (value !== null) {
                    this.setState({
                        selectedlocationID: value
                    });

                    this.setState({ loadinghardware: true, selectedhardwareID: "0", loadingsublocation: true, selectedsublocationID: "0", loadingdispatch: true, selecteddispatchID: "0", });

                    if (this.state.pagePreload) {
                        this.getHardwareAll(value); //have to use currnet as state is not snyc
                        this.getSubLocationAll(value); //Request for the Sublocation for this location
                        var value2 = this.state.selectedhardwareID;
                        if (value2 !== 0) {
                            this.getDispatchByHardware(value, value2); //request for dispatch
                        }
                    }
                }
            }
            this.setState({ location: data, loadinglocation: false, });


        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getTreeData(locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Location Tree" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/treedata/' + locationID + '/' + (this.state.optionsTreeShowDispatchWCaptures ? 0 : 1) + '/' + (this.state.optionsTreeShowArchived ? 1 : 0) + (this.state.optionsShowAdvanced ? '?classic=1' : ''), {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            if (data !== null) {
                if (data.length <= 0) {
                    data = [{ id: "1", name: "Please Select a Location" }]
                }
                var treeNode = this.state.selectedsublocationID + this.state.selectedsequenceID + this.state.selectedsubsequenceID;
                sortTreeData(this.state.sortString.split("-")[0], this.state.sortString.split("-")[1],data);
                this.setState({ treeData: data, selectedTreeNode: treeNode });
            }

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    updateSortTreeData(sortString) {
        var data = this.state.treeData;
        this.setState({sortString: sortString});
        sortTreeData(sortString.split("-")[0], sortString.split("-")[1], data);
        this.setState({ treeData: data });
    }



    async getDispatchReviewRequired(locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Dispatches" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/dispatchreviewrequired/' + locationID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            if (data !== null) {
                this.setState({ dispatchesReviewRequired: data });
            }

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getDispatchLatestCaptures(locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Dispatches" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/dispatchlatestcaptures/' + locationID + '/' + '5', { //get latest 5
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            if (data !== null) {
                var offset = 0;
                if (this.state?.locationOptions?.UTCOffset != null) {
                    offset = this.state.locationOptions.UTCOffset;
                }
                
                for(var i = 0; i < data.length; i++){
                    var date = new Date(new Date(data[i]["dispatchTime"]).getTime() + (offset * 60 * 60 * 1000));
                    data[i]["dispatchTime"] = date.toString();
                    var datestring = ('0' + date.getDate()).slice(-2) + "/" + ('0' + (date.getMonth() + 1)).slice(-2) + "/" + ('' +date.getFullYear()).slice(2); //Month is 0-11
                    var timestring = ('0' + date.getHours()).slice(-2) + ":" + ('0' + date.getMinutes()).slice(-2);

                    data[i]["dispatchTimeString"] = datestring + " " + timestring;
                }

                this.setState({ dispatchesLatestCaptures: data });
            }

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getLocationOptions(locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Options" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/options/' + locationID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            if (data !== null) {
                this.setState({ locationOptions: data });

                if (data?.DefaultXYTolerance) {
                    this.setState({ uploadPlanXYTolerance: data.DefaultXYTolerance});
                }

                if (data?.DefaultDepthPlusTolerance) {
                    this.setState({ uploadPlanRLTolerancePlus: data.DefaultDepthPlusTolerance });
                }

                if (data?.DefaultDepthMinusTolerance) {
                    this.setState({ uploadPlanRLToleranceMinus: data.DefaultDepthMinusTolerance });
                }

                if (data?.DefaultCS) {
                    this.setState({ uploadPlanCS: data.DefaultCS, exportDataCS: data.DefaultCS });
                }

                if (data?.DefaultUploadFormat) {
                    this.setState({ uploadPlanType: data.DefaultUploadFormat });
                }
            }

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getSubLocationAll(locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "SubLocation" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/sublocationall/' + locationID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            //Insert blank
            data.unshift({
                id: '0',
                name: '---Select---',
            });

            //Match drop down if required
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (this.state.selectedsublocationID == data[i].id) {
                        checkMatch = true;
                        break;
                    }
                }
                if (checkMatch == false) {
                    if (typeof data[0] === "undefined") {
                    } else {
                        var value = data[0].id;
                        if (value !== null) {
                            this.setState({
                                selectedsublocationID: value
                            });
                        }
                        this.setState({ loadingsequence: true, selectedsequenceID: "0", });

                        if (this.state.pagePreload) {
                            this.getSequenceAll(value); //Request
                        }
                    }
                }
            }
           
            this.setState({ sublocation: data, loadingsublocation: false, });
            //this.generateTreeData();

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async postsublocationInsert(locationID, sublocationName) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({'LocationId': locationID, 'Name': sublocationName, 'Description': sublocationName })
        };

        this.postUserLog("SubLocation Insert", "JSON", requestOptions.body, this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/sublocation', requestOptions)
            .then(response => response.json()).then(sublocId => {
                //Get latest list of
                this.setState({ loadingsublocation: true, selectedsublocationID: sublocId});
                this.getSubLocationAll(this.state.selectedlocationID);
                this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "subloc-"+sublocId}));
                
            });
    }

    async postsublocationUpdateArchived(sublocationID, archived) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(archived)
        };

        this.postUserLog("SubLocation Archive", "JSON", JSON.stringify({sublocationID: sublocationID, archived: archived}), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/sublocation/' + sublocationID + '/archive', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async postsublocationUpdateDeleted(sublocationID, deleted) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(deleted)
        };

        this.postUserLog("SubLocation Delete", "JSON", JSON.stringify({ sublocationID: sublocationID, deleted: deleted }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/sublocation/' + sublocationID + '/delete', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async postsublocationUpdateName(sublocationID, name) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(name)
        };

        this.postUserLog("SubLocation Rename", "JSON", JSON.stringify({ sublocationID: sublocationID, name: name }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/sublocation/' + sublocationID + '/name', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async getSequenceAll(sublocationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Sequence" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/sequenceall/' + sublocationID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            //Insert blank
            data.unshift({
                id: '0',
                name: '---Select---',
            });

            //Match drop down if required
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (this.state.selectedsequenceID == data[i].id) {
                        checkMatch = true;
                        break;
                    }
                }
                if (checkMatch == false) {
                    if (typeof data[0] === "undefined") {
                    } else {
                        var value = data[0].id;
                        if (value !== null) {
                            this.setState({
                                selectedsequenceID: value
                            });

                            this.setState({ loadingsubsequence: true, selectedsubsequenceID: "0", });

                            if (this.state.pagePreload) {
                                this.getSubSequenceAll(value); //Request
                            }

                        }
                    }
                }
            }
            this.setState({ sequence: data, loadingsequence: false, });
            //this.generateTreeData();

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async postsequenceInsert(sublocationID, sequenceName) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ 'SubLocationId': sublocationID, 'Name': sequenceName, 'Description': sequenceName })
        };

        this.postUserLog("Sequence Insert", "JSON", requestOptions.body, this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/sequence', requestOptions)
            .then(response => response.json()).then(seqId => {
                //Get latest list of
                this.setState({ loadingsequence: true, selectedsequenceID: seqId,  });
                this.getSequenceAll(this.state.selectedsublocationID);
                this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "seq-" + seqId }));
            });
    }

    async postsequenceUpdateArchived(sequenceID, archived) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(archived)
        };

        this.postUserLog("Sequence Archive", "JSON", JSON.stringify({ sequenceID: sequenceID, archived: archived }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/sequence/' + sequenceID + '/archive', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async postsequenceUpdateDeleted(sequenceID, deleted) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(deleted)
        };

        this.postUserLog("Sequence Delete", "JSON", JSON.stringify({ sequenceID: sequenceID, deleted: deleted }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/sequence/' + sequenceID + '/delete', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async postsequenceUpdateName(sequenceID, name) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(name)
        };

        this.postUserLog("Sequence Rename", "JSON", JSON.stringify({ sequenceID: sequenceID, name: name }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/sequence/' + sequenceID + '/name', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async getSubSequenceAll(sequenceID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "SubSequence" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/subsequenceall/' + sequenceID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            //Insert blank
            data.unshift({
                id: '0',
                name: '---Select---',
            });

            //Match drop down if required
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (this.state.selectedsubsequenceID == data[i].id) {
                        checkMatch = true;
                        break;
                    }
                }
                if (checkMatch == false) {
                    if (typeof data[0] === "undefined") {
                    } else {
                        var value = data[0].id;
                        if (value !== null) {
                            this.setState({
                                selectedsubsequenceID: value
                            });

                            this.setState({ loadingplan: true, selectedplanID: "0", });

                            if (this.state.pagePreload) {
                                this.getPlanAll(value); //Request
                            }
   
                        }
                    }
                }
            }
            this.setState({ subsequence: data, loadingsubsequence: false, });
            //this.generateTreeData();

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async postsubsequenceInsert(sequenceID, subsequenceName) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ 'SequenceId': sequenceID, 'Name': subsequenceName, 'Description': subsequenceName })
        };

        this.postUserLog("SubSequence Insert", "JSON", requestOptions.body, this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/subsequence', requestOptions)
            .then(response => response.json()).then(subseqId => {
                //Get latest list of
                this.setState({ loadingsubsequence: true, selectedsubsequenceID: subseqId, });
                this.getSubSequenceAll(this.state.selectedsequenceID);
                this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "subseq-" + subseqId }));
            });
    }

    // quick-260810-dqw D-04 / quick-260810-jvd findings 2-frontend + 7: "Add New Shot" on the
    // flat-mode Shots group. There is no selected sequence in flat mode, so unlike
    // postsubsequenceInsert this does NOT call getSubSequenceAll(this.state.selectedsequenceID)
    // up front — the server lazily creates the default "Shots" pit + RL and resolves the
    // SequenceId itself, returned as { subSequenceId, sequenceId }. parseFlatInsertResponse
    // guards every branch (non-2xx, malformed body, the legacy 8888 sentinel) so a failed insert
    // can never turn into a phantom "subseq-8888" selection: on failure we alert and leave tree
    // selection untouched; on success we mirror postsubsequenceInsert's post-insert state refresh
    // using the server-resolved sequenceId (finding 7).
    async postsubsequenceInsertFlat(locationID, subsequenceName) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ 'Name': subsequenceName, 'Description': subsequenceName })
        };

        this.postUserLog("SubSequence Insert Flat", "JSON", requestOptions.body, locationID);

        try {
            const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/subsequence/flat/' + locationID, requestOptions);
            const body = await response.json().catch(() => null);
            const result = parseFlatInsertResponse(response.status, body);

            if (!result.ok) {
                window.alert("Add Shot failed: " + result.error);
                return;
            }

            this.setState({ loadingsubsequence: true, selectedsubsequenceID: result.subSequenceId, selectedsequenceID: result.sequenceId });
            this.getSubSequenceAll(result.sequenceId);
            this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "subseq-" + result.subSequenceId }));
        } catch (e) {
            window.alert("Add Shot failed: " + e.message);
        }
    }

    async postsubsequenceUpdateArchived(subsequenceID, archived) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(archived)
        };

        this.postUserLog("SubSequence Archive", "JSON", JSON.stringify({ subsequenceID: subsequenceID, archived: archived }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/subsequence/' + subsequenceID + '/archive', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async postsubsequenceUpdateDeleted(subsequenceID, deleted) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(deleted)
        };

        this.postUserLog("SubSequence Delete", "JSON", JSON.stringify({ subsequenceID: subsequenceID, deleted: deleted }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/subsequence/' + subsequenceID + '/delete', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async postsubsequenceUpdateName(subsequenceID, name) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(name)
        };

        this.postUserLog("SubSequence Rename", "JSON", JSON.stringify({ subsequenceID: subsequenceID, name: name }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update/subsequence/' + subsequenceID + '/name', requestOptions)
            .then(response => {
                this.getTreeData(this.state.selectedlocationID);
            });
    }

    async getPlanAll(subsequenceID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Plan" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/planall/' + subsequenceID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            //for (var i = 0; i < data.length; i++) {
            //    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
            //}

            //Insert blank
            data.unshift({
                id: '0',
                data: {
                    Name: '---Select---'
                },
            });

            //Match drop down if required
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (this.state.selectedplanID == data[i].id) {
                        checkMatch = true;
                        break;
                    }
                }
                if (checkMatch == false) {
                    if (typeof data[0] === "undefined") {
                    } else {
                        var value = data[0].id;
                        if (value !== null) {
                            this.setState({
                                selectedplanID: value
                            });

                            //Get PlanDetails
                            this.setState({ loadingplandetails: true, });

                            if (this.state.pagePreload) {
                                this.getPlanDetailsAll(value); //Request
                            }

                            //Get PlanLifecyceles
                            this.setState({ loadingplanlifecycles: true, selectedplanlifecyclesID: "0", });

                            if (this.state.pagePreload) {
                                this.getPlanLifecyclesAll(value); //Request
                            }
                        }
                    }
                }
            }

            //Set greatest Verno for create
            var greatestVerNo = 0;
            var greatestVerIndex = 0;
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (data[i].subSequenceId = this.state.selectedsubsequenceID) {
                        if (data[i].version > greatestVerNo) {
                            greatestVerNo = data[i].version;
                            greatestVerIndex = i;
                            }
                    }
                }
            }
            //data[greatestVerIndex].data["Name"] = data[greatestVerIndex].data["Name"] + " [Latest]";
            this.setState({
                createplanVersion: greatestVerNo + 1
            });

            this.setState({ plan: data, loadingplan: false, });
            //this.generateTreeData();

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getPlan(planID) {
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/plan/getone/' + planID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            
            return data;

        } else {
            console.log("Error in response: " + response.status);
            return null;
        }
    }

    async uploadNewPlan() {

        let fileObj = this.state.uploadPlanFile;
        let fileName = fileObj.name;
        var extension = fileName.slice(fileName.lastIndexOf('.') + 1);
        const fileReader = new FileReader();
        fileReader.fileName = fileName;
        fileReader.location = this.state.selectedlocationID;
        fileReader.onload = e => {
            const base64string = e.target.result.replace('data:', '').replace(/^.+,/, '');
            this.postUserLog("Plan File Upload", "file-" + extension, "{\"filename\": \"" + e.target.fileName + "\",\"data\": \"" + base64string + "\"}", e.target.location);
        };
        let data = await fileReader.readAsDataURL(fileObj);
        
        if (this.state.planModalOption == 'update') {
            this.uploadUpdatedPlan(this.state.selectedplanID);
        } else {
            this.postautoplanInsertArray(this.state.selectedsubsequenceID, this.convertPlanRowColsToObject(this.state.rows, this.state.cols));
        }
        
    }

    uploadReset() {
        this.setState({
            uploadedFileName: "",
            dataLoaded: false,
            rows: null,
            cols: null,
            extra: null,
            extra_line: null,
            updatePlanInfo: [],
            uploadShowAdvancedOptions: false,
            columnMappingRequired: false,
            columnMapping: {},
            uploadedRawHeaders: [],
            pendingPlanJSON: null,
            pendingPlanFileType: null,
            pendingHeaderRowIndex: 0,
            uploadShowExpectedColumns: false,
            headersCopied: false,
        })
    }

    async uploadUpdatedPlan(planid) {
        this.postautoplanUpdateArray(planid, this.convertPlanRowColsToObject(this.state.rows, this.state.cols));
    }

    async postautoplanInsertArray(subsequenceID, sendobject) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Plan Upload" }));
        //Remove upload window state
        this.setState({
            dataLoaded: false,
            rows: null,
            cols: null,
            extra: null,
            extra_line: null
        });

        this.postUserLog("Plan Upload Submit New", "JSON", JSON.stringify({subsequenceID: subsequenceID, data: sendobject}),this.state.selectedlocationID);

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(sendobject)
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/auto/plan/' + subsequenceID, requestOptions)
            .then(response => response.json()).then(planID => {
                //Get Plan details
                this.setState({ loadingplandetails: true });
                this.getPlanDetailsAll(planID);
                this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "plan-" + planID }));
                this.setSelectedPlan(planID);
                this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
            });
    }

    async postautoplanTransformCoordinates(sendobject) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(sendobject)
        };

        return fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/transformautoplancoordinates/' + this.state.uploadPlanCS, requestOptions);
    }

    async postautoplanUpdateArray(planid, sendobject) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Plan Upload" }));
        //Remove upload window state
        this.setState({
            dataLoaded: false,
            rows: null,
            cols: null,
            extra: null,
            extra_line: null
        });

        this.postUserLog("Plan Upload Submit Update", "JSON", JSON.stringify({ planid: planid, data: sendobject }), this.state.selectedlocationID);

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(sendobject)
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/update2/auto/plan/' + planid, requestOptions)
            .then(response => response.json()).then(success => {
                if (success == 1) {
                    //Get Plan details
                    this.setState({ loadingplandetails: true });
                    this.getPlanDetailsAll(planid);
                    this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "plan-" + planid }));
                    this.setSelectedPlan(planid, true);
                    this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
                }
            });
    }

    async matchRedrillHoles() {
        this.setState({ redrillMatchLoading: true });
        const sendobject = this.convertPlanRowColsToObject(this.state.rows, this.state.cols);
        const accessToken = this.props.auth.getAccessToken();
        try {
            const response = await fetch(
                `${window._env_.REACT_APP_API_ADDRESS}/api/db/match/redrill/plan/${this.state.selectedplanID}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
                    body: JSON.stringify(sendobject)
                }
            );
            const result = await response.json();
            this.setState({ redrillMatchResult: result, redrillMatchLoading: false });
        } catch (e) {
            console.error("Error matching redrill holes:", e);
            this.setState({ redrillMatchLoading: false });
        }
    }

    async uploadRedrillPlan() {
        const { redrillMatchResult, redrillIncludeUnmatched } = this.state;
        const request = {
            matched: redrillMatchResult.matched,
            includedUnmatched: redrillMatchResult.unmatched.filter(h => redrillIncludeUnmatched[h.newHoleId]),
            removed: redrillMatchResult.removed || []
        };

        const planid = this.state.selectedplanID;
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Redrill Upload" }));

        //Remove upload window state
        this.setState({
            dataLoaded: false,
            rows: null,
            cols: null,
            extra: null,
            extra_line: null,
            redrillMatchResult: null,
            redrillIncludeUnmatched: {},
            redrillMatchLoading: false
        });

        this.postUserLog("Redrill Upload Submit", "JSON", JSON.stringify({ planid: planid, data: request }), this.state.selectedlocationID);

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(request)
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/redrill/plan/' + planid, requestOptions)
            .then(response => response.json()).then(success => {
                if (success == 1) {
                    this.setState({ loadingplandetails: true });
                    this.getPlanDetailsAll(planid);
                    this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "plan-" + planid }));
                    this.setSelectedPlan(planid, true);
                    this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
                }
            });
    }

    async postUserLog(action,inputType,inputData,locationID) {
        try{
            var sendobject = {
                LogTime: new Date(),
                ActionName: action,
                InputType: inputType ? inputType : "",
                InputData: inputData ? inputData : "{}",
                LocationId: locationID,
                BrowserDetails: navigator.userAgent
            };

            const accessToken = this.props.auth.getAccessToken();
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
                body: JSON.stringify(sendobject)
            };

            fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/userlog', requestOptions);
        } catch {
            console.log("UserLog Failed to Post - " + action);
        }
    }

    async postplanInsert(subsequenceID, version, starttime, endtime, data) {
        var newPlanID = -1;
        

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ 'SubSequenceId': subsequenceID, 'Version': version, 'StartTime': starttime, 'EndTime': endtime })
        };
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/plan', requestOptions); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var planid = await response.json();
            if ((planid !== null) && (planid.length !== 0)) {
                //Get latest list of
                this.setState({ loadingplan: true, selectedplanID: "0", });
                this.getPlanAll(this.state.selectedsubsequenceID);
                this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: "plan-" + planid }));

                newPlanID = planid;
            }
        } else {
            console.log("Error in response: " + response.status);
        }

        return newPlanID;
    }

    convertPlanRowColsToObject(rows, cols) {
        var sendobject = [];
        var arrayobject = {};
        var tempdataobject = {};
        for (var i = 0; i < rows.length; i++) {
            //arrayobject = {};
            //arrayobject["Id"] = i; //Not used
            //arrayobject["PlanId"] = -1; //Not used
            //arrayobject["PlanOrder"] = i;
            //arrayobject["DispatchId"] = -1; //Not used
            //arrayobject["DispatchOrder"] = -1; //Not used

            //Temp data object from upload information
            tempdataobject = {}
            for (var j = 1; j < cols.length; j++) { //As Row# in first spot and dont want
                tempdataobject[cols[j].name] = rows[i][j];
            }
            tempdataobject["Active"] = true; //set Active as default

            //arrayobject["Data"] = JSON.stringify(tempdataobject);
            sendobject.push(JSON.stringify(tempdataobject));
        }
        return sendobject;
    }

    async postplandetailsInsertArray(planID, sendobject) {

        //Remove upload window state
        this.setState({
            dataLoaded: false,
            rows: null,
            cols: null,
            extra: null,
            extra_line: null
        });

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(sendobject)
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/plandetailsarray/' + planID, requestOptions)
            .then(response => {
               //Get Plan details
                this.setState({ loadingplandetails: true, });
                this.getPlanDetailsAll(planID);
            });
    }

    async getPlanDetailsAll(planID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "PlanDetails" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/plandetailsall/' + planID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            if ((data !== null) && (data.length !== 0)) {
                for (var i = 0; i < data.length; i++) {
                   // data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                }
                this.setState({ plandetails: data, loadingplandetails: false, });
            }        
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getPlanDetailsAllForDispatch(planID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "PlanDetailsForDispatch" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/plandetailsall/' + planID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            if ((data !== null) && (data.length !== 0)) {
                for (var i = 0; i < data.length; i++) {
                    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                }
                this.setState({ plandetails: data, loadingplandetails: false, });

                //Add to Dispatch List
                var data2 = this.state.dispatchPlanDetailsAll;
                for (var i = 0; i < data.length; i++) {
                    data2.push(data[i]);
                }
                this.setState({ dispatchPlanDetailsAll: data2 });
            }
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getDispatchByHardware(locationID,hardwareID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Dispatch" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/dispatchbyhardware/' + locationID + '/' + hardwareID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            if ((data !== null) && (data.length !== 0)) {
                for (var i = 0; i < data.length; i++) {
                    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                }

                //Set greatest Time for dispatch
                var greatestTimeNo = 0;
                var greatestTimeIndex = 0;
                var temptime;
                if (data !== null) {
                    for (var i = 0; i < data.length; i++) {
                        temptime = Date.parse(data[i].dispatchTime);
                        if (temptime > 0) {
                            data[i].name = data[i].name + " (Dispatched @ " + data[i].dispatchTime + ")"; //Show dispatch time
                        }
                        if (temptime > greatestTimeNo) {
                            greatestTimeNo = temptime;
                            greatestTimeIndex = i;
                        }
                    }
                }
                data[greatestTimeIndex].name = data[greatestTimeIndex].name + " [Device]"; //Show latest on device

                //Insert blank
                data.unshift({
                    id: '0',
                    name: '---Select---',
                });

                //Match drop down if required
                if (data !== null) {
                    var checkMatch = false;
                    for (var i = 0; i < data.length; i++) {
                        if (this.state.selecteddispatchID == data[i].id) {
                            checkMatch = true;
                            break;
                        }
                    }
                    if (checkMatch == false) {
                        if (typeof data[0] === "undefined") {
                        } else {
                            var value = data[0].id;
                            if (value !== null) {
                                this.setState({
                                    selecteddispatchID: value
                                });

                                if (this.state.pagePreload) {
                                    //this.getDispatchDetailsAll(value); //***********************FUTURE
                                }
                            }
                        }
                    }
                }
                this.setState({ dispatch: data, loadingdispatch: false, });
            }
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getDispatchAll(locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Dispatch" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/dispatchall/' + locationID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            if ((data !== null) && (data.length !== 0)) {
                for (var i = 0; i < data.length; i++) {
                    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                }

                //Set greatest Time for dispatch
                var greatestTimeNo = 0;
                var greatestTimeIndex = 0;
                var temptime;
                if (data !== null) {
                    for (var i = 0; i < data.length; i++) {
                        temptime = Date.parse(data[i].dispatchTime);
                        if (temptime > 0) {
                            data[i].name = data[i].name + " (Dispatched @ " + data[i].dispatchTime + ")"; //Show dispatch time
                        }
                        if (temptime > greatestTimeNo) {
                            greatestTimeNo = temptime;
                            greatestTimeIndex = i;
                        }
                    }
                }
                data[greatestTimeIndex].name = data[greatestTimeIndex].name + " [Device]"; //Show latest on device

                //Insert blank
                data.unshift({
                    id: '0',
                    name: '---Select---',
                });

                //Match drop down if required
                if (data !== null) {
                    var checkMatch = false;
                    for (var i = 0; i < data.length; i++) {
                        if (this.state.selecteddispatchID == data[i].id) {
                            checkMatch = true;
                            break;
                        }
                    }
                    if (checkMatch == false) {
                        if (typeof data[0] === "undefined") {
                        } else {
                            var value = data[0].id;
                            if (value !== null) {
                                this.setState({
                                    selecteddispatchID: value
                                });

                                if (this.state.pagePreload) {
                                    //this.getDispatchDetailsAll(value); //***********************FUTURE
                                }
                            }
                        }
                    }
                }
                this.setState({ dispatch: data, loadingdispatch: false, });
                //this.generateTreeData();
            }        
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getDispatchByPlanID(subsequenceID,planID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Dispatch" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/dispatchbyplan/' + subsequenceID + '/' + planID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            if ((data !== null) && (data.length !== 0)) {
                //for (var i = 0; i < data.length; i++) {
                //    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                //}

                //Set greatest Time for dispatch
                var greatestTimeNo = 0;
                var greatestTimeIndex = 0;
                var temptime;
                if (data !== null) {
                    for (var i = 0; i < data.length; i++) {
                        temptime = Date.parse(data[i].dispatchTime);
                        if (temptime > 0) {
                            data[i].name = data[i].name + " (Dispatched @ " + data[i].dispatchTime + ")"; //Show dispatch time
                        }
                        if (temptime > greatestTimeNo) {
                            greatestTimeNo = temptime;
                            greatestTimeIndex = i;
                        }
                    }
                }
                data[greatestTimeIndex].name = data[greatestTimeIndex].name + " [Device]"; //Show latest on device

                //Insert blank
                data.unshift({
                    id: '0',
                    name: '---Select---',
                });

                //Match drop down if required
                if (data !== null) {
                    var checkMatch = false;
                    for (var i = 0; i < data.length; i++) {
                        if (this.state.selecteddispatchID == data[i].id) {
                            checkMatch = true;
                            break;
                        }
                    }
                    if (checkMatch == false) {
                        if (typeof data[0] === "undefined") {
                        } else {
                            var value = data[0].id;
                            if (value !== null) {
                                this.setState({
                                    selecteddispatchID: value
                                });

                                if (this.state.pagePreload) {
                                    //this.getDispatchDetailsAll(value); //***********************FUTURE
                                }
                            }
                        }
                    }
                }

                this.setState({ dispatch: data, loadingdispatch: false, });
                //this.generateTreeData();
            }
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getDispatchReview(dispatchID, locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "DispatchReview" }));
        this.setState({ review: [], loadedreview: false, reviewSelectionMode: false, reviewSelections: [], loadingreview: true, loadingreport: false });

        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/dispatchreview/' + locationID + '/' + dispatchID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            if ((data !== null) && (data.length !== 0)) {
                if (this.state.loadingreview) {
                    //for (var i = 0; i < data.length; i++) {
                    //    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                    //}
                    //Not requried expected to be string
                    //for (var i = 0; i < data.length; i++) {
                    //    for (var j = 0; j < data[i]["data"]["ReviewDetails"]["Captures_Raw"].length; j++) {
                    //        data[i]["data"]["ReviewDetails"]["Captures_Raw"][j]["Data"] = JSON.parse(data[i]["data"]["ReviewDetails"]["Captures_Raw"][j]["Data"]); //Parse string into object
                    //    }
                    //}

                    this.setState({ dbReview: JSON.parse(JSON.stringify(data)) }); //deep copy

                    data["data"] = JSON.parse(data["data"]);

                    // Override Reviewed if dispatch still requires review (new captures arrived after approval)
                    if (this.state.selectedDispatchTreeId && this.state.selectedDispatchTreeId.includes("reviewRequired")) {
                        data["data"]["Reviewed"] = 0;
                    }

                    //adjust  data for show
                    for (var i = 0; i < data["data"]["ReviewDetails"].length; i++) {
                        data["data"]["ReviewDetails"][i]["Captures_Raw"] = this.manipulateCaptureDataForFrontEnd(data["data"]["ReviewDetails"][i]["Captures_Raw"], data["data"]["ReviewDetails"][i]["ReviewCaptures"]);
                        data["data"]["ReviewDetails"][i]["ReviewPlans"] = this.manipulatePlanDataForFrontEnd(data["data"]["ReviewDetails"][i]["ReviewPlans"]);
                        data["data"]["ReviewDetails"][i]["Summary"] = this.manipulateSummaryForFrontEnd(data["data"]["ReviewDetails"][i]["Summary"]);
                        data["data"]["ReviewDetails"][i]["ReviewRequired"] = data["data"]["Reviewed"] !== 1 && data["data"]["ReviewDetails"][i]["Captures_Raw"].length > 0; //not already reviewed and atleast one capture
                    }

                    data["data"]["ReviewDetails"][0]["ReviewRequired"] = false; //set false as shown as default below

                    this.setState({ review: data, selectedReviewDetails: data["data"]["ReviewDetails"][0], loadedreview: true, activeReviewLifecycle: 0, activeLifecycleTypeId: data["data"]["ReviewDetails"][0].LifecycleTypeId, loadingreview: false }); //init to first lifecycle

                    if (!this.state.loadingreport) {
                        this.setState({ loadedreport: false }); //hide report
                    }

                    //update capture data - to reuse capture table
                    this.setState({ selectedCaptureData: data["data"]["ReviewDetails"][0]["Captures_Raw"], loading: false });
                    this.setMapStyle(10, 1);
                }
            } else {
                //Loading error
                this.setState({ reviewOpen: false, anyPageError: true, anyPageErrorDetails: '1: API returned null results for review' });
            }
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async postDispatchReviewEdits(dispatchID, locationID, reviewselections) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "DispatchReview" }));
        this.setState({ review: [], loadedreview: false, reviewSelectionMode: false, reviewUnassignMode: false, reviewSelections: [] });
        const accessToken = this.props.auth.getAccessToken();
        var manualselections = [];
        var tempobject;
        for (var i = 0; i < reviewselections.length; i++) {
            tempobject = {}
            tempobject["CaptureId"] = reviewselections[i]["capture_rowData"]['Id'];
            if (reviewselections[i]["missing_rowData"] != null){
                tempobject["PlandetailsId"] = reviewselections[i]["missing_rowData"]['PlanDetailsId'];
            } else if (reviewselections[i]["plan_rowData"] != null){
                tempobject["PlandetailsId"] = reviewselections[i]["plan_rowData"]['AssocPlanDetailsId'];
            }
            manualselections.push(tempobject);
        }
        const bodystring = JSON.stringify({ 'ManualSelections': manualselections });
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: bodystring
        };

        this.postUserLog("Review Insert", "JSON", JSON.stringify({ 'ManualSelections': manualselections, 'DispatchID': dispatchID}), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/dispatchreviewedits/' + locationID + '/' + dispatchID, requestOptions)
            .then(response => {
                response.json().then(data => {
                    //Get dispatch info for drop down
                    //this.setState({ review: [], loadedreview: false });
                    
                    if ((data !== null) && (data.length !== 0)) {
                        //for (var i = 0; i < data.length; i++) {
                        //    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                        //}

                        this.setState({ dbReview: JSON.parse(JSON.stringify(data)) }); //deep copy

                        data["data"] = JSON.parse(data["data"]);

                        //Not requried expected to be string
                        //for (var i = 0; i < data.length; i++) {
                        //    for (var j = 0; j < data[i]["data"]["ReviewDetails"]["Captures_Raw"].length; j++) {
                        //        data[i]["data"]["ReviewDetails"]["Captures_Raw"][j]["Data"] = JSON.parse(data[i]["data"]["ReviewDetails"]["Captures_Raw"][j]["Data"]); //Parse string into object
                        //    }
                        //}

                        // Override Reviewed if dispatch still requires review (new captures arrived after approval)
                        if (this.state.selectedDispatchTreeId && this.state.selectedDispatchTreeId.includes("reviewRequired")) {
                            data["data"]["Reviewed"] = 0;
                        }

                        //adjust  data for show
                        for (var i = 0; i < data["data"]["ReviewDetails"].length; i++) {
                            data["data"]["ReviewDetails"][i]["Captures_Raw"] = this.manipulateCaptureDataForFrontEnd(data["data"]["ReviewDetails"][i]["Captures_Raw"], data["data"]["ReviewDetails"][i]["ReviewCaptures"]);
                            data["data"]["ReviewDetails"][i]["ReviewPlans"] = this.manipulatePlanDataForFrontEnd(data["data"]["ReviewDetails"][i]["ReviewPlans"]);
                            data["data"]["ReviewDetails"][i]["Summary"] = this.manipulateSummaryForFrontEnd(data["data"]["ReviewDetails"][i]["Summary"]);
                            data["data"]["ReviewDetails"][i]["ReviewRequired"] = data["data"]["Reviewed"] !== 1 && data["data"]["ReviewDetails"][i]["Captures_Raw"].length > 0; //not already reviewed and atleast one capture
                        }

                        this.setState({ review: data, selectedReviewDetails: data["data"]["ReviewDetails"][this.state.activeReviewLifecycle], loadedreview: true, }); //re-init to active lifecycle
                        // Refresh tree data to update potential assignment asterisk
                        var oldTreeId = this.state.selectedDispatchTreeId;
                        this.getTreeData(this.state.selectedlocationID).then(() => this.setState({ selectedDispatchTreeId: oldTreeId }));
                    } else {
                        //Loading error
                        this.setState({ reviewOpen: false, anyPageError: true, anyPageErrorDetails: '1: API returned null results for review edits' });
                    }
                    this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
                })
            });
    }

    async postDispatchReviewResetEdits(dispatchID, locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "DispatchReview" }));
        this.setState({ review: [], loadedreview: false, reviewSelectionMode: false, reviewUnassignMode: false, reviewSelections: [] });
        const accessToken = this.props.auth.getAccessToken();

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` }
        };

        this.postUserLog("Review Reset Assignments", "JSON", JSON.stringify({ 'DispatchID': dispatchID }), this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/dispatchreviewresetedits/' + locationID + '/' + dispatchID, requestOptions)
            .then(response => {
                response.json().then(data => {
                    //Get dispatch info for drop down
                    //this.setState({ review: [], loadedreview: false });

                    if ((data !== null) && (data.length !== 0)) {
                        //for (var i = 0; i < data.length; i++) {
                        //    data[i]["data"] = JSON.parse(data[i]["data"]); //Parse string into object
                        //}

                        this.setState({ dbReview: JSON.parse(JSON.stringify(data)) }); //deep copy

                        data["data"] = JSON.parse(data["data"]);

                        //Not requried expected to be string
                        //for (var i = 0; i < data.length; i++) {
                        //    for (var j = 0; j < data[i]["data"]["ReviewDetails"]["Captures_Raw"].length; j++) {
                        //        data[i]["data"]["ReviewDetails"]["Captures_Raw"][j]["Data"] = JSON.parse(data[i]["data"]["ReviewDetails"]["Captures_Raw"][j]["Data"]); //Parse string into object
                        //    }
                        //}

                        // Override Reviewed if dispatch still requires review (new captures arrived after approval)
                        if (this.state.selectedDispatchTreeId && this.state.selectedDispatchTreeId.includes("reviewRequired")) {
                            data["data"]["Reviewed"] = 0;
                        }

                        //adjust  data for show
                        for (var i = 0; i < data["data"]["ReviewDetails"].length; i++) {
                            data["data"]["ReviewDetails"][i]["Captures_Raw"] = this.manipulateCaptureDataForFrontEnd(data["data"]["ReviewDetails"][i]["Captures_Raw"], data["data"]["ReviewDetails"][i]["ReviewCaptures"]);
                            data["data"]["ReviewDetails"][i]["ReviewPlans"] = this.manipulatePlanDataForFrontEnd(data["data"]["ReviewDetails"][i]["ReviewPlans"]);
                            data["data"]["ReviewDetails"][i]["Summary"] = this.manipulateSummaryForFrontEnd(data["data"]["ReviewDetails"][i]["Summary"]);
                            data["data"]["ReviewDetails"][i]["ReviewRequired"] = data["data"]["Reviewed"] !== 1 && data["data"]["ReviewDetails"][i]["Captures_Raw"].length > 0; //not already reviewed and atleast one capture
                        }

                        this.setState({ review: data, selectedReviewDetails: data["data"]["ReviewDetails"][this.state.activeReviewLifecycle], loadedreview: true }); //re-init to active lifecycle
                    } else {
                        //Loading error
                        this.setState({ reviewOpen: false, anyPageError: true, anyPageErrorDetails: '1: API returned null results for review edits' });
                    }
                    this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
                })
            });
    }

    async postDispatchReviewApprove(dbReview, dispatchID) {
        //this.setState({ review: [], loadedreview: false, reviewSelectionMode: false, reviewSelections: [] });
        var tempData = JSON.parse(dbReview.data);
        tempData.Reviewed = 1; //set reviewed in data for web view

        const accessToken = this.props.auth.getAccessToken();
        //var manualselections = [];
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ Id: dbReview.id, DispatchId: dbReview.dispatchId, Name: dbReview.name, ReviewTime: dbReview.reviewTime, Data: JSON.stringify(tempData) })
        };

        this.postUserLog("Review Approve", "JSON", requestOptions.body, this.state.selectedlocationID);

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/review/' + dispatchID, requestOptions)
            .then(response => {
                response.json().then(data => {

                    //refresh data
                    this.getTreeData(this.state.selectedlocationID);
                    this.getDispatchReviewRequired(this.state.selectedlocationID);
                    this.getDispatchLatestCaptures(this.state.selectedlocationID);
                })
            });
    }

    async getShotReport(locationID, subsequenceID) {
        this.getPlanReport(locationID, subsequenceID, -1).then(() => {
            this.setState({ loadedshotreport: true })
        });
    }

    async getPlanReport(locationID, subsequenceID, planID, cs) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "PlanReport" }));
        this.setState({ report: [], loadedreport: false, PDFgenerated: false, PDFloading: false, loadingreview: false, loadingreport: true  });

        //await this.getPlanLifecyclesAll(planID);
        //console.log(this.state.planlifecycles);

        var api = window._env_.REACT_APP_API_ADDRESS + '/api/db/planreport/' + locationID + '/' + subsequenceID + '/' + planID + '/' + cs;
        if (planID == -1) {
            api = window._env_.REACT_APP_API_ADDRESS + '/api/db/shotreport/' + locationID + '/' + subsequenceID;
        }

        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(api, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var stringdata = await response.json();
            if ((stringdata !== null) && (stringdata.length !== 0)) {
                if (this.state.loadingreport) {
                    var data = JSON.parse(stringdata); //Parse string into object

                    for (var p = 0; p < data["ReportDetails"].length; p++) {
                        //Go through lifecycles
                        if ((data["ReportDetails"][p]["ReviewPlans"].length > 0) && (data["ReportDetails"][p].hasOwnProperty("Summary"))) {
                            //adjust  data for show
                            data["ReportDetails"][p]["ReviewPlans"] = this.manipulatePlanDataForFrontEnd(data["ReportDetails"][p]["ReviewPlans"]);
                            data["ReportDetails"][p]["Summary"] = this.manipulateSummaryForFrontEnd(data["ReportDetails"][p]["Summary"]);


                            //Generate Plotly data
                            //------------------------------------------
                            var Chart = {};

                            //Percent Diff
                            Chart["PercentDiffvsPlan"] = ((data["ReportDetails"][p]["Summary"]["TotalCountDiffvsPlan"] * 100 / data["ReportDetails"][p]["Summary"]["TotalCountPlan"])).toFixed();

                            //Radar Data
                            var RadarData = {};
                            var RadarDataNorthArray = [];
                            var RadarDataEastArray = [];
                            for (var i = data["ReportDetails"][p]["Summary"]["DiffvsPlan"].length - 1; i >= 0; --i) {
                                RadarDataEastArray.push(data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Diff_DistEast"]);
                                RadarDataNorthArray.push(data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Diff_DistNorth"]);
                            };
                            RadarData["x"] = RadarDataEastArray;
                            RadarData["y"] = RadarDataNorthArray;
                            Chart["RadarData"] = RadarData;

                            //Bar Depth Data 
                            var BarDepthData = {};
                            var BarDepthDataCountArray = [0, 0, 0];
                            for (var i = data["ReportDetails"][p]["Summary"]["DiffvsPlan"].length - 1; i >= 0; --i) {
                                //Count
                                var sev = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Diff_HoleDepth_Severity"];
                                if (sev == 3) {
                                    BarDepthDataCountArray[1] = BarDepthDataCountArray[1] + 1;
                                } else if (sev < 3) {
                                    BarDepthDataCountArray[2] = BarDepthDataCountArray[2] + 1;
                                } else if (sev > 3) {
                                    BarDepthDataCountArray[0] = BarDepthDataCountArray[0] + 1;
                                }
                            };
                            BarDepthData["x"] = BarDepthDataCountArray;
                            Chart["BarDepthData"] = BarDepthData;

                            //Bar ToelRL Data 
                            var BarToeRLData = {};
                            var BarToeRLDataCountArray = [0, 0, 0];
                            for (var i = data["ReportDetails"][p]["Summary"]["DiffvsPlan"].length - 1; i >= 0; --i) {
                                //Count
                                var sev = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Diff_ToeRL_Severity"];
                                if (sev == 3) {
                                    BarToeRLDataCountArray[1] = BarToeRLDataCountArray[1] + 1;
                                } else if (sev < 3) {
                                    BarToeRLDataCountArray[2] = BarToeRLDataCountArray[2] + 1;
                                } else if (sev > 3) {
                                    BarToeRLDataCountArray[0] = BarToeRLDataCountArray[0] + 1;
                                }
                            };

                            BarToeRLData["x"] = BarToeRLDataCountArray;
                            Chart["BarToelRLData"] = BarToeRLData;

                            //Generate Redrill Map
                            var RedrillMapData = [];
                            var hasExplicitRedrills = data["ReportDetails"][p]["ReviewPlans"].some(rp => rp.IsExplicitRedrill);

                            if (hasExplicitRedrills) {
                                for (var i = 0; i < data["ReportDetails"][p]["ReviewPlans"].length; i++) {
                                    var rp = data["ReportDetails"][p]["ReviewPlans"][i];
                                    if (rp.IsExplicitRedrill) {
                                        var TempObject = {};
                                        TempObject["Latitude"] = rp.Latitude;
                                        TempObject["Longitude"] = rp.Longitude;
                                        TempObject["Name"] = rp.AssocPlanDetailsHoleId;
                                        TempObject["PlanDetailsId"] = rp.AssocPlanDetailsId;
                                        TempObject["IsExplicitRedrill"] = true;
                                        RedrillMapData.push(TempObject);
                                    }
                                }
                            } else {
                                // Inferred mode: no explicit redrills, use severity < 3
                                for (var i = 0; i <= data["ReportDetails"][p]["Summary"]["DiffvsPlan"].length - 1; i++) {
                                    if (data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Diff_" + this.state.reportHeaderCustomField[5] + "_Severity"] < 3) {
                                        var TempObject = {};
                                        TempObject["Latitude"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Latitude_Plan"];
                                        TempObject["Longitude"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Longitude_Plan"];
                                        TempObject["Name"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Name"];
                                        TempObject["PlanDetailsId"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["PlanDetailsId"];
                                        RedrillMapData.push(TempObject);
                                    }
                                };
                            }
                            Chart["RedrillMapData"] = RedrillMapData;

                            //Generate Backfill Map
                            var BackfillMapData = [];
                            for (var i = 0; i <= data["ReportDetails"][p]["Summary"]["DiffvsPlan"].length - 1; i++) {
                                //Count
                                if (data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Diff_" + this.state.reportHeaderCustomField[6] + "_Severity"] > 3) { // Overdrill
                                    //Reformat
                                    var TempObject = {};
                                    TempObject["Latitude"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Latitude_Plan"];
                                    TempObject["Longitude"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Longitude_Plan"];
                                    TempObject["Name"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["Name"];
                                    TempObject["PlanDetailsId"] = data["ReportDetails"][p]["Summary"]["DiffvsPlan"][i]["PlanDetailsId"];
                                    BackfillMapData.push(TempObject);
                                }
                            };
                            Chart["BackfillMapData"] = BackfillMapData;

                            data["ReportDetails"][p]["Chart"] = Chart;

                            //Update Missing to only unmeasured (i.e. do not appear in DiffvsPlan)
                            var UnmeasuredMapData = [];
                            for (var i = 0; i < data["ReportDetails"][p]["ReviewPlans"].length; i++) {
                                if (data["ReportDetails"][p]["Summary"]["DiffvsPlan"].find((row) => row.PlanDetailsId == data["ReportDetails"][p]["ReviewPlans"][i].AssocPlanDetailsId) == null) {
                                    //Reformat
                                    var TempObject = {};
                                    TempObject["Latitude"] = data["ReportDetails"][p]["ReviewPlans"][i]["Latitude"];
                                    TempObject["Longitude"] = data["ReportDetails"][p]["ReviewPlans"][i]["Longitude"];
                                    TempObject["Name"] = data["ReportDetails"][p]["ReviewPlans"][i]["AssocPlanDetailsHoleId"];
                                    TempObject["PlanDetailsId"] = data["ReportDetails"][p]["ReviewPlans"][i]["AssocPlanDetailsId"];
                                    UnmeasuredMapData.push(TempObject);
                                }
                            }
                            data["ReportDetails"][p]["Summary"]["Missing"] = UnmeasuredMapData;
                            


                        } else {
                            //Loading error
                            this.setState({ reportOpen: false, anyPageError: true, anyPageErrorDetails: '1: API returned null results for report' });
                        }
                    }
                

                    //set globals
                    this.setState({ report: data, loadedreport: true, selectedReportDetails: data.ReportDetails[0], activeReportLifecycle: 0, activeLifecycleTypeId: data.ReportDetails[0].LifecycleTypeId, loadingreport: false });

                    if (!this.state.loadingreview) {
                        this.setState({ loadedreview: false });  //hide review
                    }
                    this.setState({ loading: false });

                    this.setMapStyle(10, 1);
                }
            }            
        } else {
            console.log("Error in response: " + response.status);
        }

        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async postDispatch(name, hardwareID, locationID, dispatchtime, data) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ 'Id': -1, 'Name': name, 'HardwareId': hardwareID, 'LocationId': locationID, 'DispatchTime': dispatchtime, 'Data': JSON.stringify(data) })
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/dispatch', requestOptions)
            .then(response => {
                //Get dispatch info for drop down
                this.setState({ loadingdispatch: true, dispatchPlanDetailsShow: false, });
                this.getDispatchByHardware(locationID,hardwareID);
            });
    }

    async postDispatchToDevice(dispatchID, locationID) {
        this.setState({ dispatchToDeviceLoading: true });
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ title: 'React POST Request Example' })
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/RabbitMQPublish/dispatchtodevice/' + locationID + '/' + dispatchID, requestOptions)
            .then(response => {
                //Nothing
                this.setState({ dispatchToDeviceLoading: false });
            });
    }


    async getHardwareAll(locationID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Hardware" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/hardwareall/' + locationID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcode
        if (response.status === 200) {
            var data = await response.json();

            //Insert blank
            data.unshift({
                id: '0',
                name: '---Select---',
            });

            //Request for the hardware for this location if we have it
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (this.state.selectedhardwareID == data[i].id) {
                        checkMatch = true;
                        break;
                    }
                }
                if (checkMatch == false) {
                    if (typeof data[0] === "undefined") {
                    } else {
                        var value = data[0].id;
                        if (value !== null) {
                            this.setState({
                                selectedhardwareID: value
                            });
                            //this.getDataCaptureAllFiltered(this.state.selectedlocationID, value); //have to use currnet as state is not snyc
                        }
                    }
                }
            }
            this.setState({ hardware: data, loadinghardware: false });

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));

    }

    async getDataCaptureAll(locationID, hardwareID) {
        //reset
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "DataCapture" }));
        this.setState({ captureData: [], loading: true });

        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/datacaptureall/' + locationID + '/' + hardwareID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcode
        if (response.status === 200) {
            var data = await response.json();

            if ((data !== null) && (data.length !== 0)) {
                //adjust data for show
                data = this.manipulateCaptureDataForFrontEndInternal(data);
                this.setState({ captureData: data, loading: false });
            } else {
                //Loading error
                this.setState({ anyPageError: true, anyPageErrorDetails: '2: API returned no results for selected time period' });
            }
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));

    }

    async getDataCaptureAllFiltered(locationID, hardwareID) {
        //reset
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "DataCapture" }));
        this.setState({ filterOpen: false });
        this.setState({ captureData: [], loading: true });

        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/datacaptureallfiltered/' + locationID + '/' + hardwareID + '/' + this.state.startDate.toISOString() + '/' + this.state.endDate.toISOString(), {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcode
        if (response.status === 200) {
            var data = await response.json();

            if ((data !== null) && (data.length !== 0)) {
                //adjust data for show
                data = this.manipulateCaptureDataForFrontEndInternal(data);
                this.setState({ captureData: data, loading: false });
            } else {
                //Loading error
                this.setState({ anyPageError: true, anyPageErrorDetails: '3: API returned no results for selected time period' });
            }
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));

    }

    async getDataCaptureOneDetailed(id) {
        //Popup detailed display - on
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "DataCaptureDetailed" }));
        this.setState({
            seen: true
        });

        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/datacaptureonedetailed/' + this.state.selectedlocationID + '/' + this.state.selectedhardwareID + '/' + id, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcode
        if (response.status === 200) {
            var data = await response.json();

            //Store data and Popup detailed display - on
            var dataobject = JSON.parse(data[0].detailedData);

            //unpack  hole data if avail
            if (dataobject["holedata"] != null) {
                if (dataobject["holedata"] != "") {
                    dataobject["holedata"] = JSON.parse(dataobject["holedata"]);

                    //unpack data for maps
                    var referencestring;
                    dataobject["holedata"]["x1"] = [];
                    dataobject["holedata"]["y1"] = [];
                    dataobject["holedata"]["z1"] = [];
                    dataobject["holedata"]["x2"] = [];
                    dataobject["holedata"]["y2"] = [];
                    dataobject["holedata"]["z2"] = [];
                    for (var i = 0; i < dataobject["holedata"]["utc"].length; i++) {
                        referencestring = "x" + dataobject["holedata"]["sequence"][i];
                        dataobject["holedata"][referencestring].push(dataobject["holedata"]["x"][i])
                        referencestring = "y" + dataobject["holedata"]["sequence"][i];
                        dataobject["holedata"][referencestring].push(dataobject["holedata"]["y"][i])
                        referencestring = "z" + dataobject["holedata"]["sequence"][i];
                        dataobject["holedata"][referencestring].push(dataobject["holedata"]["z"][i])
                    }
                }
            }

            //Convert FLIR to rendered
            var canvas = document.createElement('canvas');
            canvas.width = 640;
            canvas.height = 512;
            var context = canvas.getContext('2d');
            var imagesrc = dataobject["flirimage"];
            var img = document.createElement('img');
            img.src = imagesrc;
            img.onload = () => { this.drawflirrender(canvas, dataobject, context, img) };
                      
        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));

    }

    drawflirrender(canvas, dataobject, context, img) {
        //const colorstops = ["#ffffff", "#ffd700", "#cc0077", "#20008c", "#000000"]; // palette
        const colorstops = ["#000000", "#20008c", "#cc0077", "#ffd700", "#ffffff"]; // palette

        context.drawImage(img, 0, 0);

        var imageData = context.getImageData(0, 0, img.width, img.height);

        for (let i = 0; i < imageData.data.length; i += 4) {
            let avg = (imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2]) / 3;

            // find the closest color in the custom palette
            let closestColorIndex = 0;
            for (let j = 0; j < colorstops.length - 1; j++) {
                if (avg >= (j * 255 / (colorstops.length - 1)) && avg <= ((j + 1) * 255 / (colorstops.length - 1))) {
                    closestColorIndex = j;
                    break;
                }
            }

            // determine the values for each color channel based on linear interpolation
            let color1 = colorstops[closestColorIndex];
            let color2 = colorstops[closestColorIndex + 1];
            let t = (avg - (closestColorIndex * 255 / (colorstops.length - 1))) / (255 / (colorstops.length - 1));

            imageData.data[i] = parseInt(color1.substring(1, 3), 16) + (parseInt(color2.substring(1, 3), 16) - parseInt(color1.substring(1, 3), 16)) * t;
            imageData.data[i + 1] = parseInt(color1.substring(3, 5), 16) + (parseInt(color2.substring(3, 5), 16) - parseInt(color1.substring(3, 5), 16)) * t;
            imageData.data[i + 2] = parseInt(color1.substring(5, 7), 16) + (parseInt(color2.substring(5, 7), 16) - parseInt(color1.substring(5, 7), 16)) * t;
        }
        context.putImageData(imageData, 0, 0);

        var renderedimage = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");  // here is the most important part because if you dont replace you will get a DOM 18 exception.
        dataobject["flirimage_rendered"] = renderedimage;

        this.setState({
            seen: true,
            showDetailedWindow: true,
            popupDetailedData: dataobject
        });

    }

    async postDataCaptureInsert(jsonstring) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(jsonstring)
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/datacapture', requestOptions)
            .then(response => {
                //Get latest list of
                this.setState({ loading: true, });
                this.getDataCaptureAllFiltered(this.state.selectedlocationID, this.state.selectedhardwareID); //refresh data on page
            });
    }

    async postUploadFull(handsetID, endtime) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ title: 'React POST Request Example' })
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/RabbitMQPublish/getuploadfull/' + handsetID + '/' + endtime, requestOptions)
            .then(response => {
                this.setState({ loading: true, });
                this.getDataCaptureAllFiltered(this.state.selectedlocationID, this.state.selectedhardwareID); //refresh data on page
            });
    }

    async postReuploadAll(handsetID) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ title: 'React POST Request Example' })
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/RabbitMQPublish/getreuploadall/' + handsetID, requestOptions)
            .then(response => {
                this.setState({ loading: true, });
                this.getDataCaptureAllFiltered(this.state.selectedlocationID, this.state.selectedhardwareID); //refresh data on page
            });
    }

    async getRefLifecycleTypeAll() {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Ref-LifecycleType" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/ref/lifecycletype/', {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            this.setState({ reflifecycletype: data, loadingreflifecycletype: false, });

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getPlanLifecyclesAll(planID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "PlanLifecycles" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/planlifecyclesall/' + planID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            //Insert blank
            data.unshift({
                id: '0',
                name: '---Select---',
            });

            //Match drop down if required
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (this.state.selectedplanlifecyclesID == data[i].id) {
                        checkMatch = true;
                        break;
                    }
                }
                if (checkMatch == false) {
                    if (typeof data[0] === "undefined") {
                    } else {
                        var value = data[0].id;
                        if (value !== null) {
                            this.setState({
                                selectedplanlifecyclesID: value
                            });

                            this.setState({ loadingplancampaigns: true, selectedplancampaignsID: "0", });

                            if (this.state.pagePreload) {
                                this.getPlanCampaignsAll(value); //Request
                            }

                        }
                    }
                }
            }
            this.setState({ planlifecycles: data, loadingplanlifecycles: false, });
            //this.generateTreeData();

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async postPlanLifecylesInsert(planID, lifecycletype, active, completed, data, capturedistancemin, capturedistancemax) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ 'PlanId': planID, 'LifecycleType': lifecycletype, 'Active': active, 'Completed': completed, 'Data': JSON.stringify(data), 'CaptureDistanceMin': capturedistancemin, 'CaptureDistanceMax': capturedistancemax })
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/planlifecycle', requestOptions)
            .then(response => {
                //Get latest list of
                this.setState({ loadingplanlifecycles: true, selectedplanlifecyclesID: "0", });
                this.getPlanLifecyclesAll(this.state.selectedplanID);
            });
    }

    async getPlanCampaignsAll(planID) {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "PlanCampaigns" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/plancampaignsall/' + planID, {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();

            //Insert blank
            data.unshift({
                id: '0',
                name: '---Select---',
            });

            //Match drop down if required
            if (data !== null) {
                var checkMatch = false;
                for (var i = 0; i < data.length; i++) {
                    if (this.state.selectedplancampaignsID == data[i].id) {
                        checkMatch = true;
                        break;
                    }
                }
                if (checkMatch == false) {
                    if (typeof data[0] === "undefined") {
                    } else {
                        var value = data[0].id;
                        if (value !== null) {
                            this.setState({
                                selectedplancampaignsID: value
                            });

                            //PLANCAMPAIGNDETAILS
                            //this.setState({ loadingplancampaignsdetails: true, selectedplancampaignsdetailsID: "0", });

                            //if (this.state.pagePreload) {
                            //    this.getPlanCampaignDetailsAll(value); //Request
                            //}

                        }
                    }
                }
            }
            this.setState({ plancampaigns: data, loadingplancampaigns: false, });
            //this.generateTreeData();

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async getRefCampaignTypeAll() {
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading + 1, anyPageDataLoadingDetails: "Ref-CampaignType" }));
        const accessToken = this.props.auth.getAccessToken();
        const response = await fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/ref/campaigntype/', {
            headers: new Headers({
                "Accept": "application/json",
                "Authorization": `Bearer ${accessToken}`
            })
        }); //TODO: Need to change from hardcoding
        if (response.status === 200) {
            var data = await response.json();
            this.setState({ refcampaigntype: data, loadingrecampaigntype: false, });

        } else {
            console.log("Error in response: " + response.status);
        }
        this.setState((prevState) => ({ anyPageDataLoading: prevState.anyPageDataLoading - 1 }));
    }

    async postPlanCampaignsInsert(planlifecycleID, campaigntype, active, completed) {
        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify({ 'PlanLifecycleID': planlifecycleID, 'CampaignType': campaigntype, 'Active': active, 'Completed': completed })
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/plancampaign', requestOptions)
            .then(response => {
                //Get latest list of
                this.setState({ loadingplancampaigns: true, selectedplancampaignID: "0", });
                this.getPlanCampaignsAll(this.state.selectedplanID);
            });
    }

    async postplancampaigndetailsInsertArray(planID, sendobject) {

        const accessToken = this.props.auth.getAccessToken();
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', "Accept": "application/json", "Authorization": `Bearer ${accessToken}` },
            body: JSON.stringify(sendobject)
        };

        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/insert/plancampaigndetailsarray/' + planID, requestOptions)
            .then(response => {
                //Get Plan details
                this.setState({ loadingplandetails: true, });
                this.getPlanDetailsAll(planID);
            });
    }

    async getDatacaptureImagesZipped(planID) {
        const accessToken = this.props.auth.getAccessToken();
        fetch(window._env_.REACT_APP_API_ADDRESS + '/api/db/zip/datacapturealldetailed/' + planID, {
            headers: new Headers({
                "Accept": "application/zip",
                "Authorization": `Bearer ${accessToken}`
            })
        })
        .then((response) => response.blob()) //1. Get blob
        .then((blob) => {
            // 2. Create blob link to download
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', planID + "_images.zip");
            // 3. Append to html page
            document.body.appendChild(link);
            // 4. Force download
            link.click();
            // 5. Clean up and remove the link
            link.parentNode.removeChild(link);

            this.setState({ loadingplanimages: false, });
        });
       
    }
}
